import { extendRecipes } from '../../../../modules/vendor/content-studio/recipe-schema/recipe-extensions';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { AnimatePresence, motion } from 'framer-motion';
import { v4 as uuidv4 } from 'uuid';
import {
    ArrowLeft,
    ArrowUpDown,
    Book,
    Check,
    ChevronDown,
    CirclePlus,
    Clapperboard,
    Film,
    Image as ImageIcon,
    Loader2,
    Maximize2,
    Play,
    Plus,
    RefreshCw,
    Redo2,
    SquareCheckBig,
    Undo2,
    X,
    ImagePlus,
} from 'lucide-react';

import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { useContentStudioApi } from '../../../../common/hooks/use-content-studio-api.hook';
import {
    CsProject,
    CsAsset,
    CsRecipe,
    CsGenerationCreateResponse,
} from '../../../../common/models/content-studio.model';

// Canvas components
import { CsCanvas, DEFAULT_ZOOM, ZOOM_MAX, ZOOM_MIN } from '../../../../modules/vendor/content-studio/canvas/CsCanvas';
import {
    CsConnectionLines,
    CsEdge,
    CsNodePosition,
} from '../../../../modules/vendor/content-studio/canvas/CsConnectionLines';
import { ProductNode, ProductNodeProduct, PRODUCT_NODE_WIDTH } from '../../../../modules/vendor/content-studio/canvas/ProductNode';
import { RecipeNode, RECIPE_NODE_WIDTH } from '../../../../modules/vendor/content-studio/canvas/RecipeNode';
import { GenerationNode, GENERATION_NODE_WIDTH } from '../../../../modules/vendor/content-studio/canvas/GenerationNode';
import { EditImageNode, EDIT_IMAGE_NODE_WIDTH } from '../../../../modules/vendor/content-studio/canvas/EditImageNode';
import { describeIdle, findSilentlyIdle } from '../../../../modules/vendor/content-studio/canvas/idle-check';

import { DesignNode, DESIGN_NODE_WIDTH } from '../../../../modules/vendor/content-studio/canvas/DesignNode';
import { WallPickerNode, WALL_PICKER_NODE_WIDTH } from '../../../../modules/vendor/content-studio/canvas/WallPickerNode';
import { VideoEditNode, VIDEO_EDIT_NODE_WIDTH } from '../../../../modules/vendor/content-studio/canvas/VideoEditNode';
import { useVideoEditCanvas } from '../../../../modules/vendor/content-studio/video-editor/use-video-edit-canvas';
import { useVideoStory } from '../../../../modules/vendor/content-studio/video-editor/use-video-story';
import type { CompiledVideoStory } from '../../../../modules/vendor/content-studio/planner/plan-compiler';
import { createEmptyComposition } from '../../../../modules/vendor/content-studio/video-editor/composition';
import { resolveCreditCost } from '../../../../modules/vendor/content-studio/recipe-pricing';
import { ProductPickerPanel } from '../../../../modules/vendor/content-studio/components/ProductPickerPanel';
import { GenerationLightbox } from '../../../../modules/vendor/content-studio/components/GenerationLightbox';
import { CreditBadge } from '../../../../modules/vendor/content-studio/components/CreditBadge';
import { CanvasMiniMap } from '../../../../modules/vendor/content-studio/components/ProjectCard';
import { NewProjectDialog } from '../../../../modules/vendor/content-studio/components/NewProjectDialog';
import { RecipeSelector } from '../../../../modules/vendor/content-studio/canvas/RecipeSelector';
import { CanvasLeftBar } from '../../../../modules/vendor/content-studio/canvas/CanvasLeftBar';

import { AddImageDialog } from '../../../../modules/vendor/content-studio/canvas/AddImageDialog';
import { CanvasContextMenu } from '../../../../modules/vendor/content-studio/canvas/CanvasContextMenu';
import { ImageEditorModal, ExpandParams, RetouchParams, ModifyParams, GraphicsParams } from '../../../../modules/vendor/content-studio/components/ImageEditorModal';
import { DesignEditorModal, DesignEditorSaveResult } from '../../../../modules/vendor/content-studio/components/DesignEditorModal';
import { fillSubmitDefaults, resolveSubmitSlug, resolveRecipeSchema, resolveRecipeFlags, needsWallPicker, keepsThePhotograph } from '../../../../modules/vendor/content-studio/recipe-schema';
import {
    AGENT_PANEL_MARGIN, AGENT_PANEL_WIDTH, hasAgentHistory, PlannerPanel, AgentTarget,
} from '../../../../modules/vendor/content-studio/planner/PlannerPanel';
import { takeBrief } from '../../../../common/services/content-brief.service';
import { CanvasEditInfo, CanvasGenerationInfo, CompiledPlan, DesignSpec, ExistingNode, PlannedRegion, PlannerProduct, DesignBrandDefaults, compiledPlanExtent, designSpecToFabric, expansionForAspect, isEmptyPlan, nodesAwaited, nodesToGenerate, offsetCompiledPlan } from '../../../../modules/vendor/content-studio/planner/plan-compiler';
import { renderDesignScene } from '../../../../modules/vendor/content-studio/canvas/design-render';
import { shouldKeepPolling } from '../../../../modules/vendor/content-studio/canvas/polling';
import { useSelector } from 'react-redux';
import { VendorState } from '../../../../redux/vendor/rootReducer';
import { editImageRequest, EditTool } from '../../../../modules/vendor/content-studio/edit-image-recipes';
import { boardToCanvas } from '../../../../modules/vendor/studio/board-to-canvas';
import { StudioBoard } from '../../../../modules/vendor/studio/studio-contract';
import { toCanvasProduct } from '../../../../modules/vendor/content-studio/product-mapping';
import { useProductsApi } from '../../../../common/hooks/use-products-api.hook';

// ═══════════════════════════════════════════════════════════════════════
// Internal node graph types
// ═══════════════════════════════════════════════════════════════════════

import {

    CanvasNode,
    CanvasProductNode,
    CanvasRecipeNode,
    CanvasGenerationNode,
    CanvasEditImageNode,
    CanvasDesignNode,
    CanvasWallPickerNode,
    CanvasVideoEditNode,
    EditImageTool,
    RetouchRegion,
} from '../../../../modules/vendor/content-studio/canvas/canvas-graph.types';

/**
 * How long a promised node may take to start before that counts as silence.
 *
 * Generous: a node posing the 3D studio has to capture its frame first, and a
 * design has to be flattened and uploaded. Both are seconds, not instants.
 */
const IDLE_GRACE_MS = 30_000;

/** Breathing room between the canvas's own controls and the agent panel. */
const CANVAS_CHROME_GAP = 16;

/** Stored on nodes to drive the schema rather than being described by it, so
 *  they are never "ignored" even when no visible input declares them.
 *
 *  The second group is written by the 3D studio when it captures a frame. No
 *  recipe declares them, so they used to be reported to the agent as ignored —
 *  which the system prompt defines as "editing this changes nothing the vendor
 *  can see". A captured frame is the opposite of that: it is the whole point of
 *  the shot, and the agent was being told to disregard it. */
const CONTROL_INPUT_KEYS = new Set([
    'mode', 'autoPick', 'use3dCamera', 'surfaceMode', 'appliedEnvironment',
    'snapshotUrl', 'cameraView', 'capturedAngle', 'lensMm', 'aperture', 'blockouts', 'furnitureRoom',
    // Place in a room — written by the placement view, not declared by the recipe.
    'placeProduct', 'roomAssetUuid', 'roomSpaceId', 'roomJobId', 'roomAnalysis', 'roomAnalysisError',
    'placement', 'compositeUrl', 'rugMaskUrl',
]);

/**
 * "Room Scene" (surface product-closeup, applied) is one card with two methods
 * switched by the 3D-studio toggle. It submits under the matching recipe slug so
 * the worker + pricing route to the right pipeline without any backend changes:
 *   - 3D ON  → In-Room  → product-closeup (studio render)
 *   - 3D OFF → Lifestyle → lifestyle-scene (depth / visualizer pipeline)
 */
/**
 * The products a job used — recorded on every asset as `sourceProductUuids`.
 *
 * A recipe off a product node used that node's products. A recipe off an image
 * used none of its own, EXCEPT Place in a room, whose product is chosen on the
 * card: its parent is the room image (`parentAssetUuid`) and its product is the
 * piece placed in it, so the job says both, the same way every job does.
 */
function jobProductUuids(recipeNode: CanvasRecipeNode, productNode?: CanvasProductNode): string[] {
    if (productNode?.products?.length) return productNode.products.map((p) => p.uuid);
    const placed = recipeNode.inputs?.placeProduct?.uuid;
    return placed ? [placed] : [];
}

function effectiveRecipeSlug(recipeSlug: string, inputs: Record<string, any> | undefined, vizType?: string): string {
    return resolveSubmitSlug({
        recipe: { slug: recipeSlug },
        inputs: inputs || {},
        productVisualizationType: vizType,
    });
}

/**
 * When Room Scene · Lifestyle submits under lifestyle-scene, the backend requires
 * that recipe's inputs (it does NOT filter by appliesTo), so furniture-only fields
 * like `style` / `roomType` must be present even for a surface product. Fill any
 * missing required lifestyle-scene inputs with their seed defaults (harmless — the
 * surface worker path ignores the furniture fields).
 */
function fillLifestyleRequiredDefaults(inputs: Record<string, any>, lifestyleRecipe?: CsRecipe): Record<string, any> {
    if (!lifestyleRecipe?.inputs) return inputs;
    // Callers already resolved the reroute, so force the fill by presenting the
    // source slug the rules reroute away from.
    return fillSubmitDefaults(
        {
            recipe: { slug: 'product-closeup' },
            inputs: { surfaceMode: 'applied' },
            productVisualizationType: 'surface',
            relatedRecipes: { 'lifestyle-scene': lifestyleRecipe },
        },
        inputs,
    );
}

/**
 * Render a one-line summary of the Expand tool's pixel deltas for display
 * under the node's Settings button. Returns null when nothing is set.
 */
function formatExpandSummary(p: {
    extendTop: number;
    extendRight: number;
    extendBottom: number;
    extendLeft: number;
    prompt: string;
}): string | null {
    const parts: string[] = [];
    if (p.extendTop) parts.push(`↑ ${p.extendTop}`);
    if (p.extendRight) parts.push(`→ ${p.extendRight}`);
    if (p.extendBottom) parts.push(`↓ ${p.extendBottom}`);
    if (p.extendLeft) parts.push(`← ${p.extendLeft}`);
    if (parts.length === 0) return null;
    const dimensions = parts.join(' · ');
    return p.prompt ? `${dimensions} · "${p.prompt.slice(0, 30)}${p.prompt.length > 30 ? '…' : ''}"` : dimensions;
}

function formatRetouchSummary(r: { regions: RetouchRegion[] }): string | null {
    const ready = r.regions.filter((re) => re.prompt.trim() && re.width > 0 && re.height > 0);
    if (ready.length === 0) return null;
    if (ready.length === 1) {
        const p = ready[0].prompt;
        return `1 region · "${p.slice(0, 40)}${p.length > 40 ? '…' : ''}"`;
    }
    return `${ready.length} regions · ${ready.map((re) => `"${re.prompt.slice(0, 18)}${re.prompt.length > 18 ? '…' : ''}"`).join(', ')}`;
}

/**
 * Given a source width × height and a list of recipe aspect-ratio option
 * values ("16:9", "9:16", "4:5", …), return whichever option is closest
 * to the source in log-ratio space (so 2:1 and 1:2 are equidistant from
 * 1:1, which matches visual intuition). Returns null if we can't pick.
 *
 * Used when chaining a new recipe off an existing asset (Image→Video,
 * Variation) so the downstream recipe defaults to the upstream frame
 * instead of forcing the user to change 16:9 every time.
 */
function pickClosestAspect(
    sourceWidth: number | undefined,
    sourceHeight: number | undefined,
    options: Array<{ value: string }> | undefined,
): string | null {
    if (!sourceWidth || !sourceHeight || !options || options.length === 0) return null;
    const target = sourceWidth / sourceHeight;
    let best: string | null = null;
    let bestDiff = Infinity;
    for (const opt of options) {
        const [wStr, hStr] = (opt.value || '').split(':');
        const w = Number(wStr);
        const h = Number(hStr);
        if (!w || !h) continue;
        const diff = Math.abs(Math.log(w / h) - Math.log(target));
        if (diff < bestDiff) {
            bestDiff = diff;
            best = opt.value;
        }
    }
    return best;
}

// Every node renders its label header at top:-24 (absolutely positioned, so it
// isn't in the measured height) plus a little breathing room — reserve a band
// above each box for spawn collision-avoidance so a node below never tucks under
// the "REVIEW" header of the one above it.
const LABEL_BAND = 34;

/**
 * One square button in the floating rail top-right of the canvas.
 *
 * Four of them (select / undo / redo / refresh) share the same size, tooltip
 * and disabled treatment, so they live here rather than four near-identical
 * copies inline. The `<span>` wrapper is deliberate: a disabled button emits
 * no pointer events, so without it the tooltip would go quiet exactly when the
 * user most wants to know why the control is dead.
 */
const CanvasToolButton: React.FC<{
    label: string;
    onClick: () => void;
    disabled?: boolean;
    active?: boolean;
    children: React.ReactNode;
}> = ({ label, onClick, disabled, active, children }) => (
    <Tooltip>
        <TooltipTrigger asChild>
            <span className="inline-flex">
                <Button
                    type="button"
                    variant="outline"
                    size="icon"
                    onClick={onClick}
                    disabled={disabled}
                    aria-label={label}
                    aria-pressed={active}
                    // Active reads as an ink outline rather than a filled slab —
                    // the rail sits on the canvas and a black square would fight
                    // the artwork underneath it.
                    className={cn(active && 'border-foreground bg-muted text-foreground')}
                >
                    {children}
                </Button>
            </span>
        </TooltipTrigger>
        <TooltipContent>{label}</TooltipContent>
    </Tooltip>
);

// ═══════════════════════════════════════════════════════════════════════
// Workspace page
// ═══════════════════════════════════════════════════════════════════════

const ContentStudioWorkspacePage: React.FC = () => {
    const { projectUuid } = useParams();
    const navigate = useNavigate();
    const { t } = useTranslation();
    const api = useContentStudioApi();

    // Project + recipes
    const [project, setProject] = useState<CsProject | null>(null);
    const [recipes, setRecipes] = useState<CsRecipe[]>([]);
    const [loading, setLoading] = useState(true);
    const [balance, setBalance] = useState<number | null>(null);
    const [siblingProjects, setSiblingProjects] = useState<CsProject[]>([]);

    // Canvas state
    const [zoom, setZoom] = useState(DEFAULT_ZOOM);
    const [panX, setPanX] = useState(100);
    const [panY, setPanY] = useState(100);
    const [nodes, setNodes] = useState<CanvasNode[]>([]);

    // ─── Undo / redo history ───────────────────────────────────────────
    //
    // Snapshot-based canvas history. A small ring of past/future `nodes`
    // arrays; every user-driven mutation pushes the pre-change state into
    // `past`. Undo pops the last past snapshot back to current; redo does
    // the reverse. Async updates (polling, note edits) do NOT push —
    // they mutate state silently so they don't pollute the undo stack.
    //
    // Asset deletion is DEFERRED: on reject / delete we mark the asset's
    // UUID as tombstoned rather than calling api.deleteAsset directly.
    // When a past snapshot containing a tombstoned asset falls off the
    // end of the stack (i.e., undo can no longer reach it), we flush the
    // api.deleteAsset call. This keeps "undo a delete" working without
    // leaving dead assets in S3 forever.
    const MAX_HISTORY = 20;
    const [past, setPast] = useState<CanvasNode[][]>([]);
    const [future, setFuture] = useState<CanvasNode[][]>([]);
    // Tombstoned asset UUIDs awaiting the delete flush once they're no
    // longer reachable from any history snapshot or the current state.
    const tombstonesRef = useRef<Set<string>>(new Set());
    // Ref mirror of `nodes` so history helpers stay referentially stable
    // (callable from any useCallback-memoized handler without stale
    // closures) while still reading the current snapshot on demand.
    const nodesRef = useRef<CanvasNode[]>(nodes);
    /** Spots findFreeSpawn has handed out but whose nodes have not landed yet. */
    const reservedSpawnsRef = useRef<Array<{ x: number; y: number; w: number; h: number; at: number }>>([]);
    nodesRef.current = nodes;
    const pastRef = useRef<CanvasNode[][]>(past);
    pastRef.current = past;
    const futureRef = useRef<CanvasNode[][]>(future);
    futureRef.current = future;

    // Collect every generation-asset UUID referenced by a snapshot list.
    const collectAssetUuids = useCallback((lists: CanvasNode[][]): Set<string> => {
        const uuids = new Set<string>();
        for (const list of lists) {
            for (const n of list) {
                if (n.kind === 'generation') {
                    uuids.add((n as CanvasGenerationNode).asset.uuid);
                }
            }
        }
        return uuids;
    }, []);

    // Flush any tombstone whose UUID is no longer reachable — called
    // whenever the history stack changes shape (push, trim, undo, redo).
    const flushUnreachableTombstones = useCallback(
        (pastList: CanvasNode[][], futureList: CanvasNode[][], current: CanvasNode[]) => {
            if (tombstonesRef.current.size === 0) return;
            const reachable = collectAssetUuids([...pastList, ...futureList, current]);
            const toFlush: string[] = [];
            tombstonesRef.current.forEach((uuid) => {
                if (!reachable.has(uuid)) toFlush.push(uuid);
            });
            for (const uuid of toFlush) {
                tombstonesRef.current.delete(uuid);
                api.deleteAsset(uuid).catch(() => {});
            }
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [],
    );

    // Snapshot the CURRENT nodes array into `past` before applying a
    // user-driven mutation. Clears `future` (new branch overrides the
    // redo history), enforces MAX_HISTORY by shifting the oldest off,
    // and flushes any tombstones that fall out of reach. Reads `nodes`
    // via ref so the callback stays referentially stable.
    const pushHistory = useCallback(() => {
        const current = nodesRef.current;
        setPast((prev) => {
            const next = [...prev, current];
            while (next.length > MAX_HISTORY) next.shift();
            flushUnreachableTombstones(next, [], current);
            return next;
        });
        setFuture([]);
    }, [flushUnreachableTombstones]);

    // Drag sessions fire moveNode() many times per frame. We want ONE
    // history entry per drag (the pre-drag state), so a flag gates the
    // push to the first onDragStart of each session and resets on mouseup.
    const dragPushedRef = useRef(false);
    const beginDragSession = useCallback(() => {
        if (dragPushedRef.current) return;
        dragPushedRef.current = true;
        pushHistory();
    }, [pushHistory]);
    // Reset the flag after any mouseup anywhere on the document — ends
    // the session cleanly regardless of which node was dragged.
    useEffect(() => {
        const onUp = () => { dragPushedRef.current = false; };
        document.addEventListener('mouseup', onUp);
        return () => document.removeEventListener('mouseup', onUp);
    }, []);

    const undo = useCallback(() => {
        const current = nodesRef.current;
        const p = pastRef.current;
        if (p.length === 0) return;
        const prevSnap = p[p.length - 1];
        const newPast = p.slice(0, -1);
        const newFuture = [current, ...futureRef.current];
        setPast(newPast);
        setFuture(newFuture);
        setNodes(prevSnap);
        flushUnreachableTombstones(newPast, newFuture, prevSnap);
    }, [flushUnreachableTombstones]);

    const redo = useCallback(() => {
        const current = nodesRef.current;
        const f = futureRef.current;
        if (f.length === 0) return;
        const nextSnap = f[0];
        const newFuture = f.slice(1);
        const newPast = [...pastRef.current, current];
        while (newPast.length > MAX_HISTORY) newPast.shift();
        setPast(newPast);
        setFuture(newFuture);
        setNodes(nextSnap);
        flushUnreachableTombstones(newPast, newFuture, nextSnap);
    }, [flushUnreachableTombstones]);

    const canUndo = past.length > 0;
    const canRedo = future.length > 0;

    // ─── Multi-asset select mode ───────────────────────────────────────
    //
    // A workspace-wide mode for picking N generated images to feed into
    // a downstream action (Create image / Create video from these). When
    // active, every node except ready image generations renders dimmed
    // and non-interactive; image generations expose a checkbox overlay
    // in their top-left corner. Exiting the mode (Escape / Cancel /
    // action taken) restores normal canvas interaction and clears the
    // per-mode selection (which is distinct from `selectedNodeIds`).
    const [selectMode, setSelectMode] = useState(false);
    const [selectedAssetUuids, setSelectedAssetUuids] = useState<Set<string>>(new Set());
    /**
     * Waiting for the vendor to point at an image for the agent.
     *
     * Reuses the canvas's existing pick mode — everything except a finished
     * image dims and stops responding — so "which picture do you mean" is
     * answered by pointing at it rather than describing it.
     */
    const pickForAgentRef = useRef<((generationNodeId: string | null) => void) | null>(null);
    const exitSelectMode = useCallback(() => {
        setSelectMode(false);
        setSelectedAssetUuids(new Set());
        // A pick abandoned by Escape or Cancel answers with nothing, so the
        // panel is not left waiting on a promise that never settles.
        const pending = pickForAgentRef.current;
        pickForAgentRef.current = null;
        pending?.(null);
    }, []);

    const pickGenerationForAgent = useCallback(() => new Promise<string | null>((resolve) => {
        pickForAgentRef.current?.(null);
        pickForAgentRef.current = resolve;
        setSelectedAssetUuids(new Set());
        setSelectMode(true);
    }), []);
    const toggleAssetSelection = useCallback((uuid: string) => {
        setSelectedAssetUuids((prev) => {
            const next = new Set(prev);
            if (next.has(uuid)) next.delete(uuid);
            else next.add(uuid);
            return next;
        });
    }, []);
    // Swap the order of the (exactly two) currently-selected assets so
    // the user can flip start / end frames before firing Create video.
    // No-op when count != 2 — the action bar only exposes the button
    // in that state.
    const swapSelectionOrder = useCallback(() => {
        setSelectedAssetUuids((prev) => {
            if (prev.size !== 2) return prev;
            const [a, b] = Array.from(prev);
            return new Set([b, a]);
        });
    }, []);

    // Keyboard shortcuts. Skip when the user is typing in an input /
    // textarea / contentEditable — those have their own native undo.
    useEffect(() => {
        const isEditable = (el: Element | null): boolean => {
            if (!el) return false;
            const tag = (el as HTMLElement).tagName;
            if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT') return true;
            return (el as HTMLElement).isContentEditable === true;
        };
        const onKey = (e: KeyboardEvent) => {
            // Escape — highest priority, exits multi-select mode.
            if (e.key === 'Escape' && selectMode) {
                if (isEditable(document.activeElement)) return;
                e.preventDefault();
                exitSelectMode();
                return;
            }
            const mod = e.ctrlKey || e.metaKey;
            if (!mod) return;
            const key = e.key.toLowerCase();
            if (key !== 'z' && key !== 'y') return;
            if (isEditable(document.activeElement)) return;
            const wantRedo = key === 'y' || (key === 'z' && e.shiftKey);
            e.preventDefault();
            if (wantRedo) redo();
            else undo();
        };
        window.addEventListener('keydown', onKey);
        return () => window.removeEventListener('keydown', onKey);
    }, [undo, redo, selectMode, exitSelectMode]);

    // Live-measured DOM heights keyed by nodeId. Node content is
    // dynamic — recipe nodes expand/collapse option cards, generation
    // nodes toggle review buttons, edit-image nodes change tool — so
    // any static height estimate puts connection-line endpoints off-
    // center. A ResizeObserver watches every [data-cs-node-id] element
    // and mirrors its actual height here; nodePositions reads this map
    // with a fallback estimate for the first render (before the observer
    // has fired).
    const [nodeHeights, setNodeHeights] = useState<Map<string, number>>(() => new Map());

    // ─── Spawn collision-avoidance ─────────────────────────────────────
    // Rough on-canvas footprint of a node — its measured height when the
    // ResizeObserver has reported one, else a per-kind estimate. Used only to
    // keep freshly-spawned nodes from landing on top of existing ones.
    const nodeFootprint = useCallback((n: CanvasNode): { w: number; h: number } => {
        const w = n.kind === 'product' ? PRODUCT_NODE_WIDTH
            : n.kind === 'recipe' ? RECIPE_NODE_WIDTH
            : n.kind === 'edit-image' ? EDIT_IMAGE_NODE_WIDTH
            : n.kind === 'design' ? DESIGN_NODE_WIDTH
            : n.kind === 'wall-picker' ? WALL_PICKER_NODE_WIDTH
            : n.kind === 'video-edit' ? VIDEO_EDIT_NODE_WIDTH
            : GENERATION_NODE_WIDTH;
        const estimate = n.kind === 'recipe' ? 320
            : n.kind === 'video-edit' ? 360
            : n.kind === 'design' ? 300
            : n.kind === 'wall-picker' ? 320
            : n.kind === 'edit-image' ? 210
            : n.kind === 'product' ? 200
            : 260; // generation
        return { w, h: nodeHeights.get(n.id) ?? estimate };
    }, [nodeHeights]);

    // Find a collision-free position for a new node of size (w,h) that is the
    // NEAREST open pocket to the desired (x,y). If the anchor is clear we use it.
    // Otherwise we scan the DOWN-RIGHT region (the way the generation flow runs:
    // left→right, top→bottom) on a grid and pick the closest grid point where
    // the node fits with no overlap. A 2D scan (rather than walking straight
    // down or right) tucks the node into the nearest empty gap near the recipe
    // instead of extending a long horizontal row or a deep column — and never
    // lands on another product's nodes. Boxes are inflated by LABEL_BAND on top
    // (for the -24 header) plus a gutter so nodes never visually touch. Reads
    // the LIVE nodes via ref so it's correct even after an await.
    const findFreeSpawn = useCallback(
        (x: number, y: number, w: number, h: number): { x: number; y: number } => {
            const GUTTER = 44;
            // Spots handed out but not yet occupied.
            //
            // A node only joins `nodes` once its job comes back, which is
            // seconds later. Two generations fired together therefore both
            // measured a canvas that still had neither of them in it, picked
            // the same gap and landed on top of each other. Reserving the spot
            // at the moment it is chosen is what makes the second one look.
            const now = Date.now();
            reservedSpawnsRef.current = reservedSpawnsRef.current.filter(
                (r) => now - r.at < 90_000,
            );
            const boxes = nodesRef.current
                .filter((n) => Number.isFinite(n.x) && Number.isFinite(n.y))
                .map((n) => {
                    const d = nodeFootprint(n);
                    return { left: n.x, right: n.x + d.w, top: n.y - LABEL_BAND, bottom: n.y + d.h };
                })
                .concat(reservedSpawnsRef.current.map((r) => ({
                    left: r.x, right: r.x + r.w, top: r.y - LABEL_BAND, bottom: r.y + r.h,
                })));
            const clearAt = (cx: number, cy: number) => {
                const cl = cx; const cr = cx + w; const ct = cy - LABEL_BAND; const cb = cy + h;
                return !boxes.some((b) =>
                    cl < b.right + GUTTER && cr + GUTTER > b.left
                    && ct < b.bottom + GUTTER && cb + GUTTER > b.top,
                );
            };

            const take = (spot: { x: number; y: number }) => {
                reservedSpawnsRef.current.push({ ...spot, w, h, at: Date.now() });
                return spot;
            };

            // Anchor is clear — keep the node right where it wants to be.
            if (clearAt(x, y)) return take({ x, y });

            // Nearest empty grid point in the down-right quadrant. dx outer so
            // exact-distance ties resolve to the more VERTICAL spot (closer to
            // the recipe's own column) rather than sprawling rightward.
            const STEP = 40;
            const MAX = 4000;
            let best: { x: number; y: number } | null = null;
            let bestD = Infinity;
            for (let dx = 0; dx <= MAX; dx += STEP) {
                if (dx * dx >= bestD) break; // no closer column possible
                for (let dy = 0; dy <= MAX; dy += STEP) {
                    const d = dx * dx + dy * dy;
                    if (d >= bestD) break; // dy only grows ⇒ rest of column is farther
                    if (!clearAt(x + dx, y + dy)) continue;
                    best = { x: x + dx, y: y + dy };
                    bestD = d;
                    break; // first clear cell in this column is its nearest
                }
            }
            return take(best ?? { x, y });
        },
        [nodeFootprint],
    );

    // Smoothly glide the canvas camera so a node is centred in the viewport —
    // called right after a node spawns so the vendor always sees a fresh
    // generation immediately, even if they were zoomed into another area. Keeps
    // the current zoom unless the node wouldn't fit (then eases out just enough).
    const focusAnimRef = useRef<number | null>(null);
    const focusNode = useCallback((nx: number, ny: number, nw: number, nh: number) => {
        const rect = canvasElRef.current?.getBoundingClientRect();
        const vw = rect?.width || 800;
        const vh = rect?.height || 600;
        const { panX: startX, panY: startY, zoom: startZoom } = latestStateRef.current;
        const margin = 140;
        const fitZoom = Math.min((vw - margin) / Math.max(nw, 1), (vh - margin) / Math.max(nh + LABEL_BAND, 1));
        const targetZoom = Math.max(0.3, Math.min(startZoom, fitZoom));
        const cx = nx + nw / 2;
        const cy = ny + nh / 2;
        const targetPanX = vw / 2 - cx * targetZoom;
        const targetPanY = vh / 2 - cy * targetZoom;
        // Skip the animation if we're already essentially there.
        if (Math.abs(targetPanX - startX) < 2 && Math.abs(targetPanY - startY) < 2 && Math.abs(targetZoom - startZoom) < 0.01) {
            return;
        }
        if (focusAnimRef.current) cancelAnimationFrame(focusAnimRef.current);
        const t0 = performance.now();
        const dur = 480;
        const ease = (p: number) => (p < 0.5 ? 4 * p * p * p : 1 - Math.pow(-2 * p + 2, 3) / 2); // easeInOutCubic
        const step = (now: number) => {
            const p = Math.min(1, (now - t0) / dur);
            const e = ease(p);
            setPanX(startX + (targetPanX - startX) * e);
            setPanY(startY + (targetPanY - startY) * e);
            setZoom(startZoom + (targetZoom - startZoom) * e);
            if (p < 1) focusAnimRef.current = requestAnimationFrame(step);
            else focusAnimRef.current = null;
        };
        focusAnimRef.current = requestAnimationFrame(step);
    }, []);

    useEffect(() => () => { if (focusAnimRef.current) cancelAnimationFrame(focusAnimRef.current); }, []);

    // Multi-select: Ctrl/Cmd+click toggles a node in the set; plain
    // click resets to a single-node selection; clicking the empty
    // canvas background clears. Dragging any selected node moves all
    // currently-selected nodes together.
    const [selectedNodeIds, setSelectedNodeIds] = useState<Set<string>>(new Set());
    const selectedNodeIdsRef = useRef(selectedNodeIds);
    selectedNodeIdsRef.current = selectedNodeIds;
    const handleNodeClick = useCallback(
        (nodeId: string, modifiers: { ctrlKey: boolean; metaKey: boolean; shiftKey: boolean }) => {
            const additive = modifiers.ctrlKey || modifiers.metaKey || modifiers.shiftKey;
            setSelectedNodeIds((prev) => {
                if (!additive) return new Set([nodeId]);
                const next = new Set(prev);
                if (next.has(nodeId)) next.delete(nodeId);
                else next.add(nodeId);
                // Keep at least one node selected when toggling the
                // only selected node — otherwise Ctrl-clicking it
                // deselects entirely, which feels weird. (If the user
                // wants to clear, they can click the empty canvas.)
                return next.size === 0 ? prev : next;
            });
        },
        [],
    );

    // Panels
    const [pickerOpen, setPickerOpen] = useState(false);
    // Right-click context menu on empty canvas. Viewport coords; null = closed.
    const [contextMenu, setContextMenu] = useState<{ x: number; y: number } | null>(null);
    const [newProjectOpen, setNewProjectOpen] = useState(false);
    const [browseOpen, setBrowseOpen] = useState(false);
    // Delay heavy CanvasMiniMap renders until after the browse-projects
    // bottom sheet finishes sliding in — otherwise 7 project previews
    // all build their SVG (with <image> thumbnail fetches) during the
    // animation and the spring stutters on first frame.
    const [browseReady, setBrowseReady] = useState(false);
    useEffect(() => {
        if (!browseOpen) return;
        const t = window.setTimeout(() => setBrowseReady(true), 340);
        return () => window.clearTimeout(t);
    }, [browseOpen]);
    // Coalesce both state changes in a single render so the mini-maps
    // unmount on the SAME frame the exit animation starts — otherwise
    // the first exit frame still has all 7 SVGs live and the slide-out
    // stutters symmetrically to the old slide-in.
    const closeBrowse = useCallback(() => {
        setBrowseReady(false);
        setBrowseOpen(false);
    }, []);
    const [refreshing, setRefreshing] = useState(false);
    const [refreshDone, setRefreshDone] = useState(false);
    const [lightboxAsset, setLightboxAsset] = useState<CsAsset | null>(null);
    // Which edit-image node is currently showing its settings modal (if any).
    const [editSettingsNodeId, setEditSettingsNodeId] = useState<string | null>(null);
    // Which design node is currently open in the DesignEditorModal. null
    // closes the modal; set to a node id to open it with that scene.
    const [designEditorNodeId, setDesignEditorNodeId] = useState<string | null>(null);
    // Which EDIT-IMAGE node has the design editor open (Design is a tool on the
    // edit node now; the standalone design node is the legacy path).
    const [designEditNodeId, setDesignEditNodeId] = useState<string | null>(null);

    // Refs
    const canvasElRef = useRef<HTMLDivElement>(null);
    const pollingRef = useRef<NodeJS.Timeout | null>(null);
    const saveTimerRef = useRef<NodeJS.Timeout | null>(null);
    const initialLoadDone = useRef(false);

    // ─── Load ──────────────────────────────────────────────────────

    const loadBalance = useCallback(async () => {
        try {
            const res = await api.getCreditBalance();
            setBalance(res.balance);
        } catch { /* ignore */ }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        if (!projectUuid) return;
        // Reset all state immediately so stale data from previous project
        // doesn't flash while the new one loads
        setNodes([]);
        setProject(null);
        setLoading(true);
        setSelectedNodeIds(new Set());
        setPanX(100);
        setPanY(100);
        setZoom(DEFAULT_ZOOM);
        initialLoadDone.current = false;
        saveTargetRef.current = null;
        pendingSaveRef.current = false;
        if (saveTimerRef.current) { clearTimeout(saveTimerRef.current); saveTimerRef.current = null; }

        (async () => {
            try {
                const [p, r, a] = await Promise.all([
                    api.getProject(projectUuid),
                    api.listRecipes(),
                    api.listAssets({ projectUuid, limit: 200 }),
                ]);
                setProject(p);
                // Controls this build layers on in code (see recipe-extensions.ts).
                setRecipes(extendRecipes(r));

                // Restore canvas state from project
                if (p.canvasViewport) {
                    setPanX(p.canvasViewport.x ?? 100);
                    setPanY(p.canvasViewport.y ?? 100);
                    // Clamp: projects saved before the range narrowed carry a
                    // zoom the controls can no longer reach, which would leave
                    // the canvas stuck past the ceiling until the vendor
                    // happened to zoom out.
                    const saved = p.canvasViewport.zoom;
                    setZoom(
                        Number.isFinite(saved)
                            ? Math.max(ZOOM_MIN, Math.min(ZOOM_MAX, saved as number))
                            : DEFAULT_ZOOM,
                    );
                }

                // Restore nodes from persisted canvasGraph, refreshing
                // generation assets with latest server data
                const assetMap = new Map(
                    (a.items || []).map((asset: CsAsset) => [asset.uuid, asset]),
                );
                const board = (p as any).board;
                if (Array.isArray(p.canvasGraph) && p.canvasGraph.length === 0 && board?.shots?.length) {
                    // A Studio production that has never been opened here: its
                    // graph is the default empty list, not a cleared canvas.
                    // Draw the same production as nodes (see board-to-canvas.ts).
                    await nodesFromBoard(board, a.items || []);
                } else if (Array.isArray(p.canvasGraph)) {
                    // A saved graph exists and is AUTHORITATIVE — even when it's
                    // empty (the vendor cleared the canvas / rejected everything).
                    // Restore exactly those nodes, refreshing generation and
                    // wall-picker assets from the latest server data. We do NOT
                    // fall back to the asset list here: doing so would resurrect
                    // rejected/removed generations that still linger on the
                    // backend as independent stacked nodes.
                    const restored = (p.canvasGraph as CanvasNode[]).map((node) => {
                        if (node.kind === 'generation') {
                            const gn = node as CanvasGenerationNode;
                            const fresh = assetMap.get(gn.asset?.uuid);
                            return fresh ? { ...gn, asset: fresh } : gn;
                        }
                        if (node.kind === 'wall-picker') {
                            const wp = node as CanvasWallPickerNode;
                            const fresh = assetMap.get(wp.asset?.uuid);
                            return fresh ? { ...wp, asset: fresh } : wp;
                        }
                        return node;
                    });
                    setNodes(restored);
                } else {
                    // Legacy project with no saved graph at all — auto-layout
                    // from the asset list as a one-time bootstrap.
                    await rebuildNodesFromAssets(a.items || []);
                }

                initialLoadDone.current = true;
                saveTargetRef.current = projectUuid!;
            } catch (err) {
                console.error('Failed to load workspace', err);
            } finally {
                setLoading(false);
            }
            loadBalance();
            // Load sibling projects for tabs
            try {
                const siblings = await api.listProjects({ limit: 20 });
                setSiblingProjects(siblings.items || []);
            } catch { /* ignore */ }
        })();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [projectUuid]);

    // ─── Draw a Studio board as nodes ──
    const nodesFromBoard = async (board: StudioBoard, assets: CsAsset[]) => {
        const wanted = Array.from(new Set(board.shots.flatMap((s) => s.productUuids || []).filter(Boolean)));
        const resolved = new Map<string, PlannerProduct>();
        if (wanted.length) {
            try {
                const { products } = await productsApi.findProductsPage({ uuids: wanted, page: 1, limit: wanted.length, draft: 'all' });
                for (const raw of products || []) {
                    const product = toCanvasProduct(raw) as PlannerProduct;
                    if (wanted.includes(product.uuid)) resolved.set(product.uuid, product);
                }
            } catch { /* names are a nicety */ }
        }
        setNodes(boardToCanvas(board, new Map(assets.map((x) => [x.uuid, x])), resolved));
    };

    // ─── Rebuild nodes from server assets (fallback if no saved graph) ──

    const rebuildNodesFromAssets = async (assets: CsAsset[]) => {
        if (assets.length === 0) return;

        /**
         * Look the products up before drawing them.
         *
         * `mentionableProducts` is filled by a different effect that runs after
         * this one, so reading it here found an empty list every time and every
         * product node was labelled with the first eight characters of its
         * uuid — "3083CBD5", which reads as a product that does not exist.
         *
         * Fetched here instead, and only when there is something to resolve.
         */
        const wantedUuids = Array.from(
            new Set(assets.flatMap((a) => (a.sourceProductUuids ?? []).filter(Boolean))),
        );

        const resolved = new Map<string, PlannerProduct>();
        if (wantedUuids.length) {
            try {
                const { products } = await productsApi.findProductsPage({
                    uuids: wantedUuids, page: 1, limit: wantedUuids.length, draft: 'all',
                });
                for (const raw of products || []) {
                    const product = toCanvasProduct(raw) as PlannerProduct;
                    if (wantedUuids.includes(product.uuid)) resolved.set(product.uuid, product);
                }
            } catch {
                // Names are a nicety; the graph is still worth drawing without
                // them. What must not happen is inventing one.
            }
        }

        /**
         * Rebuild the whole chain, not just the pictures.
         *
         * This used to emit bare generation nodes with `recipeNodeId: ''` — so a
         * project restored this way showed images floating with nothing that
         * made them: no product they were of, no recipe they came from, and no
         * way to run it again. The canvas is a graph, and half a graph reads as
         * a bug.
         *
         * Everything needed is on the asset: `sourceProductUuids`, `recipeSlug`
         * and `recipeInputs`. Assets are grouped by (product, recipe) so a
         * recipe run four times is one node with four results, which is what it
         * would have looked like had it been built here in the first place.
         */
        const rebuilt: CanvasNode[] = [];
        const productNodeByKey = new Map<string, string>();
        const recipeNodeByKey = new Map<string, CanvasRecipeNode>();
        let productY = 80;
        let recipeY = 80;
        let genY = 80;

        for (const asset of assets) {
            // Empty strings appear when a generation was submitted without a
            // product; they are not a product and must not become a node.
            const productUuid = (asset.sourceProductUuids ?? []).find((u) => !!u);

            let productNodeId = '';
            if (productUuid) {
                productNodeId = productNodeByKey.get(productUuid) ?? '';

                if (!productNodeId) {
                    productNodeId = `product-${productUuid}`;
                    productNodeByKey.set(productUuid, productNodeId);

                    const known = resolved.get(productUuid);
                    rebuilt.push({
                        kind: 'product',
                        id: productNodeId,
                        x: 80,
                        y: productY,
                        /**
                         * A product that could not be resolved says so, rather
                         * than wearing a slice of its uuid as a name. It has
                         * usually been deleted since the images were made, and
                         * "3083CBD5" looks like a product rather than the
                         * absence of one.
                         */
                        products: [
                            known ?? {
                                uuid: productUuid,
                                name: t(
                                    'CONTENT_STUDIO.CANVAS.PRODUCT_UNAVAILABLE',
                                    'Product no longer available',
                                ) as string,
                            },
                        ],
                    });
                    productY += 260;
                }
            }

            const recipeKey = `${productNodeId}|${asset.recipeSlug}`;
            let recipeNode = recipeNodeByKey.get(recipeKey);

            if (!recipeNode) {
                recipeNode = {
                    kind: 'recipe',
                    id: `recipe-${asset.uuid}`,
                    x: 340,
                    y: recipeY,
                    productNodeId,
                    recipeSlug: asset.recipeSlug,
                    inputs: (asset as any).recipeInputs ?? {},
                    generationNodeIds: [],
                    // Nothing is in flight — these results already exist.
                    generating: false,
                    // Folded, like a plan's nodes: a rebuilt project is being
                    // looked at, not configured.
                    collapsed: true,
                };
                recipeNodeByKey.set(recipeKey, recipeNode);
                rebuilt.push(recipeNode);
                recipeY += 260;
            }

            const genId = `gen-${asset.uuid}`;
            recipeNode.generationNodeIds.push(genId);
            rebuilt.push({
                kind: 'generation',
                id: genId,
                x: 600,
                y: genY,
                asset,
                recipeNodeId: recipeNode.id,
            });
            genY += 260;
        }

        setNodes(rebuilt);
    };

    // ─── Auto-save canvas graph (debounced 1.5s + flush on unmount) ──

    // Keep refs to the latest state + the projectUuid that OWNS this
    // canvas session. The save target is locked to the UUID that was
    // active when the load completed — navigating to a new project
    // must NOT flush the old nodes to the new UUID.
    const latestStateRef = useRef({ nodes, panX, panY, zoom });
    latestStateRef.current = { nodes, panX, panY, zoom };
    const pendingSaveRef = useRef(false);
    const saveTargetRef = useRef<string | null>(null);

    // Lock save target once load completes (set alongside initialLoadDone)
    // This ref does NOT update when projectUuid changes from navigation.

    const saveNow = useCallback(() => {
        const target = saveTargetRef.current;
        if (!target || !initialLoadDone.current) return;
        const { nodes: n, panX: px, panY: py, zoom: z } = latestStateRef.current;
        pendingSaveRef.current = false;
        api.updateProject(target, {
            canvasGraph: n,
            canvasViewport: { x: px, y: py, zoom: z },
        }).catch(() => { /* silent */ });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Debounced save on every change
    useEffect(() => {
        if (!initialLoadDone.current || !saveTargetRef.current) return;
        pendingSaveRef.current = true;
        if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
        saveTimerRef.current = setTimeout(saveNow, 1500);
        return () => {
            if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [nodes, panX, panY, zoom]);

    /**
     * Leaving WITHOUT unmounting.
     *
     * The debounce saves 1.5s after a change and the unmount effect below
     * catches whatever is still pending — but React only unmounts when you
     * navigate inside the app. Closing the tab, reloading, or typing a URL
     * tears the page down without running any cleanup, so everything newer
     * than the last save goes with it.
     *
     * The expensive case is a capture. Posing the 3D studio writes the frame
     * to the node and the vendor is then done, so "capture, then close" is the
     * NORMAL way to end that piece of work — and it lost the frame every time.
     * The picture it generated survived (it lives on the asset), but the card
     * reopened with no snapshot, asking to be posed again before it would run.
     * Nineteen of twenty-five captured jobs in the history had lost it this way.
     *
     * `visibilitychange` is the one that does the work: it fires while the page
     * is still alive and able to make an ordinary request. `pagehide` is the
     * backstop for the paths that skip it, and is best-effort by nature.
     */
    useEffect(() => {
        const flush = () => { if (pendingSaveRef.current) saveNow(); };
        const onVisibility = () => { if (document.visibilityState === 'hidden') flush(); };
        document.addEventListener('visibilitychange', onVisibility);
        window.addEventListener('pagehide', flush);
        return () => {
            document.removeEventListener('visibilitychange', onVisibility);
            window.removeEventListener('pagehide', flush);
        };
    }, [saveNow]);

    // Flush on unmount so navigation doesn't lose unsaved changes, AND
    // hard-delete any asset the vendor rejected/removed this session that
    // isn't back on the canvas. The per-history deferred flush only fires
    // once a snapshot ages off the 20-deep undo stack — if the vendor leaves
    // before that, the rejected asset would survive on the backend and
    // reappear (via the asset-rebuild fallback when canvasGraph is empty) the
    // next time the project is opened. Deleting the un-referenced tombstones
    // here closes that leak while preserving in-session undo (a tombstone
    // whose node was restored via undo is still on the canvas, so it's
    // spared).
    useEffect(() => {
        return () => {
            if (pendingSaveRef.current) saveNow();
            if (tombstonesRef.current.size > 0) {
                const live = new Set(
                    nodesRef.current
                        .map((n) => (n.kind === 'generation' || n.kind === 'wall-picker')
                            ? (n as CanvasGenerationNode | CanvasWallPickerNode).asset?.uuid
                            : undefined)
                        .filter((uuid): uuid is string => !!uuid),
                );
                tombstonesRef.current.forEach((uuid) => {
                    if (!live.has(uuid)) api.deleteAsset(uuid).catch(() => {});
                });
                tombstonesRef.current.clear();
            }
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // ─── Polling for pending assets ────────────────────────────────

    // Keeps asking while anything is pending — and for a while after a failure,
    // which is not always final. See shouldKeepPolling.
    const hasPending = useMemo(() => shouldKeepPolling(nodes), [nodes]);

    useEffect(() => {
        if (!hasPending) {
            if (pollingRef.current) {
                clearInterval(pollingRef.current);
                pollingRef.current = null;
            }
            return;
        }
        if (!pollingRef.current) {
            pollingRef.current = setInterval(async () => {
                if (!projectUuid) return;
                try {
                    const a = await api.listAssets({ projectUuid, limit: 200 });
                    const assetMap = new Map(
                        (a.items || []).map((asset: CsAsset) => [asset.uuid, asset]),
                    );
                    setNodes((prev) =>
                        prev.map((n) => {
                            if (n.kind === 'generation') {
                                const updated = assetMap.get(n.asset.uuid);
                                if (updated) return { ...n, asset: updated };
                                return n;
                            }
                            if (n.kind === 'wall-picker') {
                                const wp = n as CanvasWallPickerNode;
                                const updated = assetMap.get(wp.asset.uuid);
                                if (!updated) return n;
                                // First time the setup asset goes ready,
                                // seed the selection to "all walls" so a
                                // single Continue press matches today's
                                // behavior. Vendor can then deselect.
                                const justBecameReady = wp.asset.status !== 'ready' && updated.status === 'ready';
                                const next: CanvasWallPickerNode = { ...wp, asset: updated };
                                if (justBecameReady && updated.wallPicker?.walls?.length) {
                                    next.selectedWallIds = updated.wallPicker.walls.map((w) => w.id);
                                }
                                return next;
                            }
                            return n;
                        }),
                    );
                    loadBalance();
                } catch { /* ignore */ }
            }, 3000);
        }
        return () => {
            if (pollingRef.current && !hasPending) {
                clearInterval(pollingRef.current);
                pollingRef.current = null;
            }
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [hasPending, projectUuid]);

    // ─── Node mutation helpers ─────────────────────────────────────

    const moveNode = useCallback((nodeId: string, deltaX: number, deltaY: number) => {
        // Snapshot once at the start of a drag session so undo restores
        // the pre-drag layout. dragPushedRef gates repeat pushes across
        // the many onDrag calls that happen per frame; it resets on the
        // next global mouseup.
        beginDragSession();
        // If the dragged node is part of the current multi-selection,
        // move every selected node by the same delta so the whole
        // group travels together. Otherwise just move the one node.
        // selectedNodeIdsRef avoids stale-closure — the callback is
        // bound once per render but the drag delta stream can span
        // many frames.
        const selected = selectedNodeIdsRef.current;
        const group = selected.has(nodeId) && selected.size > 1 ? selected : new Set([nodeId]);
        setNodes((prev) =>
            prev.map((n) => (group.has(n.id) ? { ...n, x: n.x + deltaX, y: n.y + deltaY } : n)),
        );
    }, [beginDragSession]);

    // Browser download for any content-studio image URL. Fetches the
    // blob (Spaces allows CORS for public reads) so the download-link
    // filename actually sticks; falling back to a plain anchor-href
    // pointing at the CDN URL would open the image in-browser instead
    // of prompting a save on cross-origin URLs.
    const downloadAsset = useCallback(async (url: string, filename: string) => {
        try {
            const res = await fetch(url, { mode: 'cors' });
            const blob = await res.blob();
            const objUrl = URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = objUrl;
            a.download = filename;
            document.body.appendChild(a);
            a.click();
            document.body.removeChild(a);
            URL.revokeObjectURL(objUrl);
        } catch (err) {
            // CORS fallback — open the URL directly; the user can
            // save it via right-click > Save As.
            // eslint-disable-next-line no-console
            console.warn('[ContentStudio] CORS download failed, opening in new tab', err);
            window.open(url, '_blank', 'noopener');
        }
    }, []);

    const removeNode = useCallback((nodeId: string) => {
        pushHistory();
        setNodes((prev) => {
            if (!prev.find((n) => n.id === nodeId)) return prev;
            // Cascade-delete ALL descendants. Parent→child edges in this graph:
            //   product   → recipe (via recipe.productNodeId)
            //   recipe    → generation (via generation.recipeNodeId)
            //   generation→ variation recipe (ALSO via recipe.productNodeId —
            //               the field doubles as "parent node id" when a
            //               variation or other requiresSourceAsset recipe is
            //               chained off a generation) and edit-image (via
            //               editImage.sourceNodeId)
            //   edit-image→ generation (via generation.recipeNodeId pointing
            //               at the edit-image's id — edit-image acts as a
            //               recipe for its downstream generation)
            // BFS down the graph so removing an upstream node takes the
            // whole downstream branch (variation, edit chains, their own
            // generations, etc.) with it.
            const toRemove = new Set<string>([nodeId]);
            const queue: string[] = [nodeId];
            while (queue.length > 0) {
                const currentId = queue.shift()!;
                for (const n of prev) {
                    if (toRemove.has(n.id)) continue;
                    let isChild = false;
                    if (n.kind === 'recipe' && (n as CanvasRecipeNode).productNodeId === currentId) {
                        isChild = true;
                    } else if (n.kind === 'generation' && (n as CanvasGenerationNode).recipeNodeId === currentId) {
                        isChild = true;
                    } else if (n.kind === 'edit-image' && (n as CanvasEditImageNode).sourceNodeId === currentId) {
                        isChild = true;
                    } else if (n.kind === 'wall-picker' && (n as CanvasWallPickerNode).recipeNodeId === currentId) {
                        // WallPicker is a child of its upstream Recipe.
                        // Once added, its own child generations cascade
                        // via the existing generation.recipeNodeId rule
                        // (those gens store wallPickerId as recipeNodeId).
                        isChild = true;
                    }
                    if (isChild) {
                        toRemove.add(n.id);
                        queue.push(n.id);
                    }
                }
            }
            return prev.filter((n) => !toRemove.has(n.id));
        });
    }, []);

    // ─── Product drop from picker ──────────────────────────────────

    const handleProductsDrop = useCallback(
        (products: ProductNodeProduct[]) => {
            const id = `prod-${uuidv4().slice(0, 8)}`;
            // Place at the center of the current viewport so the user always sees it
            const rect = canvasElRef.current?.getBoundingClientRect();
            const vw = rect?.width || 800;
            const vh = rect?.height || 600;
            const centerX = (-panX + vw / 2) / zoom - PRODUCT_NODE_WIDTH / 2;
            const centerY = (-panY + vh / 2) / zoom - 100;
            const newNode: CanvasProductNode = {
                kind: 'product',
                id,
                x: Math.round(centerX),
                y: Math.round(centerY),
                products,
            };
            pushHistory();
            setNodes((prev) => [...prev, newNode]);
            setPickerOpen(false);
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [panX, panY, zoom],
    );

    // ─── Add recipe node from product ──────────────────────────────

    // ─── Recipe selector (RPG-style card picker) ────────────────

    const [recipeSelector, setRecipeSelector] = useState<{
        productNodeId: string;
        anchorX: number;
        anchorY: number;
    } | null>(null);

    const handleOpenRecipeSelector = useCallback(
        (productNodeId: string) => {
            const productNode = nodes.find((n) => n.id === productNodeId);
            if (!productNode) return;
            // Position the selector just after the node's "+" handle (which sits
            // ~16px past the node's right edge and is ~26px wide), so the recipe
            // options read as opening from the "+".
            setRecipeSelector({
                productNodeId,
                anchorX: productNode.x + PRODUCT_NODE_WIDTH + 50,
                anchorY: productNode.y,
            });
        },
        [nodes],
    );

    const handleSelectRecipe = useCallback(
        (recipe: CsRecipe) => {
            if (!recipeSelector) return;
            const { productNodeId } = recipeSelector;
            const productNode = nodes.find((n) => n.id === productNodeId);
            if (!productNode) return;

            // Count existing recipes for this product to offset Y
            const existingRecipes = nodes.filter(
                (n) => n.kind === 'recipe' && (n as CanvasRecipeNode).productNodeId === productNodeId,
            );

            const recipeId = `rec-${uuidv4().slice(0, 8)}`;
            // Prefer the linked product's visualization type when hydrating
            // defaults — lets us pick a viz-applicable default for inputs
            // whose seeded default targets a different product family
            // (e.g. `preset` defaults to a surface-only option in the seed,
            // so furniture nodes would otherwise mount with nothing selected).
            const productVizType = productNode.kind === 'product'
                ? (productNode as CanvasProductNode).products?.[0]?.visualizationType
                : undefined;
            const defaultInputs: Record<string, any> = {};
            for (const input of recipe.inputs || []) {
                if (input.default === undefined) continue;
                let defaultVal: any = input.default;
                const opts = (input as any).options as Array<{ value: any; appliesTo?: string[] }> | undefined;
                if (Array.isArray(opts) && productVizType) {
                    const defaultOpt = opts.find((o) => o.value === defaultVal);
                    const defaultAppliesTo = defaultOpt?.appliesTo;
                    const defaultApplies =
                        !defaultAppliesTo
                        || defaultAppliesTo.length === 0
                        || defaultAppliesTo.includes(productVizType);
                    if (!defaultApplies) {
                        const firstApplicable = opts.find(
                            (o) => !o.appliesTo || o.appliesTo.length === 0 || o.appliesTo.includes(productVizType),
                        );
                        if (firstApplicable) defaultVal = firstApplicable.value;
                    }
                }
                defaultInputs[input.key] = defaultVal;
            }
            // Recipe-level mode default overrides. Ensures the UI opens
            // on the intended tab even when the recipe doc in Mongo still
            // has an older seeded default (pre-restart).
            if (recipe.slug === 'product-closeup') {
                // Surface Product Photography is split into two cards (Single Piece
                // / In-Room) that lock the shot type via `_surfaceMode`. Single opens
                // on Preset; In-Room (applied) has no Preset tab → open on Prompt.
                const sm = (recipe as any)._surfaceMode as 'single' | 'applied' | undefined;
                if (sm) {
                    defaultInputs.surfaceMode = sm;
                    // Applied opens on Custom (Environment + Lighting have defaults, so
                    // it can generate right after a capture); Single opens on Preset.
                    // Prompt is opt-in (it requires a typed description).
                    defaultInputs.mode = sm === 'applied' ? 'options' : 'preset';
                    // Room Scene opens WITH the 3D studio on, because that is now the
                    // only way it renders. It used to open in "Lifestyle" (studio off,
                    // submitted under lifestyle-scene, drawn by the visualizer
                    // pipeline) — but the toggle beside it only ever switches the
                    // studio ON and calls itself required, so a card opening off was
                    // opening in a mode the vendor could leave and never return to,
                    // down a path that is no longer offered anywhere.
                    if (sm === 'applied') defaultInputs.use3dCamera = true;
                } else {
                    defaultInputs.mode = 'preset';
                }
            }

            const recPos = findFreeSpawn(
                productNode.x + PRODUCT_NODE_WIDTH + 80,
                productNode.y + existingRecipes.length * 560,
                RECIPE_NODE_WIDTH,
                320,
            );
            const newRecipeNode: CanvasRecipeNode = {
                kind: 'recipe',
                id: recipeId,
                x: recPos.x,
                y: recPos.y,
                productNodeId,
                recipeSlug: recipe.slug,
                inputs: defaultInputs,
                generationNodeIds: [],
                generating: false,
            };

            pushHistory();
            setNodes((prev) => [...prev, newRecipeNode]);
            setSelectedNodeIds(new Set([recipeId]));
            setRecipeSelector(null);
            focusNode(recPos.x, recPos.y, RECIPE_NODE_WIDTH, 320);
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [nodes, recipeSelector],
    );

    // ─── Replace product (swap, keep downstream) ──────────────────

    const [replacingProductNodeId, setReplacingProductNodeId] = useState<string | null>(null);
    /**
     * Set when the catalogue is opened FROM a moodboard, to add rather than
     * replace: the vendor is composing a scene, so a second sofa joins the node
     * instead of evicting the first.
     */
    const [appendingProductNodeId, setAppendingProductNodeId] = useState<string | null>(null);

    const handleReplaceProduct = useCallback(
        (productNodeId: string, newProducts: { uuid: string; name: string; thumbnailPath?: string }[]) => {
            setNodes((prev) =>
                prev.map((n) =>
                    n.id === productNodeId && n.kind === 'product'
                        ? { ...n, products: newProducts }
                        : n,
                ),
            );
            setReplacingProductNodeId(null);
            setPickerOpen(false);
        },
        [],
    );

    /** Add products to a node that already has some, skipping ones already there. */
    const handleAppendProducts = useCallback(
        (productNodeId: string, incoming: Array<{ uuid: string }>) => {
            setNodes((prev) => prev.map((n) => {
                if (n.id !== productNodeId || n.kind !== 'product') return n;
                const have = new Set(n.products.map((p) => p.uuid));
                const added = incoming.filter((p) => !have.has(p.uuid));
                return added.length ? { ...n, products: [...n.products, ...added as any] } : n;
            }));
            setAppendingProductNodeId(null);
            setPickerOpen(false);
        },
        [],
    );

    // ─── Duplicate product (deep clone product + recipes, no generations) ──

    const handleDuplicateProduct = useCallback(
        (productNodeId: string) => {
            const productNode = nodes.find((n) => n.id === productNodeId) as CanvasProductNode | undefined;
            if (!productNode) return;

            const newProductId = `prod-${uuidv4().slice(0, 8)}`;
            const yOffset = 300;

            const newProductNode: CanvasProductNode = {
                ...productNode,
                id: newProductId,
                y: productNode.y + yOffset,
            };

            // Clone all recipes attached to this product (without generations)
            const childRecipes = nodes.filter(
                (n) => n.kind === 'recipe' && (n as CanvasRecipeNode).productNodeId === productNodeId,
            ) as CanvasRecipeNode[];

            const clonedRecipes: CanvasRecipeNode[] = childRecipes.map((r) => ({
                ...r,
                id: `rec-${uuidv4().slice(0, 8)}`,
                y: r.y + yOffset,
                productNodeId: newProductId,
                generationNodeIds: [],
                generating: false,
            }));

            setNodes((prev) => [...prev, newProductNode, ...clonedRecipes]);
        },
        [nodes],
    );

    // ─── Duplicate recipe (clone with same settings, same parent) ──

    const handleDuplicateEditImage = useCallback(
        (editNodeId: string) => {
            const editNode = nodes.find((n) => n.id === editNodeId) as CanvasEditImageNode | undefined;
            if (!editNode) return;
            const newId = `edit-${uuidv4().slice(0, 8)}`;
            const cloned: CanvasEditImageNode = {
                ...editNode,
                id: newId,
                y: editNode.y + 420,
                generationNodeIds: [],
                generating: false,
            };
            pushHistory();
            setNodes((prev) => [...prev, cloned]);
            setSelectedNodeIds(new Set([newId]));
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [nodes],
    );

    const handleDuplicateRecipe = useCallback(
        (recipeNodeId: string) => {
            const recipeNode = nodes.find((n) => n.id === recipeNodeId) as CanvasRecipeNode | undefined;
            if (!recipeNode) return;

            const newId = `rec-${uuidv4().slice(0, 8)}`;
            const cloned: CanvasRecipeNode = {
                ...recipeNode,
                id: newId,
                y: recipeNode.y + 560,
                generationNodeIds: [],
                generating: false,
            };

            setNodes((prev) => [...prev, cloned]);
            setSelectedNodeIds(new Set([newId]));
        },
        [nodes],
    );

    // ─── Re-run generation (same recipe + inputs → new generation) ──

    const handleRerun = useCallback(
        async (asset: CsAsset) => {
            const genNode = nodes.find(
                (n) => n.kind === 'generation' && (n as CanvasGenerationNode).asset.uuid === asset.uuid,
            ) as CanvasGenerationNode | undefined;
            if (!genNode || !projectUuid) return;

            // Two parent shapes produce generations: recipe nodes (normal
            // catalog recipes) and edit-image nodes (Expand / Retouch /
            // Modify / Graphics tools). The retry flow differs because
            // edit-image tracks its inputs per-tool on the node itself.
            const editParent = nodes.find(
                (n) => n.kind === 'edit-image' && (n as CanvasEditImageNode).generationNodeIds.includes(genNode.id),
            ) as CanvasEditImageNode | undefined;
            if (editParent) {
                // Drop the failed asset from the canvas + server, then
                // kick off a fresh generation from the edit-image node.
                api.deleteAsset(asset.uuid).catch(() => {});
                setNodes((prev) => prev
                    .filter((n) => n.id !== genNode.id)
                    .map((n) => n.id === editParent.id && n.kind === 'edit-image'
                        ? { ...n, generationNodeIds: n.generationNodeIds.filter((gid) => gid !== genNode.id) }
                        : n,
                    ),
                );
                handleEditImageGenerate(editParent.id);
                return;
            }

            const recipeNode = nodes.find(
                (n) => n.kind === 'recipe' && (n as CanvasRecipeNode).generationNodeIds.includes(genNode.id),
            ) as CanvasRecipeNode | undefined;
            if (!recipeNode) return;

            const parentNode = nodes.find((n) => n.id === recipeNode.productNodeId);
            const productNode = parentNode?.kind === 'product'
                ? (parentNode as CanvasProductNode)
                : undefined;
            const parentGenNode = parentNode?.kind === 'generation'
                ? (parentNode as CanvasGenerationNode)
                : undefined;

            // Delete the old asset
            api.deleteAsset(asset.uuid).catch(() => {});

            // Set the existing node back to pending
            setNodes((prev) =>
                prev.map((n) =>
                    n.id === genNode.id && n.kind === 'generation'
                        ? { ...n, asset: { ...asset, status: 'pending' as const, url: undefined, thumbnailUrl: undefined, error: undefined }, approved: undefined }
                        : n,
                ),
            );

            // Gallery selection on the linked product node (if any) —
            // the worker reads this to put the chosen gallery image first
            // in productContext.imageUrls so it becomes the primary
            // reference for the generation. excludedGalleryIndexes lets
            // the vendor opt specific gallery entries OUT of the
            // reference set entirely (e.g. irrelevant angles).
            const primaryProduct = productNode?.products?.[0];
            const selectedGalleryIndex =
                typeof primaryProduct?.selectedGalleryIndex === 'number'
                    ? primaryProduct.selectedGalleryIndex
                    : -1;
            const excludedGalleryIndexes = Array.isArray(primaryProduct?.excludedGalleryIndexes)
                ? primaryProduct!.excludedGalleryIndexes
                : [];

            const site1Slug = effectiveRecipeSlug(recipeNode.recipeSlug, recipeNode.inputs, primaryProduct?.visualizationType);
            try {
                const result = await api.createGeneration({
                    projectUuid,
                    recipeSlug: site1Slug,
                    inputs: fillLifestyleRequiredDefaults({
                        ...recipeNode.inputs,
                        selectedGalleryIndex,
                        excludedGalleryIndexes,
                    }, site1Slug === 'lifestyle-scene' ? recipeBySlug.get('lifestyle-scene') : undefined),
                    sourceProductUuids: jobProductUuids(recipeNode, productNode),
                    parentAssetUuid: parentGenNode?.asset.uuid,
                    count: 1,
                });

                // Replace the old asset in the same node with the new one
                const newAsset = result.assets?.[0];
                if (newAsset) {
                    setNodes((prev) =>
                        prev.map((n) => {
                            if (n.id === genNode.id && n.kind === 'generation') {
                                return { ...n, asset: newAsset, approved: undefined };
                            }
                            // Update the recipe's generationNodeIds to point to the new asset
                            if (n.id === recipeNode.id && n.kind === 'recipe') {
                                const rn = n as CanvasRecipeNode;
                                return {
                                    ...rn,
                                    generationNodeIds: rn.generationNodeIds.map(
                                        (gid) => gid === genNode.id ? `gen-${newAsset.uuid}` : gid,
                                    ),
                                };
                            }
                            return n;
                        }).map((n) =>
                            // Also update the node id to match the new asset
                            n.id === genNode.id ? { ...n, id: `gen-${newAsset.uuid}` } : n,
                        ),
                    );
                }
                loadBalance();
            } catch (err) {
                console.error('Re-run failed', err);
                // Mark as failed in the same node
                setNodes((prev) =>
                    prev.map((n) =>
                        n.id === genNode.id && n.kind === 'generation'
                            ? { ...n, asset: { ...asset, status: 'failed' as const, error: 'Re-run failed' } }
                            : n,
                    ),
                );
            }
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [nodes, projectUuid],
    );

    // ─── Recipe input changes ──────────────────────────────────────

    const handleRecipeChange = useCallback(
        (recipeNodeId: string, recipeSlug: string, inputs: Record<string, any>) => {
            pushHistory();
            setNodes((prev) =>
                prev.map((n) =>
                    n.id === recipeNodeId && n.kind === 'recipe'
                        ? { ...n, recipeSlug, inputs }
                        : n,
                ),
            );
        },
        [pushHistory],
    );

    const handleRecipeInputChange = useCallback(
        (recipeNodeId: string, key: string, value: any) => {
            pushHistory();
            setNodes((prev) =>
                prev.map((n) =>
                    n.id === recipeNodeId && n.kind === 'recipe'
                        ? { ...n, inputs: { ...n.inputs, [key]: value } }
                        : n,
                ),
            );
        },
        [pushHistory],
    );

    // ─── Generate ──────────────────────────────────────────────────

    /**
     * Chained steps waiting for the picture they are chained to.
     *
     * A plan says "shoot the rug, then make a video of it": the video node hangs
     * off the shoot's RECIPE node, which has no picture yet when the plan is
     * applied. It used to be fired anyway, went out with no parent asset, and
     * nothing came back — the video node sat there, correct and idle, in every
     * matrix run (11 Oct 2026). It now waits here until its parent recipe has a
     * ready image, and runs from that image.
     */
    const waitForParentRef = useRef<Set<string>>(new Set());

    const handleGenerate = useCallback(
        async (recipeNodeId: string) => {
            const recipeNode = nodes.find((n) => n.id === recipeNodeId) as CanvasRecipeNode | undefined;
            if (!recipeNode || !projectUuid) return;
            // The "parent" of a recipe can be a product node (catalog recipes)
            // OR a generation node (variation / image-to-video chained off a
            // ready asset). Resolve both shapes so we send the correct fields.
            const parentNode = nodes.find((n) => n.id === recipeNode.productNodeId);
            const productNode = parentNode?.kind === 'product'
                ? (parentNode as CanvasProductNode)
                : undefined;
            let parentGenNode = parentNode?.kind === 'generation'
                ? (parentNode as CanvasGenerationNode)
                : undefined;
            // Chained to another recipe (a plan step): run from its newest ready
            // picture, or wait for one.
            if (parentNode?.kind === 'recipe') {
                const parentGens = nodes.filter((n) => n.kind === 'generation'
                    && (n as CanvasGenerationNode).recipeNodeId === parentNode.id) as CanvasGenerationNode[];
                const ready = [...parentGens].reverse().find((g) => g.asset?.status === 'ready');
                if (!ready) {
                    waitForParentRef.current.add(recipeNodeId);
                    return;
                }
                parentGenNode = ready;
            }

            pushHistory();
            setNodes((prev) =>
                prev.map((n) =>
                    n.id === recipeNodeId && n.kind === 'recipe'
                        ? { ...n, generating: true }
                        : n,
                ),
            );

            // Gallery selection on the linked product node (if any) —
            // the worker reads this to put the chosen gallery image
            // first in productContext.imageUrls.
            const primaryProductInitial = productNode?.products?.[0];
            const selectedGalleryIndex =
                typeof primaryProductInitial?.selectedGalleryIndex === 'number'
                    ? primaryProductInitial.selectedGalleryIndex
                    : -1;
            const excludedGalleryIndexes = Array.isArray(primaryProductInitial?.excludedGalleryIndexes)
                ? primaryProductInitial!.excludedGalleryIndexes
                : [];

            // ─── WallPicker setup-phase routing ───────────────────────
            // For lifestyle-scene with surface=walls, we don't render
            // straight to a final image. We first run a setup-only pass
            // that generates the base interior + analyzes it, then the
            // vendor picks which walls receive the texture and the
            // resulting render-phase generation spawns downstream of the
            // picker. Floor flow + non-lifestyle recipes are unchanged.
            // Room Scene · Lifestyle submits under lifestyle-scene → resolve first.
            const submitSlug = effectiveRecipeSlug(recipeNode.recipeSlug, recipeNode.inputs, productNode?.products?.[0]?.visualizationType);
            const isLifestyleScene =
                submitSlug === 'lifestyle-scene'
                || submitSlug === 'lifestyle-floor';
            // Which walls? A room has several, and "walls" does not say. The
            // decision lives in needsWallPicker because it used to test the
            // studio flag — which was force-set on every From Reference node,
            // so the vendor stopped being asked and every wall got textured.
            const routeThroughWallPicker = needsWallPicker({
                recipeSlug: submitSlug,
                visualizationType: productNode?.products?.[0]?.visualizationType,
                inputs: recipeNode.inputs || {},
            });

            try {
                // The mode is resolved at render and usually never stored, so a
                // node showing the Prompt tab submitted `mode: undefined`. The
                // worker then fell back to the option fields and produced a
                // living room while the vendor's spa prompt sat unused in
                // `sceneDetails`. Send the mode the card is actually showing.
                const nodeRecipe = recipeBySlug.get(recipeNode.recipeSlug);
                const resolvedMode = nodeRecipe
                    ? resolveRecipeFlags({
                        recipe: nodeRecipe,
                        inputs: recipeNode.inputs || {},
                        productVisualizationType:
                            productNode?.products?.[0]?.visualizationType as any,
                        productApplicationTypes: productNode?.products?.[0]?.applicationTypes,
                    })
                    : undefined;

                const baseInputs = fillLifestyleRequiredDefaults({
                    ...recipeNode.inputs,
                    ...(resolvedMode?.hasMode && recipeNode.inputs?.mode === undefined
                        ? { mode: resolvedMode.modeValue }
                        : {}),
                    selectedGalleryIndex,
                    excludedGalleryIndexes,
                }, isLifestyleScene ? recipeBySlug.get('lifestyle-scene') : undefined);
                const result: CsGenerationCreateResponse = await api.createGeneration({
                    projectUuid,
                    recipeSlug: submitSlug,
                    inputs: routeThroughWallPicker
                        ? { ...baseInputs, phase: 'setup' }
                        : baseInputs,
                    sourceProductUuids: jobProductUuids(recipeNode, productNode),
                    parentAssetUuid: parentGenNode?.asset.uuid,
                    count: 1,
                });

                if (routeThroughWallPicker) {
                    // The setup-phase asset will arrive with a wallPicker
                    // payload. Spawn a WallPicker canvas node that polls
                    // it; it'll show "Analyzing scene…" until ready.
                    const wpBase = findFreeSpawn(
                        recipeNode.x + RECIPE_NODE_WIDTH + 80,
                        recipeNode.y,
                        WALL_PICKER_NODE_WIDTH,
                        320,
                    );
                    const newPickerNodes: CanvasWallPickerNode[] = (result.assets || []).map(
                        (asset, i) => ({
                            kind: 'wall-picker' as const,
                            id: `wp-${asset.uuid}`,
                            x: wpBase.x,
                            y: wpBase.y + i * 260,
                            recipeNodeId,
                            asset,
                            selectedWallIds: asset.wallPicker?.walls?.map((w) => w.id) || [],
                            generationNodeIds: [],
                            generating: false,
                        }),
                    );
                    setNodes((prev) => {
                        const ids = newPickerNodes.map((p) => p.id);
                        return prev
                            .map((n) =>
                                n.id === recipeNodeId && n.kind === 'recipe'
                                    ? {
                                          ...n,
                                          generating: false,
                                          generationNodeIds: [...n.generationNodeIds, ...ids],
                                      }
                                    : n,
                            )
                            .concat(newPickerNodes);
                    });
                    focusNode(wpBase.x, wpBase.y, WALL_PICKER_NODE_WIDTH, 320);
                    loadBalance();
                    return;
                }

                const genBase = findFreeSpawn(
                    recipeNode.x + RECIPE_NODE_WIDTH + 80,
                    recipeNode.y,
                    GENERATION_NODE_WIDTH,
                    260,
                );
                const newGenNodes: CanvasGenerationNode[] = (result.assets || []).map(
                    (asset, i) => ({
                        kind: 'generation' as const,
                        id: `gen-${asset.uuid}`,
                        x: genBase.x,
                        y: genBase.y + i * 260,
                        asset,
                        recipeNodeId,
                    }),
                );

                setNodes((prev) => {
                    const genIds = newGenNodes.map((g) => g.id);
                    return prev
                        .map((n) =>
                            n.id === recipeNodeId && n.kind === 'recipe'
                                ? {
                                      ...n,
                                      generating: false,
                                      generationNodeIds: [
                                          ...n.generationNodeIds,
                                          ...genIds,
                                      ],
                                  }
                                : n,
                        )
                        .concat(newGenNodes);
                });

                focusNode(genBase.x, genBase.y, GENERATION_NODE_WIDTH, 260);
                loadBalance();
            } catch (err) {
                console.error('Generation failed', err);
                setNodes((prev) =>
                    prev.map((n) =>
                        n.id === recipeNodeId && n.kind === 'recipe'
                            ? { ...n, generating: false }
                            : n,
                    ),
                );
            }
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [nodes, projectUuid],
    );

    // ─── WallPicker callbacks ──────────────────────────────────────

    const handleToggleWall = useCallback(
        (wallPickerNodeId: string, wallId: number) => {
            pushHistory();
            setNodes((prev) =>
                prev.map((n) => {
                    if (n.id !== wallPickerNodeId || n.kind !== 'wall-picker') return n;
                    const wp = n as CanvasWallPickerNode;
                    const set = new Set(wp.selectedWallIds);
                    if (set.has(wallId)) set.delete(wallId);
                    else set.add(wallId);
                    return { ...wp, selectedWallIds: Array.from(set).sort((a, b) => a - b) };
                }),
            );
        },
        [pushHistory],
    );

    const handleWallPickerContinue = useCallback(
        async (wallPickerNodeId: string) => {
            const wpNode = nodes.find((n) => n.id === wallPickerNodeId) as
                | CanvasWallPickerNode
                | undefined;
            if (!wpNode || !projectUuid) return;
            const recipeNode = nodes.find(
                (n) => n.id === wpNode.recipeNodeId,
            ) as CanvasRecipeNode | undefined;
            if (!recipeNode) return;
            if (wpNode.asset.status !== 'ready' || !wpNode.asset.wallPicker) return;
            if (wpNode.selectedWallIds.length === 0) return;

            const productNodeParent = nodes.find((n) => n.id === recipeNode.productNodeId);
            const productNode = productNodeParent?.kind === 'product'
                ? (productNodeParent as CanvasProductNode)
                : undefined;
            const primaryProductInitial = productNode?.products?.[0];
            const selectedGalleryIndex =
                typeof primaryProductInitial?.selectedGalleryIndex === 'number'
                    ? primaryProductInitial.selectedGalleryIndex
                    : -1;
            const excludedGalleryIndexes = Array.isArray(primaryProductInitial?.excludedGalleryIndexes)
                ? primaryProductInitial!.excludedGalleryIndexes
                : [];

            pushHistory();
            setNodes((prev) =>
                prev.map((n) =>
                    n.id === wallPickerNodeId && n.kind === 'wall-picker'
                        ? { ...n, generating: true }
                        : n,
                ),
            );

            const renderSlug = effectiveRecipeSlug(recipeNode.recipeSlug, recipeNode.inputs, productNode?.products?.[0]?.visualizationType);
            try {
                const result: CsGenerationCreateResponse = await api.createGeneration({
                    projectUuid,
                    recipeSlug: renderSlug,
                    inputs: {
                        ...fillLifestyleRequiredDefaults({
                            ...recipeNode.inputs,
                            selectedGalleryIndex,
                            excludedGalleryIndexes,
                        }, renderSlug === 'lifestyle-scene' ? recipeBySlug.get('lifestyle-scene') : undefined),
                        // Render phase: skip generate+analyze, reuse the
                        // analyzed Space, restrict /display to the picked walls.
                        phase: 'render',
                        existingSpaceId: wpNode.asset.wallPicker.spaceId,
                        selectedWallIds: wpNode.selectedWallIds,
                    },
                    sourceProductUuids: productNode?.products.map((p) => p.uuid) || [],
                    count: 1,
                });

                // Stack each new render output below the prior siblings
                // so successive Continue presses lay out vertically, then drop
                // the batch to a free spot so it never lands on an existing node.
                const renderBase = findFreeSpawn(
                    wpNode.x + WALL_PICKER_NODE_WIDTH + 80,
                    wpNode.y + wpNode.generationNodeIds.length * 260,
                    GENERATION_NODE_WIDTH,
                    260,
                );
                const newGenNodes: CanvasGenerationNode[] = (result.assets || []).map(
                    (asset, i) => ({
                        kind: 'generation' as const,
                        id: `gen-${asset.uuid}`,
                        x: renderBase.x,
                        y: renderBase.y + i * 260,
                        asset,
                        recipeNodeId: wallPickerNodeId,
                    }),
                );

                setNodes((prev) => {
                    const genIds = newGenNodes.map((g) => g.id);
                    return prev
                        .map((n) =>
                            n.id === wallPickerNodeId && n.kind === 'wall-picker'
                                ? {
                                      ...n,
                                      generating: false,
                                      generationNodeIds: [...n.generationNodeIds, ...genIds],
                                  }
                                : n,
                        )
                        .concat(newGenNodes);
                });
                focusNode(renderBase.x, renderBase.y, GENERATION_NODE_WIDTH, 260);
                loadBalance();
            } catch (err) {
                console.error('WallPicker render failed', err);
                setNodes((prev) =>
                    prev.map((n) =>
                        n.id === wallPickerNodeId && n.kind === 'wall-picker'
                            ? { ...n, generating: false }
                            : n,
                    ),
                );
            }
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [nodes, projectUuid],
    );

    // ─── Child affordances (variation / video) ─────────────────────

    const handleMakeVariation = useCallback(
        (parentAsset: CsAsset) => {
            // Create a variation recipe node from the generation node
            const recipe = recipes.find((r) => r.slug === 'style-variation');
            if (!recipe || !projectUuid) return;
            const genNode = nodes.find(
                (n) => n.kind === 'generation' && n.asset.uuid === parentAsset.uuid,
            );
            if (!genNode) return;

            const recipeId = `rec-${uuidv4().slice(0, 8)}`;
            const defaults: Record<string, any> = {};
            for (const input of recipe.inputs || []) {
                if (input.default !== undefined) defaults[input.key] = input.default;
            }
            // Inherit the source asset's aspect ratio so the variation
            // doesn't silently reframe into 16:9 when the user chained off
            // a portrait or square shot.
            const aspectInput = recipe.inputs?.find((i) => i.key === 'aspectRatio');
            const sourceAspect = pickClosestAspect(
                parentAsset.dimensions?.width,
                parentAsset.dimensions?.height,
                aspectInput?.options,
            );
            if (sourceAspect) defaults.aspectRatio = sourceAspect;

            // For variation, we can auto-generate immediately or show recipe node
            // Show recipe node so user can configure strength / prompt
            const varPos = findFreeSpawn(genNode.x + GENERATION_NODE_WIDTH + 80, genNode.y, RECIPE_NODE_WIDTH, 320);
            const newNode: CanvasRecipeNode = {
                kind: 'recipe',
                id: recipeId,
                x: varPos.x,
                y: varPos.y,
                productNodeId: genNode.id, // using gen node as "source"
                recipeSlug: recipe.slug,
                inputs: defaults,
                generationNodeIds: [],
                generating: false,
            };

            pushHistory();
            setNodes((prev) => [...prev, newNode]);
            setSelectedNodeIds(new Set([recipeId]));
            focusNode(varPos.x, varPos.y, RECIPE_NODE_WIDTH, 320);
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [nodes, recipes, projectUuid],
    );

    // Place in a room, off an image: the image is the room; the product is
    // chosen on the card. Chained like Variation, so the image is the parent.
    const handlePlaceProduct = useCallback(
        (roomAsset: CsAsset) => {
            const recipe = recipes.find((r) => r.slug === 'place-in-room');
            if (!recipe || !projectUuid) return;
            const genNode = nodes.find(
                (n) => n.kind === 'generation' && n.asset.uuid === roomAsset.uuid,
            );
            if (!genNode) return;
            const recipeId = `rec-${uuidv4().slice(0, 8)}`;
            const pos = findFreeSpawn(genNode.x + GENERATION_NODE_WIDTH + 80, genNode.y, RECIPE_NODE_WIDTH, 420);
            const newNode: CanvasRecipeNode = {
                kind: 'recipe',
                id: recipeId,
                x: pos.x,
                y: pos.y,
                productNodeId: genNode.id,
                recipeSlug: recipe.slug,
                inputs: { roomAssetUuid: roomAsset.uuid },
                generationNodeIds: [],
                generating: false,
            };
            pushHistory();
            setNodes((prev) => [...prev, newNode]);
            setSelectedNodeIds(new Set([recipeId]));
            focusNode(pos.x, pos.y, RECIPE_NODE_WIDTH, 420);
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [nodes, recipes, projectUuid],
    );

    // An image brought in by the vendor — uploaded or from a link. The API
    // registers it as a ready asset; here it becomes an image node like any
    // generated one (approved, so its "+" options are there at once), placed
    // at the centre of the view.
    const [addImageOpen, setAddImageOpen] = useState(false);
    // Set when the dialog was opened from the right-click menu: the canvas
    // point that was clicked. Used once, then cleared.
    const addImageAtRef = useRef<{ x: number; y: number } | null>(null);
    const handleImportImage = useCallback(
        async (source: { file: File } | { url: string }) => {
            if (!projectUuid) return;
            const asset = await api.importImage(projectUuid, source);
            const rect = canvasElRef.current?.getBoundingClientRect();
            const at = addImageAtRef.current;
            addImageAtRef.current = null;
            const pos = at
                ? findFreeSpawn(at.x, at.y, GENERATION_NODE_WIDTH, 280)
                : findFreeSpawn(
                    (rect ? (rect.width / 2 - panX) / zoom : 0) - GENERATION_NODE_WIDTH / 2,
                    (rect ? (rect.height / 2 - panY) / zoom : 0) - 140,
                    GENERATION_NODE_WIDTH,
                    280,
                );
            const node: CanvasGenerationNode = {
                kind: 'generation',
                id: `gen-${asset.uuid}`,
                x: pos.x,
                y: pos.y,
                asset,
                recipeNodeId: '',
                approved: true,
            };
            pushHistory();
            setNodes((prev) => [...prev, node]);
            setSelectedNodeIds(new Set([node.id]));
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [projectUuid, panX, panY, zoom, nodes],
    );

    // Edit image pill on a generation node → spawn a new edit-image
    // canvas node linked to that source. The node carries its own
    // per-tool settings; the user opens a modal from the node's
    // settings button, saves values, then hits Generate on the node to
    // fire the underlying recipe.
    const handleSpawnEditImage = useCallback(
        (
            parentAsset: CsAsset,
            tool: EditImageTool = 'expand',
            /** Pre-set expansion, when a plan asked for a particular shape. */
            expand?: { extendTop: number; extendRight: number; extendBottom: number; extendLeft: number },
            /** Text a plan asked to lay over the image, composed here. */
            design?: DesignSpec,
            /** Areas a plan asked to fix, already in this image's pixels. */
            retouch?: PlannedRegion[],
            /** What a plan asked the whole picture to become. */
            modifyPrompt?: string,
            /** A picture the plan asked the change to match — ships as image 2. */
            modifyReferenceUrl?: string,
            /** The plan was applied with "generate", so run this node once it is ready. */
            runWhenReady?: boolean,
            /**
             * The id a plan already minted for this node. A continuation waits
             * on it by name, so the canvas must not invent a different one.
             */
            plannedId?: string,
        ) => {
            const genNode = nodes.find(
                (n) => n.kind === 'generation' && n.asset.uuid === parentAsset.uuid,
            );
            if (!genNode) return;
            const editId = plannedId || `edit-${uuidv4().slice(0, 8)}`;
            // A plan minted the id, so a plan created this node — fold it. One
            // spawned from the Edit menu is a node being configured and opens.
            const fromPlan = !!plannedId;
            const editPos = findFreeSpawn(genNode.x + GENERATION_NODE_WIDTH + 80, genNode.y, EDIT_IMAGE_NODE_WIDTH, 210);
            const editNode: CanvasEditImageNode = {
                kind: 'edit-image',
                id: editId,
                x: editPos.x,
                y: editPos.y,
                sourceNodeId: genNode.id,
                sourceAssetUuid: parentAsset.uuid,
                tool,
                collapsed: fromPlan,
                generating: false,
                generationNodeIds: [],
                // No context prompt: the outpainting continues the scene it was
                // given, which is what "same picture, taller" means.
                ...(expand ? { expandInputs: { ...expand, prompt: '' } } : {}),
                // A retouch the plan located and a modify it worded arrive
                // ready to run. They used to arrive empty, with the editor
                // thrown open for the vendor to draw the region and type the
                // instruction — which is the job they had just asked for.
                ...(retouch?.length
                    ? {
                        retouchInputs: {
                            regions: retouch.map((r, i) => ({
                                id: `plan-${i}-${uuidv4().slice(0, 6)}`,
                                // The editor works in polygons; a plan names a
                                // box, so it becomes its four corners.
                                points: [
                                    { x: r.x, y: r.y },
                                    { x: r.x + r.width, y: r.y },
                                    { x: r.x + r.width, y: r.y + r.height },
                                    { x: r.x, y: r.y + r.height },
                                ],
                                x: r.x, y: r.y, width: r.width, height: r.height,
                                prompt: r.prompt,
                            })),
                        },
                    }
                    : {}),
                ...(modifyPrompt
                    ? { modifyInputs: { prompt: modifyPrompt, referenceUrl: modifyReferenceUrl } }
                    : {}),
                // Text the plan asked for, composed against this picture's real
                // size and the vendor's own logo. The editor opens on a draft
                // rather than a blank overlay.
                ...(design && parentAsset.dimensions?.width
                    ? {
                        designInputs: {
                            fabricJson: designSpecToFabric(
                                design,
                                parentAsset.dimensions.width,
                                parentAsset.dimensions.height,
                                vendorLogo,
                                designBrand,
                            ),
                            // Kept so the agent can read back what this design
                            // says, and change it rather than stack another.
                            spec: design,
                        },
                    }
                    : {}),
            };
            pushHistory();
            // A node the plan configured completely runs itself. An expand
            // arrives with its extensions worked out and a design with its copy
            // laid out, so leaving them for the vendor to press Generate on is
            // asking them to finish a job they already asked for — the design
            // landed on the canvas and simply sat there.
            // Upscale counts too: it takes no configuration at all — the scale
            // has a default — so a plan that asks for one has said everything
            // there is to say. Left out, "upscale the best one" produced a node
            // the vendor had to press Generate on themselves.
            const selfRunning = !!expand || !!design || !!retouch?.length || !!modifyPrompt
                || tool === 'upscale';
            // Marked, not fired: the node does not exist yet. An effect picks it
            // up once React has committed it — see the self-running edits effect.
            if (runWhenReady && selfRunning) runWhenReadyRef.current.add(editId);
            setNodes((prev) => [...prev, editNode]);
            setSelectedNodeIds(new Set([editId]));
            focusNode(editPos.x, editPos.y, EDIT_IMAGE_NODE_WIDTH, 210);
            // The tool is now chosen up-front from the Edit menu, so land the
            // vendor straight in that tool's settings (except upscale, which has
            // no settings pane). Mirrors the Design flow's auto-open.
            //
            // A plan that already said what shape it wants has filled those
            // settings in, so opening the editor would interrupt a vendor who
            // asked for exactly this and has nothing left to decide.
            //
            // A design the plan composed is left alone: it flattens itself and
            // is ready to run, so throwing the editor open over the canvas
            // interrupts a vendor with nothing to decide — and invited them to
            // press Save on a scene that was already handled.
            // Nothing is opened over a node the plan finished configuring — the
            // vendor asked for the picture, not the settings pane.
            if (tool === 'design' && !design) setDesignEditNodeId(editId);
            else if (tool !== 'upscale' && tool !== 'design' && !selfRunning) {
                setEditSettingsNodeId(editId);
            }
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [nodes],
    );

    // Design button on a generation node → spawn a new design node
    // linked to that source and auto-open the editor.
    const handleSpawnDesign = useCallback(
        (parentAsset: CsAsset) => {
            const genNode = nodes.find(
                (n) => n.kind === 'generation' && n.asset.uuid === parentAsset.uuid,
            );
            if (!genNode) return;
            const designId = `dsn-${uuidv4().slice(0, 8)}`;
            const designPos = findFreeSpawn(genNode.x + GENERATION_NODE_WIDTH + 80, genNode.y, DESIGN_NODE_WIDTH, 300);
            const designNode: CanvasDesignNode = {
                kind: 'design',
                id: designId,
                x: designPos.x,
                y: designPos.y,
                sourceNodeId: genNode.id,
                sourceAssetUuid: parentAsset.uuid,
                saving: false,
            };
            pushHistory();
            setNodes((prev) => [...prev, designNode]);
            setSelectedNodeIds(new Set([designId]));
            focusNode(designPos.x, designPos.y, DESIGN_NODE_WIDTH, 300);
            // Auto-open the editor so the vendor lands straight in the
            // designer — consistent with the Canva-style "pick → draw"
            // flow; they can come back to the canvas when done.
            setDesignEditorNodeId(designId);
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [nodes],
    );

    // Save from the design editor when it's bound to an EDIT-IMAGE node: upload
    // the rasterised composite and store it (+ the fabric scene) on the node.
    // Generate then registers that PNG as the output asset for free.
    const handleSaveEditDesign = useCallback(
        async (editNodeId: string, result: DesignEditorSaveResult) => {
            try {
                const file = new File([result.pngBlob], `design-${editNodeId}.png`, { type: 'image/png' });
                const upload = await api.uploadStyleRef(file);
                pushHistory();
                setNodes((prev) => prev.map((n) => (
                    n.id === editNodeId && n.kind === 'edit-image'
                        ? {
                            ...n,
                            tool: 'design' as EditImageTool,
                            designInputs: {
                                fabricJson: result.fabricJson,
                                renderedUrl: upload.url,
                                width: result.width,
                                height: result.height,
                            },
                        }
                        : n
                )));
            } catch (err) {
                // eslint-disable-next-line no-console
                console.error('[CS] design save failed', err);
            }
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [],
    );

    // Handle Save from the DesignEditorModal — rasterize the fabric
    // canvas, upload the PNG via the style-ref endpoint (reused for its
    // authenticated blob upload), and store the resulting CDN URL +
    // fabric JSON on the design node so the node thumbnail updates and
    // re-opening restores every layer.
    const handleSaveDesign = useCallback(
        async (designNodeId: string, result: DesignEditorSaveResult) => {
            setNodes((prev) => prev.map((n) =>
                n.id === designNodeId && n.kind === 'design'
                    ? { ...n, saving: true, fabricJson: result.fabricJson }
                    : n,
            ));
            try {
                const file = new File([result.pngBlob], `design-${designNodeId}.png`, { type: 'image/png' });
                const upload = await api.uploadStyleRef(file);
                pushHistory();
                setNodes((prev) => prev.map((n) =>
                    n.id === designNodeId && n.kind === 'design'
                        ? {
                              ...n,
                              saving: false,
                              fabricJson: result.fabricJson,
                              renderedUrl: upload.url,
                              renderedDimensions: { width: result.width, height: result.height },
                          }
                        : n,
                ));
            } catch (err) {
                console.error('design save failed', err);
                setNodes((prev) => prev.map((n) =>
                    n.id === designNodeId && n.kind === 'design'
                        ? { ...n, saving: false }
                        : n,
                ));
            } finally {
                setDesignEditorNodeId(null);
            }
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [],
    );

    // Save settings from the modal back onto the edit-image node.
    // Each save handler also switches the node's active tool — vendors
    // change tools inside the modal (4 tabs on the sidebar) and expect
    // Save to commit whichever tool they configured, not whichever
    // tool the node was on when they opened the modal.
    const handleSaveExpandSettings = useCallback(
        (editNodeId: string, params: ExpandParams) => {
            pushHistory();
            setNodes((prev) => prev.map((n) =>
                n.id === editNodeId && n.kind === 'edit-image'
                    ? { ...n, tool: 'expand', expandInputs: params }
                    : n,
            ));
        },
        [pushHistory],
    );
    const handleSaveRetouchSettings = useCallback(
        (editNodeId: string, params: RetouchParams) => {
            pushHistory();
            setNodes((prev) => prev.map((n) =>
                n.id === editNodeId && n.kind === 'edit-image'
                    ? { ...n, tool: 'retouch', retouchInputs: params }
                    : n,
            ));
        },
        [pushHistory],
    );
    const handleSaveModifySettings = useCallback(
        (editNodeId: string, params: ModifyParams) => {
            pushHistory();
            setNodes((prev) => prev.map((n) =>
                n.id === editNodeId && n.kind === 'edit-image'
                    ? { ...n, tool: 'modify', modifyInputs: params }
                    : n,
            ));
        },
        [pushHistory],
    );
    const handleSaveGraphicsSettings = useCallback(
        (editNodeId: string, params: GraphicsParams) => {
            pushHistory();
            setNodes((prev) => prev.map((n) =>
                n.id === editNodeId && n.kind === 'edit-image'
                    ? { ...n, tool: 'graphics', graphicsInputs: params }
                    : n,
            ));
        },
        [pushHistory],
    );

    // Fire the generation when the user hits Generate on an edit-image
    // node. Dispatches to the right recipe based on the selected tool
    // (Expand today; Inpaint / others later follow the same pattern).
    const handleEditImageGenerate = useCallback(
        async (editNodeId: string) => {
            if (!projectUuid) return;
            // Read through the ref, not this render's `nodes`. A node created
            // and run in the same breath is not in the array this closure
            // captured, and the lookup failing here is silent — which is
            // exactly how a planned expand landed on the canvas and never ran.
            const editNode = nodesRef.current.find(
                (n) => n.id === editNodeId,
            ) as CanvasEditImageNode | undefined;
            if (!editNode || editNode.kind !== 'edit-image') return;

            // Dispatch per-tool → recipe + inputs. Each tool tracks its own
            // settings on the node; bail if the current tool's settings
            // haven't been configured yet (UI already disables Generate
            // in that case, this is defensive).
            let recipeSlug: string;
            let inputs: Record<string, any>;
            if (editNode.tool !== 'design') {
                // One mapping for canvas and Studio: see edit-image-recipes.ts.
                const source = editNode.sourceAssetUuid
                    ? (nodes.find((n) => n.kind === 'generation' && (n as CanvasGenerationNode).asset.uuid === editNode.sourceAssetUuid) as CanvasGenerationNode | undefined)?.asset?.dimensions
                    : undefined;
                const req = editImageRequest(editNode.tool as EditTool, {
                    expand: editNode.expandInputs,
                    retouch: editNode.retouchInputs,
                    modify: editNode.modifyInputs,
                    graphics: editNode.graphicsInputs,
                    upscaleScale: editNode.upscaleScale,
                }, { width: source?.width, height: source?.height });
                if (!req) return;
                recipeSlug = req.recipeSlug;
                inputs = req.inputs;
            } else if (editNode.tool === 'design') {
                const d = editNode.designInputs;
                if (!d?.renderedUrl) return;
                recipeSlug = 'design-render';
                inputs = { renderedUrl: d.renderedUrl, width: d.width || 0, height: d.height || 0 };
            } else {
                // other stubs aren't wired yet.
                return;
            }

            pushHistory();
            setNodes((prev) => prev.map((n) =>
                n.id === editNodeId && n.kind === 'edit-image'
                    ? { ...n, generating: true }
                    : n,
            ));

            try {
                const result: CsGenerationCreateResponse = await api.createGeneration({
                    projectUuid,
                    recipeSlug,
                    inputs,
                    sourceProductUuids: [],
                    parentAssetUuid: editNode.sourceAssetUuid,
                    count: 1,
                });

                const editGenBase = findFreeSpawn(
                    editNode.x + EDIT_IMAGE_NODE_WIDTH + 80,
                    editNode.y,
                    GENERATION_NODE_WIDTH,
                    260,
                );
                const newGenNodes: CanvasGenerationNode[] = (result.assets || []).map(
                    (asset, i) => ({
                        kind: 'generation' as const,
                        id: `gen-${asset.uuid}`,
                        x: editGenBase.x,
                        y: editGenBase.y + i * 260,
                        asset,
                        recipeNodeId: editNodeId, // reuse field so CsConnectionLines draws the edge
                    }),
                );

                setNodes((prev) => {
                    const genIds = newGenNodes.map((g) => g.id);
                    return prev
                        .map((n) =>
                            n.id === editNodeId && n.kind === 'edit-image'
                                ? {
                                      ...n,
                                      generating: false,
                                      generationNodeIds: [
                                          ...n.generationNodeIds,
                                          ...genIds,
                                      ],
                                  }
                                : n,
                        )
                        .concat(newGenNodes);
                });

                focusNode(editGenBase.x, editGenBase.y, GENERATION_NODE_WIDTH, 260);
                loadBalance();
            } catch (err) {
                console.error('Edit-image generation failed', err);
                setNodes((prev) => prev.map((n) =>
                    n.id === editNodeId && n.kind === 'edit-image'
                        ? { ...n, generating: false }
                        : n,
                ));
            }
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [nodes, projectUuid],
    );

    const handleMakeVideo = useCallback(
        (parentAsset: CsAsset) => {
            const recipe = recipes.find((r) => r.slug === 'image-to-video');
            if (!recipe || !projectUuid) return;
            const genNode = nodes.find(
                (n) => n.kind === 'generation' && n.asset.uuid === parentAsset.uuid,
            );
            if (!genNode) return;

            const recipeId = `rec-${uuidv4().slice(0, 8)}`;
            const defaults: Record<string, any> = {};
            for (const input of recipe.inputs || []) {
                if (input.default !== undefined) defaults[input.key] = input.default;
            }
            // Inherit the source image's aspect ratio — a portrait hero
            // shot should become a portrait clip, not forced into 16:9.
            const aspectInput = recipe.inputs?.find((i) => i.key === 'aspectRatio');
            const sourceAspect = pickClosestAspect(
                parentAsset.dimensions?.width,
                parentAsset.dimensions?.height,
                aspectInput?.options,
            );
            if (sourceAspect) defaults.aspectRatio = sourceAspect;

            const vidPos = findFreeSpawn(genNode.x + GENERATION_NODE_WIDTH + 80, genNode.y, RECIPE_NODE_WIDTH, 320);
            const newNode: CanvasRecipeNode = {
                kind: 'recipe',
                id: recipeId,
                x: vidPos.x,
                y: vidPos.y,
                productNodeId: genNode.id,
                recipeSlug: recipe.slug,
                inputs: defaults,
                generationNodeIds: [],
                generating: false,
            };

            pushHistory();
            setNodes((prev) => [...prev, newNode]);
            setSelectedNodeIds(new Set([recipeId]));
            focusNode(vidPos.x, vidPos.y, RECIPE_NODE_WIDTH, 320);
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [nodes, recipes, projectUuid],
    );

    // ─── Multi-asset actions (fire from the select-mode action bar) ────
    //
    // Both entry points take the ordered list of currently-checked image
    // asset UUIDs and route to an existing flow, using the first asset
    // as the primary source (its on-canvas position anchors the new
    // node). Additional assets become references — the exact plumbing
    // depends on recipe support:
    //   - Create image → opens the Design editor on the first asset as
    //     background. Extra assets are added as image layers side-by-
    //     side so vendors land on a ready-to-arrange composition.
    //   - Create video → chains the image-to-video recipe off the first
    //     asset. Extra UUIDs are currently passed through to the recipe
    //     node's inputs as a 'referenceAssetUuids' hint so future recipe
    //     versions that accept references can consume them without a
    //     separate entry point.
    const resolveSelectedAssets = useCallback((): { node: CanvasGenerationNode; asset: CsAsset }[] => {
        const out: { node: CanvasGenerationNode; asset: CsAsset }[] = [];
        for (const uuid of Array.from(selectedAssetUuids)) {
            const n = nodes.find((x) => x.kind === 'generation' && (x as CanvasGenerationNode).asset.uuid === uuid) as CanvasGenerationNode | undefined;
            if (n) out.push({ node: n, asset: n.asset });
        }
        return out;
    }, [nodes, selectedAssetUuids]);

    const handleCreateImageFromSelection = useCallback(() => {
        const resolved = resolveSelectedAssets();
        if (resolved.length === 0) return;
        const [primary, ...rest] = resolved;
        // Spawn a Design node anchored off the first selected image.
        const designId = `dsn-${uuidv4().slice(0, 8)}`;
        const ciPos = findFreeSpawn(primary.node.x + GENERATION_NODE_WIDTH + 80, primary.node.y, DESIGN_NODE_WIDTH, 300);
        const designNode: CanvasDesignNode = {
            kind: 'design',
            id: designId,
            x: ciPos.x,
            y: ciPos.y,
            sourceNodeId: primary.node.id,
            sourceAssetUuid: primary.asset.uuid,
            saving: false,
            // Extra selected images drive both (a) canvas-level input
            // edges so the lineage shows the full N → 1 fan-in, and
            // (b) an "import these on open" hint for the design editor
            // so the vendor doesn't re-upload each one.
            ...(rest.length > 0 && {
                additionalSourceNodeIds: rest.map((r) => r.node.id),
                initialImageLayerUrls: rest.map((r) => r.asset.url).filter(Boolean) as string[],
            }),
        } as any;
        pushHistory();
        setNodes((prev) => [...prev, designNode]);
        setSelectedNodeIds(new Set([designId]));
        focusNode(ciPos.x, ciPos.y, DESIGN_NODE_WIDTH, 300);
        // Don't auto-open the editor — mirrors the Create video flow,
        // which drops a recipe node on the canvas without forcing a
        // modal. The vendor sees the lineage land first and opens the
        // editor when ready.
        exitSelectMode();
    }, [resolveSelectedAssets, pushHistory, exitSelectMode]);

    const handleCreateVideoFromSelection = useCallback(() => {
        const resolved = resolveSelectedAssets();
        if (resolved.length === 0) return;
        const recipe = recipes.find((r) => r.slug === 'image-to-video');
        if (!recipe || !projectUuid) return;
        const [primary, ...rest] = resolved;
        const recipeId = `rec-${uuidv4().slice(0, 8)}`;
        const defaults: Record<string, any> = {};
        for (const input of recipe.inputs || []) {
            if (input.default !== undefined) defaults[input.key] = input.default;
        }
        const aspectInput = recipe.inputs?.find((i) => i.key === 'aspectRatio');
        const sourceAspect = pickClosestAspect(
            primary.asset.dimensions?.width,
            primary.asset.dimensions?.height,
            aspectInput?.options,
        );
        if (sourceAspect) defaults.aspectRatio = sourceAspect;
        // Pass extra selected assets as reference hints. Recipes that
        // don't read this key just ignore it — safe to include blindly.
        if (rest.length > 0) {
            defaults.referenceAssetUuids = rest.map((r) => r.asset.uuid);
        }
        const cvPos = findFreeSpawn(primary.node.x + GENERATION_NODE_WIDTH + 80, primary.node.y, RECIPE_NODE_WIDTH, 320);
        const newNode: CanvasRecipeNode = {
            kind: 'recipe',
            id: recipeId,
            x: cvPos.x,
            y: cvPos.y,
            productNodeId: primary.node.id,
            recipeSlug: recipe.slug,
            inputs: defaults,
            generationNodeIds: [],
            generating: false,
            ...(rest.length > 0 && { additionalSourceNodeIds: rest.map((r) => r.node.id) }),
        };
        pushHistory();
        setNodes((prev) => [...prev, newNode]);
        setSelectedNodeIds(new Set([recipeId]));
        focusNode(cvPos.x, cvPos.y, RECIPE_NODE_WIDTH, 320);
        exitSelectMode();
    }, [resolveSelectedAssets, recipes, projectUuid, pushHistory, exitSelectMode]);

    // ─── Video editor (timeline) — lives in video-editor/use-video-edit-canvas ──
    const videoEdit = useVideoEditCanvas({
        projectUuid,
        nodesRef,
        setNodes,
        pushHistory,
        findFreeSpawn,
        focusNode,
        select: (ids) => setSelectedNodeIds(new Set(ids)),
        viewCenter: () => {
            const rect = canvasElRef.current?.getBoundingClientRect();
            return { x: rect ? (rect.width / 2 - panX) / zoom : 0, y: rect ? (rect.height / 2 - panY) / zoom : 0 };
        },
    });
    const handleEditInTimelineFromSelection = useCallback(() => {
        const gens = resolveSelectedAssets().map((r) => r.node);
        if (!gens.length) return;
        void videoEdit.createFromGenerations(gens);
        exitSelectMode();
    }, [resolveSelectedAssets, videoEdit, exitSelectMode]);
    /** Ready generation nodes in the current selection, in click order. */
    const selectedGenerationNodes = (): CanvasGenerationNode[] => Array.from(selectedNodeIds)
        .map((id) => nodes.find((n) => n.id === id && n.kind === 'generation') as CanvasGenerationNode | undefined)
        .filter((n): n is CanvasGenerationNode => !!n && n.asset?.status === 'ready');
    /** Products on the selected product nodes, for "Make a video story". */
    const selectedProductUuids = (): string[] => Array.from(new Set(Array.from(selectedNodeIds)
        .map((id) => nodes.find((n) => n.id === id && n.kind === 'product') as CanvasProductNode | undefined)
        .flatMap((n) => (n?.products || []).map((p) => p.uuid))
        .filter(Boolean)));
    const openVideoEditorFromSelection = (anchor?: { x: number; y: number }) => {
        const gens = selectedGenerationNodes();
        if (gens.length) void videoEdit.createFromGenerations(gens);
        else videoEdit.createNode(createEmptyComposition('9:16'), { anchor, open: true });
    };

    // ─── Planner ───────────────────────────────────────────────────
    // Resolve the uuids an agent plan names into canvas-ready products. The
    // planner only ever returns this vendor's uuids (the API re-checks
    // ownership), so a miss here means the product was deleted mid-plan.
    const productsApi = useProductsApi();
    const [mentionableProducts, setMentionableProducts] = useState<PlannerProduct[]>([]);
    // Open by default — the agent is the front door of the workspace, and
    // closing it leaves the compact composer, never nothing.
    /**
     * Open only where there is a conversation to come back to.
     *
     * A new project — or one nobody has briefed an agent in — opens as the bar
     * along the bottom instead: the first move on an empty canvas is adding a
     * product, not talking about one. Read synchronously so a project that does
     * have history opens straight into it rather than flashing the bar first.
     */
    const [agentPanelOpen, setAgentPanelOpen] = useState(() => hasAgentHistory(projectUuid || ''));
    /** The panel's live width — the vendor can drag it wider. */
    const [agentPanelWidth, setAgentPanelWidth] = useState(AGENT_PANEL_WIDTH);
    /**
     * Where the canvas's own chrome sits from the right edge.
     *
     * One number for the top toolbar and the zoom controls, so they line up
     * with each other and keep the same gap from the agent panel. Measured
     * separately they drifted: the toolbar ended exactly on the panel's edge
     * with nothing between them, while the zoom controls sat a good deal
     * further away than they needed to.
     */
    const canvasChromeRight = agentPanelOpen && !selectMode
        ? agentPanelWidth + AGENT_PANEL_MARGIN + CANVAS_CHROME_GAP
        : 12;

    // Catalog for @-mentions in the prompt bar. Loaded once — the picker panel
    // already pulls the same list, so this is warm by the time it is needed.
    useEffect(() => {
        let cancelled = false;
        (async () => {
            try {
                const all = await productsApi.findAllProducts();
                if (cancelled) return;
                setMentionableProducts((all || []).map((p: any) => toCanvasProduct(p) as PlannerProduct));
            } catch {
                // Mentions degrade to typing a name, which still works.
            }
        })();
        return () => { cancelled = true; };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);
    const loadPlannerProducts = useCallback(async (uuids: string[]): Promise<PlannerProduct[]> => {
        if (uuids.length === 0) return [];
        const wanted = new Set(uuids);
        const { products } = await productsApi.findProductsPage({
            uuids: Array.from(wanted), page: 1, limit: wanted.size, draft: 'all',
        });
        return (products || [])
            .filter((p: any) => wanted.has(p?.uuid))
            .map((p: any) => toCanvasProduct(p) as PlannerProduct);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Place in a room is offered on an image only when the vendor has a piece
    // to place: surface-only catalogues have nothing that stands on a floor.
    const furnitureProducts = useMemo(
        () => mentionableProducts.filter((p) => p.visualizationType === 'model3d' && !!p.modelPath),
        [mentionableProducts],
    );
    const hasFurniture = furnitureProducts.length > 0;
    const roomApi = useMemo(() => ({
        analyze: api.analyzeRoom,
        status: api.getRoomAnalysis,
        scene: api.getRoomScene,
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }), []);

    // Recipe nodes the planner can see and re-configure. Rebuilt whenever the
    // graph changes so a plan can never target something already deleted.
    const existingRecipeNodes = useMemo(() => {
        const map = new Map<string, ExistingNode>();
        for (const n of nodes) {
            if (n.kind !== 'recipe') continue;
            const rn = n as CanvasRecipeNode;
            const parent = nodes.find((p) => p.id === rn.productNodeId);
            const product = parent?.kind === 'product'
                ? (parent as CanvasProductNode).products?.[0]
                : undefined;
            // A node's stored inputs are not its effective state. `mode` is
            // resolved at render and usually never stored, so a node sitting in
            // Prompt mode still carries a `roomType` that it ignores — and the
            // agent, seeing only the stored inputs, edited exactly that. Resolve
            // the node the same way the card does before describing it.
            const recipe = recipes.find((r) => r.slug === rn.recipeSlug);
            const inputs = rn.inputs || {};
            let mode: string | undefined;
            let ignoredInputs: string[] = [];
            let missing: string[] = [];

            if (recipe) {
                const resolved = resolveRecipeSchema({
                    recipe,
                    inputs,
                    productVisualizationType: product?.visualizationType as any,
                    productApplicationTypes: product?.applicationTypes,
                });
                mode = resolved.flags.modeValue;
                const visible = new Set(resolved.inputs.map((i) => i.key));
                ignoredInputs = Object.keys(inputs).filter(
                    (key) => !visible.has(key) && !CONTROL_INPUT_KEYS.has(key),
                );
                missing = resolved.missing;
            }

            map.set(rn.id, {
                nodeId: rn.id,
                recipeSlug: rn.recipeSlug,
                recipeName: recipe?.name,
                inputs,
                // Which mode the node is actually in, and which of its stored
                // values that mode ignores.
                mode,
                ignoredInputs,
                // Required fields still empty — a node in this state cannot run,
                // which is usually the thing to fix rather than adding another.
                missing,
                // Whether it has already produced something — a node that has
                // run is one the vendor is looking at, and "make it blue" almost
                // always means that one.
                hasOutput: rn.generationNodeIds.length > 0,
                // The frame the 3D studio captured for this node, if it has one.
                // Lifted out of `inputs` because it is the answer to "did the
                // shot I asked for actually get taken" — the question the next
                // turn of a run needs before it can carry on.
                snapshot: inputs.snapshotUrl ? {
                    url: inputs.snapshotUrl,
                    // The angle this frame was posed to — absent when the vendor
                    // framed it themselves. Deliberately not `cameraAngle`,
                    // which is the last angle *requested* and says nothing about
                    // the picture that is actually there.
                    angle: inputs.capturedAngle || undefined,
                    view: inputs.cameraView,
                } : undefined,
                product: product as PlannerProduct | undefined,
            });
        }
        return map;
    }, [nodes, recipes]);

    /**
     * The rest of the graph, as the agent needs to see it.
     *
     * Recipe nodes alone were not enough: asked to accept two generations the
     * agent proposed generating three more, because a generation was not
     * something it could name. Anything the vendor can point at on the canvas
     * has to be nameable here too.
     */
    const agentGenerations = useMemo<CanvasGenerationInfo[]>(() => nodes
        .filter((n) => n.kind === 'generation')
        .map((n) => {
            const gen = n as CanvasGenerationNode;
            const parent = nodes.find((p) => p.id === gen.recipeNodeId);
            const recipeSlug = parent?.kind === 'recipe'
                ? (parent as CanvasRecipeNode).recipeSlug : undefined;
            const productNode = parent?.kind === 'recipe'
                ? nodes.find((p) => p.id === (parent as CanvasRecipeNode).productNodeId)
                : undefined;
            const productName = productNode?.kind === 'product'
                ? (productNode as CanvasProductNode).products?.[0]?.name
                : undefined;
            const recipeName = recipes.find((r) => r.slug === recipeSlug)?.name
                || (parent?.kind === 'edit-image'
                    ? `${(parent as CanvasEditImageNode).tool} edit`
                    : recipeSlug);

            return {
                nodeId: gen.id,
                assetUuid: gen.asset?.uuid,
                status: gen.asset?.status,
                approved: gen.approved,
                fromNodeId: gen.recipeNodeId,
                label: [recipeName, productName].filter(Boolean).join(' · ') || 'Generation',
                // What actually came back. Knowing an image exists is not the
                // same as being able to point at it: an edit chained onto a
                // result, or a second pass that works from the first, needs the
                // asset itself, and a failure needs its reason before a retry
                // can be more than a guess.
                url: gen.asset?.url,
                thumbnailUrl: gen.asset?.thumbnailUrl,
                error: gen.asset?.error,
                createdAt: gen.asset?.createdAt,
            };
        })
        // Oldest first, then numbered — "the last one" and "the previous image"
        // are how a vendor refers to their work, and an unordered listing left
        // the agent guessing which was which.
        .sort((a, b) => String(a.createdAt || '').localeCompare(String(b.createdAt || '')))
        .map((gen, i) => ({ ...gen, order: i + 1 })), [nodes, recipes]);

    const agentEdits = useMemo<CanvasEditInfo[]>(() => nodes
        .filter((n) => n.kind === 'edit-image')
        .map((n) => {
            const edit = n as CanvasEditImageNode;
            return {
                nodeId: edit.id,
                tool: edit.tool,
                sourceNodeId: edit.sourceNodeId,
                hasOutput: edit.generationNodeIds.length > 0,
                // What this design says, so a change to the words is a change to
                // this node rather than another design laid over its output.
                design: edit.designInputs?.spec,
            };
        }), [nodes]);

    // Nodes queued to fire after an "Add & generate". Deferred through state
    // rather than called inline because handleGenerate reads `nodes` from its
    // closure — calling it in the same tick as setNodes would not see them.
    const [pendingGenerate, setPendingGenerate] = useState<string[]>([]);

    /**
     * A request handed from a recipe card to the chat by "Ask AI".
     *
     * Carried as state rather than called straight through, because the panel
     * may be closed: opening it and writing the request are two things, and
     * the vendor should end up looking at the question before it is sent.
     */
    const [agentHandoff, setAgentHandoff] = useState<{ id: string; text: string; referenceUrl?: string; target?: AgentTarget } | null>(null);

    /**
     * Point the chat at a node, without writing the vendor a message.
     *
     * The existing "Ask AI" pill hands over a composed request; this only says
     * WHICH thing the next message is about, so "fill in the prompt" and "make
     * a caption from this" both work without them having to describe which
     * card they mean.
     */
    const targetInChat = useCallback((target: AgentTarget) => {
        setAgentHandoff({ id: `target-${target.nodeId}-${Date.now()}`, text: '', target });
        setAgentPanelOpen(true);
    }, []);


    /**
     * A brief sent here by an insight on the dashboard.
     *
     * Reuses the same handoff the canvas cards use for "Use AI": the sentence
     * is typed into the composer and focused, and the vendor sends it or edits
     * it first. Deliberately NOT auto-sent — a finding is a reason to work, and
     * spending a vendor's credits because they clicked a card on the dashboard
     * would be deciding for them.
     *
     * Taken rather than read, so returning to this project tomorrow does not
     * refill the box with last week's suggestion. Opens the panel too: the
     * brief is invisible inside a collapsed composer.
     */
    useEffect(() => {
        const brief = takeBrief();
        if (!brief) return;

        setAgentHandoff({
            id: `brief-${brief.at}`,
            text: brief.text,
            // The photo the vendor sent the assistant, so the planner starts
            // from the same picture rather than asking for it again.
            referenceUrl: brief.referenceUrl,
        });
        setAgentPanelOpen(true);
    }, []);

    /** A measured camera as JSON, whether the plan wrote an object or a string. */
    const cameraViewJson = (value: unknown): string | undefined => {
        if (!value) return undefined;
        if (typeof value === 'string') return value.trim() || undefined;
        try {
            return JSON.stringify(value);
        } catch {
            return undefined;
        }
    };

    /**
     * Node waiting for the 3D studio to pose itself and capture a frame.
     *
     * One at a time: each capture mounts a WebGL scene and renders a model, and
     * running several at once would fight over the GPU for no benefit. The node
     * id maps to the angle the plan asked for; clearing it ends the request.
     */
    const [pendingCapture, setPendingCapture] = useState<
        { nodeId: string; angle: string; cameraView?: string } | null
    >(null);

    /**
     * Captures still to take, after the one in flight.
     *
     * There is one 3D studio and it poses one node at a time, so a plan asking
     * for three scenes has to walk them. Held in a ref rather than state: it is
     * read and advanced inside the capture callback, and a stale closure over it
     * would strand the rest of the queue.
     */
    const captureQueueRef = useRef<Array<{
        nodeId: string; angle: string; cameraView?: string; thenGenerate: boolean;
    }>>([]);

    /**
     * Hand the studio the next node waiting for a frame.
     *
     * Called from every path that ends a capture — success, failure, and the
     * watchdog — because a queue that only advances on success stops dead on the
     * first node that cannot pose, and the vendor is left with the same
     * "needs a detail" cards this was meant to clear.
     */
    const startNextCapture = useCallback(() => {
        const next = captureQueueRef.current.shift();
        if (!next) return;
        generateAfterCaptureRef.current = next.thenGenerate ? next.nodeId : null;
        // A tick later, so the studio sees a closed request before the next one
        // opens it again.
        window.setTimeout(() => setPendingCapture(next), 0);
    }, []);


    /**
     * A capture that never reported back must not hold the studio open.
     *
     * The dialog opens for a headless capture and closes when the request
     * clears, so a request that never clears is a viewer with no way out —
     * which is exactly what a persistent value used as a "capture now" signal
     * produced. The widget reports every exit it knows about; this covers the
     * ones it does not, at a deadline comfortably past its own 30s.
     */
    useEffect(() => {
        if (!pendingCapture) return undefined;
        const timer = window.setTimeout(() => {
            // eslint-disable-next-line no-console
            console.warn('[studio] headless capture never reported — releasing the viewer');
            generateAfterCaptureRef.current = null;
            setPendingCapture(null);
            // One node that cannot pose must not strand the ones behind it.
            startNextCapture();
        }, 45_000);
        return () => window.clearTimeout(timer);
    }, [pendingCapture, startNextCapture]);

    /**
     * A node whose generation is held back until its new frame exists.
     *
     * "Generate it from the side" is two things happening at once — a capture
     * and a job — and the job reads the frame. Firing both together produced an
     * image from the previous angle, then the capture replaced the snapshot
     * behind it, so the card ended up showing the new framing next to a picture
     * of the old one. A ref rather than state: the capture's completion handler
     * would otherwise read whatever was current when it was last rendered.
     */
    const generateAfterCaptureRef = useRef<string | null>(null);
    /**
     * What the last applied plan undertook to run.
     *
     * The subject of the canvas invariant: everything in here has to start.
     * A tick alongside it because a ref changing cannot wake an effect, and the
     * check has to re-arm each time a plan lands.
     */
    const promisedRef = useRef<Set<string>>(new Set());
    const [promisedTick, setPromisedTick] = useState(0);

    /**
     * A way into the headless capture that isn't a model call.
     *
     * The capture is the one part of this that unit tests cannot reach — jsdom
     * has no WebGL, so the angle maths is testable and the frame it produces is
     * not, and the bugs have all been in the frame. Reaching it through the
     * agent means a real turn per attempt, which is slow enough that it stopped
     * being checked. This lets `e2e/studio-capture.spec.ts` drive the real
     * studio directly. Development builds only — it never reaches a vendor.
     */
    useEffect(() => {
        if (process.env.NODE_ENV === 'production') return undefined;
        (window as any).__csCapture = (nodeId: string, angle: string) => setPendingCapture({ nodeId, angle });
        // Run one named node. Clicking "Generate" by looking for the button on
        // screen hits whichever node happens to be under it, which spent
        // credits on the wrong one more than once. Recipe and edit nodes run
        // through different paths, so route on what the node actually is.
        // Opens the design editor on a node, so a test can see the composition
        // a vendor would see — the stored scene and the rendered picture drifting
        // apart is a bug you can only catch by looking at both.
        (window as any).__csOpenDesign = (nodeId: string) => setDesignEditNodeId(nodeId);
        (window as any).__csGenerate = (nodeId: string) => {
            const node = nodesRef.current.find((n: any) => n.id === nodeId);
            if (node?.kind === 'edit-image') void handleEditImageGenerate(nodeId);
            else setPendingGenerate([nodeId]);
        };
        (window as any).__csNodes = () => nodesRef.current.map((n: any) => ({
            id: n.id,
            kind: n.kind,
            // Which recipe, so a test can tell the two surface cards apart —
            // Single Piece and Room Scene share a slug and differ by an input.
            recipeSlug: n.recipeSlug,
            inputs: n.inputs,
            // Generation state, so a test can wait for images to land rather
            // than guess at a duration and screenshot a half-drawn canvas.
            status: n.kind === 'generation' ? n.asset?.status : undefined,
            url: n.kind === 'generation' ? n.asset?.url : undefined,
            dimensions: n.kind === 'generation' ? n.asset?.dimensions : undefined,
            generating: n.generating || undefined,
            tool: n.tool,
            // Whether a design has been flattened yet — it cannot run until it has.
            designReady: n.kind === 'edit-image' ? !!n.designInputs?.renderedUrl : undefined,
            renderedUrl: n.kind === 'video-edit' ? n.renderedUrl : undefined,
            collapsed: n.collapsed,
        }));
        // What the last applied plan undertook to run, and whether any of it
        // has gone quiet. Read by the e2e suite after an apply, so every future
        // way of going silent is caught by the check that already exists rather
        // than by a vendor noticing an hour later.
        (window as any).__csIdle = () => findSilentlyIdle(nodesRef.current, promisedRef.current);
        // The timeline editor, for automated screenshots: open a node's editor,
        // drop a node holding a given composition (returns its id), export one
        // (resolves to the saved asset's url).
        (window as any).__csOpenVideoEditor = (nodeId: string) => videoEdit.openEditor(nodeId);
        (window as any).__csAddVideoEdit = (composition: any) => videoEdit.createNode(composition, {});
        (window as any).__csExportVideo = (nodeId: string) => videoEdit.exportNode(nodeId);
        // The whole Video Story, headless: resolves with the video-edit node id
        // once the storyboard is written and its nodes are on the canvas.
        (window as any).__csVideoStory = (req: any) => videoStory.runStory(req);
        (window as any).__csVideoStoryState = (nodeId: string) => videoStory.stateFor(nodeId);
        // Re-cut a story's timeline from whatever has landed (replaces the composition).
        (window as any).__csVideoStoryAssemble = (nodeId: string) => videoStory.assemble(nodeId);
        return () => {
            delete (window as any).__csCapture;
            delete (window as any).__csGenerate;
            delete (window as any).__csOpenDesign;
            delete (window as any).__csNodes;
            delete (window as any).__csIdle;
            delete (window as any).__csOpenVideoEditor;
            delete (window as any).__csAddVideoEdit;
            delete (window as any).__csExportVideo;
            delete (window as any).__csVideoStory;
            delete (window as any).__csVideoStoryState;
            delete (window as any).__csVideoStoryAssemble;
        };
    });

    /**
     * The invariant, watched in development.
     *
     * Everything a plan promised to run must start. Left to be noticed by hand
     * this has cost days: a blank capture, a design that never rendered, an
     * expand that never fired — each looked like a working canvas and each was
     * found only when the vendor asked where their picture was.
     *
     * A grace period, because "not started yet" and "never going to start" look
     * identical for the first moment after an apply.
     */
    useEffect(() => {
        if (process.env.NODE_ENV === 'production') return undefined;
        if (promisedRef.current.size === 0) return undefined;

        const timer = window.setTimeout(() => {
            const idle = findSilentlyIdle(nodesRef.current, promisedRef.current);
            if (idle.length === 0) return;
            // eslint-disable-next-line no-console
            console.error(
                `[canvas] ${idle.length} node(s) the plan promised have gone silent:\n`
                + describeIdle(idle),
            );
        }, IDLE_GRACE_MS);
        return () => window.clearTimeout(timer);
    }, [nodes, promisedTick]);

    /**
     * Flatten a plan-authored design so the node can actually run.
     *
     * A Design node needs `renderedUrl` before it generates, and that only ever
     * came from a vendor opening the editor and pressing Save. So a plan could
     * lay the headline out exactly right and the node would sit there doing
     * nothing at all — no image, no error, nothing to see. Rasterise it here,
     * the same way Save does, and it behaves like every other node.
     */
    // The vendor's own mark, for designs that ask for it.
    const { theme: vendorTheme } = useSelector((state: VendorState) => state.settings);
    const vendorLogo = vendorTheme?.logo;

    /**
     * The brand kit's typeface and first colour, for designs a plan composes.
     * Loaded once per workspace; the kit changes rarely and never mid-turn.
     */
    const [designBrand, setDesignBrand] = useState<DesignBrandDefaults | undefined>(undefined);
    useEffect(() => {
        let cancelled = false;
        api.getBrandKit()
            .then((kit) => {
                if (cancelled) return;
                setDesignBrand({
                    fontFamily: kit?.primaryFont || undefined,
                    textColor: kit?.primaryColors?.[0] || undefined,
                    // Template designs use the whole kit: colours, roles, name, logo.
                    colors: [...(kit?.primaryColors || []), ...(kit?.secondaryColors || [])],
                    colorRoles: kit?.colorRoles,
                    brandName: kit?.brandName || undefined,
                    logoUrl: kit?.logoUrl || undefined,
                });
            })
            .catch(() => { /* no kit is the empty-account path; defaults apply */ });
        return () => { cancelled = true; };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    /** Edit nodes a plan configured and asked to run, once they are ready. */
    const runWhenReadyRef = useRef<Set<string>>(new Set());

    // Release chained steps once their parent recipe has a ready picture; give
    // up on them if every parent picture failed and nothing is running.
    useEffect(() => {
        if (waitForParentRef.current.size === 0) return;
        for (const id of Array.from(waitForParentRef.current)) {
            const rn = nodes.find((n) => n.id === id) as CanvasRecipeNode | undefined;
            const parent = rn ? nodes.find((n) => n.id === rn.productNodeId) : undefined;
            if (!rn || parent?.kind !== 'recipe') { waitForParentRef.current.delete(id); continue; }
            const gens = nodes.filter((n) => n.kind === 'generation'
                && (n as CanvasGenerationNode).recipeNodeId === parent.id) as CanvasGenerationNode[];
            if (gens.some((g) => g.asset?.status === 'ready')) {
                waitForParentRef.current.delete(id);
                if (!rn.generating) setPendingGenerate((prev) => (prev.includes(id) ? prev : [...prev, id]));
            } else if (gens.length && gens.every((g) => g.asset?.status === 'failed') && !(parent as CanvasRecipeNode).generating) {
                waitForParentRef.current.delete(id);
            }
        }
    }, [nodes]);
    const renderingDesignRef = useRef<Set<string>>(new Set());
    useEffect(() => {
        const pending = nodes.filter((n) => n.kind === 'edit-image'
            && (n as CanvasEditImageNode).tool === 'design'
            && (n as CanvasEditImageNode).designInputs?.fabricJson
            && !(n as CanvasEditImageNode).designInputs?.renderedUrl
            && !renderingDesignRef.current.has(n.id)) as CanvasEditImageNode[];
        if (pending.length === 0) return;

        for (const node of pending) {
            const source = nodes.find((n) => n.kind === 'generation'
                && (n as CanvasGenerationNode).asset?.uuid === node.sourceAssetUuid) as
                CanvasGenerationNode | undefined;
            const url = source?.asset?.url;
            const dims = source?.asset?.dimensions;
            if (!url || !dims?.width) {
                /**
                 * Not ready YET — which is the normal case, not a failure.
                 *
                 * A plan that asks for a post with a headline chains the design
                 * onto a generation that has not finished rendering. Marking the
                 * node as in-flight before this check meant it was filtered out
                 * of every later pass: the picture arrived seconds later, the
                 * effect re-ran, and this node was permanently excluded. It never
                 * flattened, so it never got `renderedUrl`, so the run-when-ready
                 * effect never fired it — the Design node sat on the canvas
                 * looking correct and producing nothing.
                 *
                 * So the claim is staked below, once there is something to
                 * render onto.
                 */
                continue;
            }

            // From here it really is in flight, and a second pass must not
            // rasterise the same node twice.
            renderingDesignRef.current.add(node.id);
            void (async () => {
                try {
                    const { blob, scene } = await renderDesignScene(
                        url, node.designInputs!.fabricJson, dims.width, dims.height,
                    );
                    const file = new File([blob], `design-${node.id}.png`, { type: 'image/png' });
                    const upload = await api.uploadStyleRef(file);
                    setNodes((prev) => prev.map((n) => (
                        n.id === node.id && n.kind === 'edit-image'
                            ? {
                                ...n,
                                designInputs: {
                                    ...(n as CanvasEditImageNode).designInputs,
                                    // The scene as drawn, so opening the editor
                                    // shows the picture that was made.
                                    fabricJson: scene,
                                    renderedUrl: upload.url,
                                    // A template design can have its own shape (a 9:16 story from a 4:5 picture).
                                    width: scene?.artboardWidth || dims.width,
                                    height: scene?.artboardHeight || dims.height,
                                },
                            } as CanvasEditImageNode
                            : n
                    )));
                    // Flattened, so it can finally run — but only once the
                    // renderedUrl above is committed, or the job reads a node
                    // with no frame and bails. The self-running edits effect
                    // sees that commit; scheduling it here could not.
                } catch (err: any) {
                    // Loudly: a design that silently never rendered is what this
                    // whole path is here to stop happening again.
                    // eslint-disable-next-line no-console
                    console.error(`[design] could not flatten ${node.id}: ${err?.message || err}`);
                    renderingDesignRef.current.delete(node.id);
                }
            })();
        }
    }, [nodes, api]);

    /**
     * Run the edit nodes a plan fully configured, once they are really there.
     *
     * These used to be fired from the spawn itself, on a `setTimeout(0)` — but
     * the callback that timer held was captured from the render *before* the
     * node existed, so the generate handler looked the node up, found nothing
     * and returned without a word. A planned expand therefore never ran: the
     * node appeared, correctly configured, and simply sat there. An effect runs
     * after the commit, so the node it is asked to generate is on the canvas.
     */
    useEffect(() => {
        if (runWhenReadyRef.current.size === 0) return;
        for (const node of nodes) {
            if (node.kind !== 'edit-image') continue;
            if (!runWhenReadyRef.current.has(node.id)) continue;
            const edit = node as CanvasEditImageNode;
            if (edit.generating) continue;
            // A design is not ready until its scene has been flattened to an
            // image; everything else is ready as soon as it exists.
            if (edit.designInputs && !edit.designInputs.renderedUrl) continue;
            runWhenReadyRef.current.delete(node.id);
            void handleEditImageGenerate(node.id);
        }
    }, [nodes, handleEditImageGenerate]);

    // Drop an approved plan onto the canvas. The compiler lays the block out
    // relative to its own origin; here it is shifted into free space so it
    // never lands on existing work, and the camera moves to meet it.
    /** Set below, once the Video Story hook exists: runs a story a plan asked for. */
    const runVideoStoryRef = useRef<((story: CompiledVideoStory, generate: boolean) => void) | null>(null);

    const handlePlanApply = useCallback((compiled: CompiledPlan, summary: string, generate: boolean) => {
        const patches = compiled.modifications;
        const removedIds = new Set(compiled.deletions.flatMap((d) => d.removedIds));
        // A generate-only plan changes no node but is still work to do, so it
        // must not be turned away here.
        //
        // Asked through isEmptyPlan rather than restated: this guard was a
        // hand-kept copy of that list and drifted from it, so a plan whose only
        // work was changing an existing design returned here without doing
        // anything — while the conversation said "Applied to canvas". Two
        // definitions of "nothing to do" is one too many.
        if (isEmptyPlan(compiled) && compiled.nodes.length === 0) return;

        // One history entry for the whole plan, so a single undo puts the canvas
        // back exactly as it was — destructive plans especially.
        pushHistory();

        if (compiled.nodes.length > 0) {
            const extent = compiledPlanExtent(compiled);
            const anchorX = (-panX + 120) / zoom;
            const anchorY = (-panY + 120) / zoom;
            const spot = findFreeSpawn(anchorX, anchorY, extent.width, extent.height);
            offsetCompiledPlan(compiled, spot.x, spot.y);
            focusNode(spot.x, spot.y, extent.width, extent.height);
        }

        setNodes((prev) => {
            // Removals first — clearing wins over deleting, since it subsumes it.
            const kept = compiled.clearsCanvas
                ? []
                : removedIds.size === 0
                ? prev
                : prev.filter((n) => !removedIds.has(n.id));

            // Then edits, so a modified node keeps its place in the list.
            const patched = patches.length === 0 ? kept : kept.map((n) => {
                const patch = patches.find((m) => m.targetNodeId === n.id);
                if (!patch || n.kind !== 'recipe') return n;
                return { ...n, inputs: { ...(n as CanvasRecipeNode).inputs, ...patch.patch } };
            });

            return [...patched, ...compiled.nodes];
        });

        setSelectedNodeIds(new Set([
            ...compiled.steps.map((s) => s.node.id),
            ...patches.map((m) => m.targetNodeId),
        ]));

        /**
         * Every node that names a camera angle gets the frame captured for it.
         *
         * `.find` — one node — used to be enough, because a plan that posed the
         * studio posed it once. A brief handed to the planner routinely asks for
         * three scenes, and all three arrive with the studio switched on: the
         * first was captured and the other two sat on the canvas reading "Needs
         * a detail before it can run", waiting for a frame nobody was going to
         * take. They are captured one after another, because there is one studio
         * and it can only pose one node at a time.
         */
        const wantsCapture = [
            ...compiled.steps.map((st) => ({
                nodeId: st.node.id,
                recipeSlug: st.recipe.slug,
                inputs: st.node.inputs as Record<string, any>,
            })),
            // Patches arm a capture only when the PATCH ITSELF moves the
            // camera. Merged with the node's previous inputs, any update — a
            // reworded scene, a lighting change — found the angle a plan set
            // weeks ago and re-posed the studio, replacing a frame the vendor
            // had captured by hand with a shot nobody asked for.
            ...patches
                .filter((m) => {
                    const patch = m.patch as Record<string, any>;
                    return patch?.cameraAngle || patch?.cameraView;
                })
                .map((m) => ({
                    nodeId: m.targetNodeId,
                    recipeSlug: (nodesRef.current.find((n) => n.id === m.targetNodeId) as any)
                        ?.recipeSlug || '',
                    inputs: { ...m.previous, ...m.patch },
                })),
            // A measured camera arms a capture exactly like a named angle —
            // it IS the angle, in numbers rather than a word.
        ].filter((n) => (n.inputs?.cameraAngle || n.inputs?.cameraView)
            && (n.inputs.use3dCamera === true || n.inputs.use3dCamera === 'true')
            // ...and only where a studio will actually open. From Reference on
            // its keep-the-photo or same-room route poses nothing, so its
            // studio never mounts — and a plan that set `use3dCamera` anyway
            // left the job waiting for a frame from a viewer that would never
            // appear. The node sat there having generated nothing, which is a
            // miserable thing to look at because everything else about it
            // looks right.
            // ...and only where a studio will actually open. From Reference on
            // its keep-the-photo or same-room route poses nothing, so its
            // studio never mounts — and a plan that set `use3dCamera` anyway
            // left the job waiting for a frame from a viewer that would never
            // appear.
            //
            // The node's OWN slug, not a hardcoded one. Written as
            // `recipeSlug: 'from-reference'` this asked "would a from-reference
            // node with these inputs keep its photograph?" of every node in the
            // plan — and since that function reads `matchStrength || 'exact'`,
            // anything without one answered yes. Every Room Scene and every
            // Lifestyle Scene was quietly excluded from the headless capture,
            // so a plan that named an angle produced a node that never posed.
            && !keepsThePhotograph({
                recipeSlug: n.recipeSlug,
                visualizationType: 'surface',
                inputs: n.inputs,
            })
            && !(n.recipeSlug === 'from-reference'
                && String(n.inputs.matchStrength || '') === 'inspired')
            // A detail shot is made from the product's own photograph; its
            // studio never opens, so waiting on a capture would idle it forever.
            && !(String(n.inputs.shot || '') === 'detail' && n.inputs.detailCamera !== 'studio'));

        const capturing = new Set(wantsCapture.map((n) => n.nodeId));

        if (generate) {
            // New nodes with nothing missing, plus every node the plan
            // re-configured — changing a setting and not re-running it leaves
            // the vendor looking at the old image, which reads as a no-op.
            //
            // Edited nodes are filtered the same way as new ones. They were not,
            // so a change that made a node *unrunnable* still fired a job:
            // switching the 3D studio on makes `snapshotUrl` required, and that
            // frame only exists once the vendor has posed the camera and
            // captured it. The canvas disables Generate in that state; queueing
            // one from a plan went around the same rule.
            //
            // Shared with the agent panel, which waits on exactly this list to
            // decide a plan's continuation is due.
            const firing = nodesToGenerate(compiled);
            // Everything this plan said it would produce an image from — the
            // fire-list plus the nodes that start themselves once they exist.
            // Watched from here on; see the canvas invariant.
            promisedRef.current = new Set(nodesAwaited(compiled));
            setPromisedTick((t) => t + 1);

            // The node being re-framed waits for its new frame.
            //
            // Asked for the same shot from a different angle, this fired the job
            // the instant the plan applied — while the studio was still posing —
            // so the image came back from the *old* frame. The studio then
            // finished and swapped the snapshot underneath it, which is the
            // worst version of the bug: the canvas shows the new angle and the
            // picture beside it is the previous one.
            // Anything waiting on a frame runs when that frame lands, not now.
            // A step chained to another step of this plan waits for that step's
            // picture (see waitForParentRef) instead of firing with no source.
            const stepIds = new Set(compiled.steps.map((st) => st.node.id));
            const chained = new Set(compiled.steps
                .filter((st) => stepIds.has(st.node.productNodeId))
                .map((st) => st.node.id));
            chained.forEach((id) => waitForParentRef.current.add(id));
            setPendingGenerate(firing.filter((id) => !capturing.has(id) && !chained.has(id)));
        }

        if (wantsCapture.length) {
            captureQueueRef.current = wantsCapture.slice(1).map((n) => ({
                nodeId: n.nodeId,
                angle: String(n.inputs.cameraAngle || ''),
                cameraView: cameraViewJson(n.inputs.cameraView),
                thenGenerate: generate,
            }));

            const first = wantsCapture[0];
            generateAfterCaptureRef.current = generate ? first.nodeId : null;
            setPendingCapture({
                nodeId: first.nodeId,
                angle: String(first.inputs.cameraAngle || ''),
                // Carried here rather than read from the node, because THIS is
                // the transient "capture now" signal. Read from the node's own
                // inputs it would never stop being true, and the studio dialog
                // — which opens for a headless capture — could never close.
                // A plan writes cameraView as a JSON OBJECT, not a string.
                // String() on it yields "[object Object]" — truthy enough to
                // open the studio, impossible to parse once there, so the
                // viewer opened and nothing ever closed it.
                cameraView: cameraViewJson(first.inputs.cameraView),
            });
        }

        // Changes to an editing node that already exists. Clearing renderedUrl
        // is what re-runs it: the flatten effect picks up any design without
        // one, recomposes it against the same picture, and — because the plan
        // asked for it — generates again.
        if (compiled.editUpdates.length > 0) {
            for (const update of compiled.editUpdates) {
                if (generate) runWhenReadyRef.current.add(update.editNodeId);
                renderingDesignRef.current.delete(update.editNodeId);
            }
            setNodes((prev) => prev.map((n) => {
                const update = compiled.editUpdates.find((u) => u.editNodeId === n.id);
                if (!update || n.kind !== 'edit-image') return n;
                const edit = n as CanvasEditImageNode;
                const source = prev.find((s) => s.kind === 'generation'
                    && (s as CanvasGenerationNode).asset?.uuid === edit.sourceAssetUuid) as
                    CanvasGenerationNode | undefined;
                // The source picture's size, or the size this node was last
                // composed at. Without the fallback an update to a node whose
                // source has since been tidied away changed nothing at all and
                // said nothing about it.
                const dims = source?.asset?.dimensions
                    || (edit.designInputs?.width
                        ? { width: edit.designInputs.width, height: edit.designInputs.height || 0 }
                        : undefined);

                if (!dims?.width) {
                    // eslint-disable-next-line no-console
                    console.error(
                        `[planner] cannot update ${edit.id}: no size for its source image`,
                    );
                    return edit;
                }

                if (update.design) {
                    return {
                        ...edit,
                        designInputs: {
                            fabricJson: designSpecToFabric(
                                update.design, dims.width, dims.height, vendorLogo, designBrand,
                            ),
                            spec: update.design,
                            renderedUrl: undefined,
                        },
                    } as CanvasEditImageNode;
                }
                if (update.aspectRatio) {
                    const expand = expansionForAspect(dims, update.aspectRatio);
                    return expand
                        ? { ...edit, expandInputs: { ...expand, prompt: '' } } as CanvasEditImageNode
                        : edit;
                }
                if (update.retouch?.length) {
                    return {
                        ...edit,
                        retouchInputs: {
                            regions: update.retouch.map((r, i) => ({
                                id: `plan-${i}-${uuidv4().slice(0, 6)}`,
                                points: [
                                    { x: r.x, y: r.y },
                                    { x: r.x + r.width, y: r.y },
                                    { x: r.x + r.width, y: r.y + r.height },
                                    { x: r.x, y: r.y + r.height },
                                ],
                                x: r.x, y: r.y, width: r.width, height: r.height,
                                prompt: r.prompt,
                            })),
                        },
                    } as CanvasEditImageNode;
                }
                if (update.modifyPrompt || update.modifyReferenceUrl) {
                    return {
                        ...edit,
                        modifyInputs: {
                            ...edit.modifyInputs,
                            prompt: update.modifyPrompt || edit.modifyInputs?.prompt || '',
                            ...(update.modifyReferenceUrl
                                ? { referenceUrl: update.modifyReferenceUrl }
                                : {}),
                        },
                    } as CanvasEditImageNode;
                }
                return edit;
            }));
        }

        // Product substitutions. The product lives on the source node, so the
        // recipes hanging off it all follow — which is what "replace the
        // product in this node" means on a canvas shaped like this.
        if (compiled.productSwaps.length > 0) {
            setNodes((prev) => prev.map((n) => {
                const swap = compiled.productSwaps.find((sw) => sw.productNodeId === n.id);
                if (!swap || n.kind !== 'product') return n;
                return { ...n, products: [toCanvasProduct(swap.product as any)] } as CanvasNode;
            }));
        }

        // Things done to generated images. Routed to the same handlers the
        // canvas buttons use, so the agent and the vendor cannot drift apart —
        // a retry deletes the failed asset and re-runs in place rather than
        // appending a second image beside the broken one.
        for (const action of compiled.assetActions) {
            // A retry re-runs a job and spends credits, so it only fires on the
            // generate button. Accepting or rejecting costs nothing and applies
            // either way.
            if (action.kind === 'retry' && !generate) continue;

            const genNode = nodesRef.current.find(
                (n) => n.id === action.generationNodeId && n.kind === 'generation',
            ) as CanvasGenerationNode | undefined;
            if (!genNode) continue;

            if (action.kind === 'retry') {
                void handleRerun(genNode.asset);
            } else if (action.kind === 'approve') {
                setNodes((prev) => prev.map((n) => (
                    n.id === genNode.id && n.kind === 'generation' ? { ...n, approved: true } : n
                )));
            } else if (action.kind === 'reject') {
                tombstonesRef.current.add(genNode.asset.uuid);
                removeNode(genNode.id);
            } else if (action.kind === 'edit' && action.tool) {
                handleSpawnEditImage(
                    genNode.asset, action.tool as EditImageTool, action.expand, action.design,
                    action.retouch, action.modifyPrompt, action.modifyReferenceUrl,
                    generate, action.editNodeId,
                );
            }
        }

        // A video story writes its storyboard first and then lays its own nodes
        // out through this same function (see use-video-story).
        for (const story of compiled.videoStories || []) runVideoStoryRef.current?.(story, generate);

        // eslint-disable-next-line no-console
        console.info(
            `[planner] +${compiled.steps.length} added, ~${patches.length} edited, `
            + `-${compiled.clearsCanvas ? compiled.clearedCount : removedIds.size} removed, `
            + `${compiled.assetActions.length} on images — ${summary}`,
        );
    }, [findFreeSpawn, focusNode, handleRerun, handleSpawnEditImage, panX, panY,
        pushHistory, removeNode, zoom]);

    // Fire queued generations once the nodes are in state. Staggered so a
    // twelve-step plan doesn't open twelve simultaneous jobs.
    useEffect(() => {
        if (pendingGenerate.length === 0) return;
        const ids = pendingGenerate;
        setPendingGenerate([]);
        ids.forEach((id, i) => {
            window.setTimeout(() => { void handleGenerate(id); }, i * 450);
        });
    }, [pendingGenerate, handleGenerate]);

    // ─── Video Story — lives in video-editor/use-video-story ──────────
    const videoStory = useVideoStory({
        projectUuid,
        nodes,
        nodesRef,
        setNodes,
        recipes,
        existingNodes: existingRecipeNodes,
        loadProducts: loadPlannerProducts,
        mentionableProducts,
        applyPlan: handlePlanApply,
        createVideoEditNode: videoEdit.createNode,
    });
    runVideoStoryRef.current = videoStory.runPlanned;

    // ─── Canvas callbacks ──────────────────────────────────────────

    const handlePanChange = useCallback((x: number, y: number) => {
        setPanX(x);
        setPanY(y);
    }, []);

    const handleRecenter = useCallback(
        (vw: number, vh: number) => {
            if (!vw || !vh || nodes.length === 0) {
                setPanX(100);
                setPanY(100);
                setZoom(DEFAULT_ZOOM);
                return;
            }
            const padding = 60;
            // Filter out nodes with invalid coordinates
            const valid = nodes.filter((n) => Number.isFinite(n.x) && Number.isFinite(n.y));
            if (valid.length === 0) {
                setPanX(100);
                setPanY(100);
                setZoom(DEFAULT_ZOOM);
                return;
            }
            const minX = Math.min(...valid.map((n) => n.x));
            const minY = Math.min(...valid.map((n) => n.y));
            const maxX = Math.max(...valid.map((n) => {
                const w = n.kind === 'recipe' ? RECIPE_NODE_WIDTH
                    : n.kind === 'generation' ? GENERATION_NODE_WIDTH
                    : PRODUCT_NODE_WIDTH;
                return n.x + w;
            }));
            const maxY = Math.max(...valid.map((n) => n.y + 300));
            const graphW = Math.max(1, maxX - minX + padding * 2);
            const graphH = Math.max(1, maxY - minY + padding * 2);
            const z = Math.max(0.4, Math.min(DEFAULT_ZOOM, vw / graphW, vh / graphH));
            if (!Number.isFinite(z)) { setZoom(DEFAULT_ZOOM); setPanX(100); setPanY(100); return; }
            setZoom(z);
            setPanX((vw - graphW * z) / 2 - minX * z + padding * z);
            setPanY((vh - graphH * z) / 2 - minY * z + padding * z);
        },
        [nodes],
    );

    // ─── Edges ─────────────────────────────────────────────────────

    const edges: CsEdge[] = useMemo(() => {
        const result: CsEdge[] = [];
        for (const n of nodes) {
            if (n.kind === 'recipe') {
                const rn = n as CanvasRecipeNode;
                // Product → Recipe
                if (nodes.find((pn) => pn.id === rn.productNodeId)) {
                    result.push({
                        fromId: rn.productNodeId,
                        toId: rn.id,
                        state: rn.generating ? 'active' : 'completed',
                    });
                }
                // Extra selected assets (multi-select "Create video")
                // → Recipe. Skip if the source was removed between
                // selection and now.
                for (const extraId of rn.additionalSourceNodeIds || []) {
                    if (!nodes.find((p) => p.id === extraId)) continue;
                    result.push({
                        fromId: extraId,
                        toId: rn.id,
                        state: rn.generating ? 'active' : 'completed',
                    });
                }
                // Recipe → Generation(s) (or → WallPicker for the
                // lifestyle-scene+walls flow). The recipe's
                // generationNodeIds bag holds either kind; the edge
                // state mirrors whatever the downstream node is doing.
                for (const genId of rn.generationNodeIds) {
                    const downstream = nodes.find((d) => d.id === genId);
                    let state: 'idle' | 'active' | 'completed' | 'failed' = 'completed';
                    if (downstream?.kind === 'generation') {
                        const gn = downstream as CanvasGenerationNode;
                        state = gn.asset.status === 'pending'
                            ? 'active'
                            : gn.asset.status === 'failed'
                            ? 'failed'
                            : 'completed';
                    } else if (downstream?.kind === 'wall-picker') {
                        const wp = downstream as CanvasWallPickerNode;
                        state = wp.asset.status === 'pending'
                            ? 'active'
                            : wp.asset.status === 'failed'
                            ? 'failed'
                            : 'completed';
                    }
                    result.push({ fromId: rn.id, toId: genId, state });
                }
            }
            if (n.kind === 'wall-picker') {
                const wp = n as CanvasWallPickerNode;
                // WallPicker → Generation(s) — each Continue press spawns
                // a fresh sibling render. The picker stays put so the
                // vendor can revisit, change selection, and produce more
                // siblings for comparison.
                for (const genId of wp.generationNodeIds) {
                    const genNode = nodes.find(
                        (gn) => gn.id === genId && gn.kind === 'generation',
                    ) as CanvasGenerationNode | undefined;
                    const state =
                        genNode?.asset.status === 'pending'
                            ? 'active'
                            : genNode?.asset.status === 'failed'
                            ? 'failed'
                            : 'completed';
                    result.push({ fromId: wp.id, toId: genId, state });
                }
            }
            if (n.kind === 'edit-image') {
                const en = n as CanvasEditImageNode;
                // Source generation → edit-image node
                if (nodes.find((p) => p.id === en.sourceNodeId)) {
                    result.push({
                        fromId: en.sourceNodeId,
                        toId: en.id,
                        state: en.generating ? 'active' : 'completed',
                    });
                }
                // Edit-image node → generation(s) it produced
                for (const genId of en.generationNodeIds) {
                    const genNode = nodes.find(
                        (gn) => gn.id === genId && gn.kind === 'generation',
                    ) as CanvasGenerationNode | undefined;
                    const state =
                        genNode?.asset.status === 'pending'
                            ? 'active'
                            : genNode?.asset.status === 'failed'
                            ? 'failed'
                            : 'completed';
                    result.push({ fromId: en.id, toId: genId, state });
                }
            }
            if (n.kind === 'design') {
                const dn = n as CanvasDesignNode;
                // Source generation → design node
                if (nodes.find((p) => p.id === dn.sourceNodeId)) {
                    result.push({
                        fromId: dn.sourceNodeId,
                        toId: dn.id,
                        state: dn.saving ? 'active' : 'completed',
                    });
                }
                // Extra selected images (multi-select "Create image")
                // → Design. Each adds an input edge so the lineage on
                // the canvas reads "N images → this design".
                for (const extraId of dn.additionalSourceNodeIds || []) {
                    if (!nodes.find((p) => p.id === extraId)) continue;
                    result.push({
                        fromId: extraId,
                        toId: dn.id,
                        state: dn.saving ? 'active' : 'completed',
                    });
                }
            }
        }
        return result;
    }, [nodes]);

    const recipeBySlug = useMemo(
        () => new Map(recipes.map((r) => [r.slug, r])),
        [recipes],
    );

    const nodePositions: CsNodePosition[] = useMemo(
        () =>
            nodes.map((n) => {
                const measured = nodeHeights.get(n.id);
                const fallbackHeight = n.kind === 'recipe'
                    ? (() => {
                        const r = recipeBySlug.get((n as CanvasRecipeNode).recipeSlug);
                        const inputCount = r?.inputs?.length || 4;
                        return 55 + inputCount * 36 + 65; // header + collapsed rows + generate button
                    })()
                    : n.kind === 'generation'
                    ? ((n as CanvasGenerationNode).approved === false || !(n as CanvasGenerationNode).approved) && (n as CanvasGenerationNode).asset?.status === 'ready'
                        ? 260 // image + review buttons
                        : 220
                    : n.kind === 'edit-image'
                    ? 210
                    : n.kind === 'design'
                    ? 300 // thumbnail + footer button
                    : n.kind === 'wall-picker'
                    ? 320 // image preview + footer continue button
                    : n.kind === 'video-edit'
                    ? 360
                    : 200;
                return {
                    id: n.id,
                    x: n.x,
                    y: n.y,
                    width:
                        n.kind === 'product'
                            ? PRODUCT_NODE_WIDTH
                            : n.kind === 'recipe'
                            ? RECIPE_NODE_WIDTH
                            : n.kind === 'edit-image'
                            ? EDIT_IMAGE_NODE_WIDTH
                            : n.kind === 'design'
                            ? DESIGN_NODE_WIDTH
                            : n.kind === 'wall-picker'
                            ? WALL_PICKER_NODE_WIDTH
                            : n.kind === 'video-edit'
                            ? VIDEO_EDIT_NODE_WIDTH
                            : GENERATION_NODE_WIDTH,
                    height: measured ?? fallbackHeight,
                };
            }),
        [nodes, recipeBySlug, nodeHeights],
    );

    // Observe every [data-cs-node-id] element's live DOM height and mirror
    // it into `nodeHeights` so connection-line endpoints stay pinned at
    // the node's true vertical center as its content grows / shrinks.
    useEffect(() => {
        const nodeIds = nodes.map((n) => n.id);
        const elements = new Map<string, Element>();
        for (const id of nodeIds) {
            const el = document.querySelector(`[data-cs-node-id="${CSS.escape(id)}"]`);
            if (el) elements.set(id, el);
        }
        if (elements.size === 0) return;

        const pending = new Map<string, number>();
        let raf = 0;
        const flush = () => {
            raf = 0;
            if (pending.size === 0) return;
            setNodeHeights((prev) => {
                let changed = false;
                const next = new Map(prev);
                for (const [id, h] of pending) {
                    if (next.get(id) !== h) {
                        next.set(id, h);
                        changed = true;
                    }
                }
                pending.clear();
                return changed ? next : prev;
            });
        };

        const observer = new ResizeObserver((entries) => {
            for (const entry of entries) {
                const id = (entry.target as HTMLElement).getAttribute('data-cs-node-id');
                if (!id) continue;
                // Round to avoid sub-pixel-oscillation re-renders.
                pending.set(id, Math.round(entry.contentRect.height));
            }
            if (!raf) raf = requestAnimationFrame(flush);
        });

        for (const el of elements.values()) observer.observe(el);
        return () => {
            observer.disconnect();
            if (raf) cancelAnimationFrame(raf);
        };
    }, [nodes]);

    // Drop height entries for nodes that no longer exist — avoids a
    // memory leak as nodes are added / removed over the session.
    useEffect(() => {
        setNodeHeights((prev) => {
            if (prev.size === 0) return prev;
            const live = new Set(nodes.map((n) => n.id));
            let changed = false;
            const next = new Map(prev);
            for (const id of next.keys()) {
                if (!live.has(id)) { next.delete(id); changed = true; }
            }
            return changed ? next : prev;
        });
    }, [nodes]);

    // ─── Render ────────────────────────────────────────────────────

    if (loading) {
        return (
            <div className="fixed inset-0 z-40 flex items-center justify-center bg-background">
                <Loader2 className="size-7 animate-spin text-muted-foreground" />
            </div>
        );
    }

    if (!project) {
        return (
            <div className="fixed inset-0 z-40 flex flex-col items-center justify-center gap-4 bg-background text-sm text-muted-foreground">
                {t('CONTENT_STUDIO.PROJECT_NOT_FOUND', 'Project not found')}
                <Button variant="outline" size="lg" onClick={() => navigate('/marketing/content-studio')}>
                    {t('CONTENT_STUDIO.BACK_TO_PROJECTS', 'Back to projects')}
                </Button>
            </div>
        );
    }

    const hasNodes = nodes.length > 0;

    return (
        // Full-bleed workspace: the canvas owns the viewport and every control
        // floats above it. The app chrome (sidebar, top nav) stays mounted
        // underneath — this sits over it, so Back is just leaving the overlay.
        // z-40: above the chrome (z-20 at most), below the z-50 that every
        // dialog, popover and select portals to <body> at. The old MUI value,
        // 1300, hid all of those behind the canvas — the 3D Studio among them.
        <div className="fixed inset-0 z-40 overflow-hidden bg-background">
            {/* ── Canvas ───────────────────────────────────────────── */}
            <div className="absolute inset-0">
                <CsCanvas
                    // Keeps zoom and recentre reachable while the agent panel
                    // is open, rather than hidden underneath it.
                    rightOffset={canvasChromeRight}
                    zoom={zoom}
                    panX={panX}
                    panY={panY}
                    onZoomChange={setZoom}
                    onPanChange={handlePanChange}
                    onRecenter={handleRecenter}
                    canvasRef={canvasElRef}
                    onContextMenu={(x, y) => setContextMenu({ x, y })}
                    onEmptyClick={() => setSelectedNodeIds(new Set())}
                    onRubberBandSelect={(box, additive) => {
                        // Discover every node's viewport rect by DOM
                        // query and keep the ones whose center falls
                        // inside the rubber-band box. Robust to the
                        // workspace's pan/zoom transform because
                        // getBoundingClientRect returns post-transform
                        // coords in viewport space — matching the
                        // box coords we receive.
                        const els = document.querySelectorAll<HTMLElement>('[data-cs-node-id]');
                        const hits = new Set<string>();
                        els.forEach((el) => {
                            const id = el.getAttribute('data-cs-node-id');
                            if (!id) return;
                            const r = el.getBoundingClientRect();
                            const cx = r.left + r.width / 2;
                            const cy = r.top + r.height / 2;
                            if (cx >= box.left && cx <= box.right
                                && cy >= box.top && cy <= box.bottom) {
                                hits.add(id);
                            }
                        });
                        setSelectedNodeIds((prev) => {
                            if (!additive) return hits;
                            const merged = new Set(prev);
                            hits.forEach((id) => merged.add(id));
                            return merged;
                        });
                    }}
                    isEmpty={!hasNodes}
                    emptyState={
                        <div className="pointer-events-auto max-w-100 text-center">
                            <p className="mb-2 font-heading text-xl font-medium tracking-tight text-foreground">
                                {t('CONTENT_STUDIO.EMPTY_CANVAS_TITLE', 'Start with products')}
                            </p>
                            <p className="mb-6 text-sm leading-relaxed text-muted-foreground">
                                {t(
                                    'CONTENT_STUDIO.EMPTY_CANVAS_HINT',
                                    'Pick products from your catalog, configure a recipe, and hit Generate. The AI creates on-brand images for your ads, social, and catalog.',
                                )}
                            </p>
                            <Button variant="outline" size="lg" onClick={() => setPickerOpen(true)}>
                                <Plus />
                                {t('CONTENT_STUDIO.PICK_PRODUCTS', 'Pick products')}
                            </Button>
                        </div>
                    }
                    backgroundLayer={
                        <div
                            className={cn(
                                'size-full transition-opacity duration-200 ease-out',
                                selectMode ? 'opacity-15' : 'opacity-100',
                            )}
                        >
                            <CsConnectionLines edges={[...edges, ...videoEdit.edgesFor(nodes)]} nodes={nodePositions} />
                        </div>
                    }
                >
                    <AnimatePresence>
                        {nodes.map((node) => {
                            // In select mode, dim every node that isn't
                            // a ready image generation. Image-video
                            // generations are also dimmed — the current
                            // flow composes still images only.
                            const isImageGen =
                                node.kind === 'generation'
                                && (node as CanvasGenerationNode).asset.kind !== 'video'
                                && (node as CanvasGenerationNode).asset.status === 'ready';
                            const dimmed = selectMode && !isImageGen;
                            if (node.kind === 'product') {
                                const pn = node as CanvasProductNode;
                                return (
                                    <ProductNode
                                        key={pn.id}
                                        nodeId={pn.id}
                                        x={pn.x}
                                        y={pn.y}
                                        products={pn.products}
                                        selected={selectedNodeIds.has(pn.id)}
                                        zoom={zoom}
                                        dimmed={dimmed}
                                        addOpen={recipeSelector?.productNodeId === pn.id}
                                        onDrag={(dx, dy) => moveNode(pn.id, dx, dy)}
                                        onClick={(mods) => handleNodeClick(pn.id, mods)}
                                        onAddRecipe={() => handleOpenRecipeSelector(pn.id)}
                                        onReplace={() => {
                                            setReplacingProductNodeId(pn.id);
                                            setPickerOpen(true);
                                        }}
                                        onDuplicate={() => handleDuplicateProduct(pn.id)}
                                        onRemove={() => removeNode(pn.id)}
                                        onSelectGalleryImage={(productUuid, index) => {
                                            setNodes((prev) => prev.map((n) => {
                                                if (n.id !== pn.id || n.kind !== 'product') return n;
                                                return {
                                                    ...n,
                                                    products: n.products.map((p) =>
                                                        p.uuid === productUuid
                                                            ? { ...p, selectedGalleryIndex: index }
                                                            : p,
                                                    ),
                                                };
                                            }));
                                        }}
                                        onToggleGalleryExclusion={(productUuid, index) => {
                                            setNodes((prev) => prev.map((n) => {
                                                if (n.id !== pn.id || n.kind !== 'product') return n;
                                                return {
                                                    ...n,
                                                    products: n.products.map((p) => {
                                                        if (p.uuid !== productUuid) return p;
                                                        const current = p.excludedGalleryIndexes || [];
                                                        const nextExcluded = current.includes(index)
                                                            ? current.filter((i) => i !== index)
                                                            : [...current, index];
                                                        // If the user just excluded the currently-
                                                        // selected gallery index, snap the selection
                                                        // back to the default thumbnail.
                                                        const stillSelected =
                                                            p.selectedGalleryIndex === index && nextExcluded.includes(index)
                                                                ? -1
                                                                : p.selectedGalleryIndex;
                                                        return {
                                                            ...p,
                                                            excludedGalleryIndexes: nextExcluded,
                                                            selectedGalleryIndex: stillSelected,
                                                        };
                                                    }),
                                                };
                                            }));
                                        }}
                                    />
                                );
                            }
                            if (node.kind === 'recipe') {
                                const rn = node as CanvasRecipeNode;
                                const recipe = recipeBySlug.get(rn.recipeSlug);
                                if (!recipe) return null;
                                // Resolve the linked product node so RecipeNode
                                // can filter Surface options by applicationTypes.
                                const linkedProductNode = nodes.find(
                                    (n) => n.id === rn.productNodeId && n.kind === 'product',
                                ) as CanvasProductNode | undefined;
                                // Variation recipes chain off a generation
                                // node — the change-angle widget needs that
                                // parent asset's URL to display the image
                                // being reframed.
                                const linkedGenNode = nodes.find(
                                    (n) => n.id === rn.productNodeId && n.kind === 'generation',
                                ) as CanvasGenerationNode | undefined;
                                // Walk up to the original product node so
                                // variation / chain recipes can still reach
                                // application types, viz-type, and the 3D
                                // model for the change-angle widget.
                                const upstreamProductNode = linkedProductNode || (() => {
                                    if (!linkedGenNode) return undefined;
                                    const parentRecipe = nodes.find(
                                        (n) => n.id === linkedGenNode.recipeNodeId && n.kind === 'recipe',
                                    ) as CanvasRecipeNode | undefined;
                                    if (!parentRecipe) return undefined;
                                    return nodes.find(
                                        (n) => n.id === parentRecipe.productNodeId && n.kind === 'product',
                                    ) as CanvasProductNode | undefined;
                                })();
                                const productApplicationTypes =
                                    upstreamProductNode?.products?.[0]?.applicationTypes;
                                const productVisualizationType =
                                    upstreamProductNode?.products?.[0]?.visualizationType;
                                const productModelPath =
                                    upstreamProductNode?.products?.[0]?.modelPath;
                                const productUuid =
                                    upstreamProductNode?.products?.[0]?.uuid;
                                const productTexturePath =
                                    upstreamProductNode?.products?.[0]?.texturePath;
                                const productSurfaceTexture =
                                    upstreamProductNode?.products?.[0]?.surfaceTexturePath
                                    // Fall back to the product's thumbnail (for surface
                                    // products this is the material image) so nodes added
                                    // before the material path was plumbed still work.
                                    || upstreamProductNode?.products?.[0]?.thumbnailPath;
                                // Flat floor coverings (rug/carpet/mat…) get laid
                                // flat in the 3D Studio so captures read flat.
                                // Keyed off the category slug ('rugs') first — the
                                // product name (e.g. 'Orenth') rarely says 'rug'.
                                // `s?` matches plural slugs like 'rugs'/'carpets'.
                                const isRug = /\b(rug|carpet|mat|runner|doormat|kilim|dhurrie)s?\b/i.test(
                                    `${upstreamProductNode?.products?.[0]?.category || ''} ${upstreamProductNode?.products?.[0]?.name || ''}`,
                                );
                                const sourceImageUrl = linkedGenNode?.asset?.url;
                                return (
                                    <RecipeNode
                                        key={rn.id}
                                        nodeId={rn.id}
                                        x={rn.x}
                                        y={rn.y}
                                        recipe={recipe}
                                        lifestyleSceneRecipe={recipeBySlug.get('lifestyle-scene')}
                                        inputs={rn.inputs}
                                        onInputChange={(k, v) => handleRecipeInputChange(rn.id, k, v)}
                                        selected={selectedNodeIds.has(rn.id)}
                                        zoom={zoom}
                                        dimmed={dimmed}
                                        collapsed={rn.collapsed}
                                        onToggleCollapse={() => setNodes((prev) => prev.map((n) => (
                                            n.id === rn.id && n.kind === 'recipe'
                                                ? { ...n, collapsed: !(n as CanvasRecipeNode).collapsed }
                                                : n
                                        )))}
                                        onDrag={(dx, dy) => moveNode(rn.id, dx, dy)}
                                        onClick={(mods) => handleNodeClick(rn.id, mods)}
                                        onGenerate={() => handleGenerate(rn.id)}
                                        onDuplicate={() => handleDuplicateRecipe(rn.id)}
                                        onTargetInChat={() => targetInChat({
                                            nodeId: rn.id,
                                            kind: 'recipe',
                                            label: recipe?.name || rn.recipeSlug,
                                        })}
                                        onRemove={() => removeNode(rn.id)}
                                        generating={rn.generating}
                                        creditBalance={balance}
                                        productApplicationTypes={productApplicationTypes}
                                        productVisualizationType={productVisualizationType}
                                        productModelPath={productModelPath}
                                        placeRoomUrl={rn.recipeSlug === 'place-in-room' ? sourceImageUrl : undefined}
                                        placeProducts={rn.recipeSlug === 'place-in-room' ? furnitureProducts : undefined}
                                        roomApi={rn.recipeSlug === 'place-in-room' ? roomApi : undefined}
                                        onRemoveBackground={api.cutoutImage}
                                        onAddBoardProducts={() => {
                                            setAppendingProductNodeId(rn.productNodeId);
                                            setPickerOpen(true);
                                        }}
                                        // The pieces a moodboard is arranged from.
                                        // No `|| []` here on purpose: an empty array is
                                        // truthy, so a fallback built inline would defeat
                                        // the memo downstream and hand the composer a new
                                        // identity every render. Undefined is stable.
                                        boardProducts={
                                            linkedProductNode?.products
                                            || upstreamProductNode?.products
                                        }
                                        headlessCaptureAngle={
                                            // Empty means the plan measured a camera rather than
                                            // naming one; null keeps the studio shut entirely.
                                            pendingCapture?.nodeId === rn.id
                                                ? (pendingCapture.angle || null)
                                                : null
                                        }
                                        headlessCaptureCameraView={
                                            pendingCapture?.nodeId === rn.id
                                                ? (pendingCapture.cameraView || null)
                                                : null
                                        }
                                        onMatchReferenceCamera={async (imageUrl) => {
                                            // Measure, remember, then pose. The
                                            // measurement is stored on the node as
                                            // well as handed to the studio, so a
                                            // later Re-frame still knows what the
                                            // reference asked for.
                                            const res = await api.measureReferenceCamera(imageUrl);
                                            if (!res?.measured || !res.cameraView) {
                                                return 'The camera could not be read from this picture. Frame the shot by hand in the studio.';
                                            }
                                            handleRecipeInputChange(rn.id, 'use3dCamera', true);
                                            handleRecipeInputChange(rn.id, 'cameraView', res.cameraView);
                                            // Empty angle: measured, not named — the studio poses to the
                                            // measurement rather than the nearest bucket.
                                            setPendingCapture({ nodeId: rn.id, angle: '', cameraView: res.cameraView });
                                            return null;
                                        }}
                                        onAskAgent={(request) => {
                                            setAgentPanelOpen(true);
                                            setAgentHandoff({ id: `${rn.id}-${Date.now()}`, text: request });
                                        }}
                                        onHeadlessCaptureDone={(err) => {
                                            const held = generateAfterCaptureRef.current;
                                            generateAfterCaptureRef.current = null;
                                            setPendingCapture(null);
                                            if (err) {
                                                // The frame never arrived, so the job it was
                                                // waiting for is not run: it would produce an
                                                // image of the old angle and call it the new one.
                                                // eslint-disable-next-line no-console
                                                console.warn(`[studio] headless capture failed: ${err}`);
                                                startNextCapture();
                                                return;
                                            }
                                            startNextCapture();
                                            if (!held) return;
                                            // A tick later so the snapshot the capture just wrote
                                            // is committed to the node before the job reads it —
                                            // which is the entire point of having waited.
                                            window.setTimeout(() => setPendingGenerate([held]), 0);
                                        }}
                                        productUuid={productUuid}
                                        productTexturePath={productTexturePath}
                                        productSurfaceTexture={productSurfaceTexture}
                                        isRug={isRug}
                                        sourceImageUrl={sourceImageUrl}
                                        // Hide the 3D camera widget when
                                        // any in-workspace overlay is up
                                        // (Browse projects sheet, product
                                        // picker, recipe selector, or the
                                        // generation lightbox). The widget
                                        // portal lives on body at zIndex
                                        // 900 so it would otherwise float
                                        // on top of the modal.
                                        widgetHidden={browseOpen || pickerOpen || !!recipeSelector || !!lightboxAsset}
                                        onUploadStyleRef={async (file) => {
                                            const result = await api.uploadStyleRef(file);
                                            return result.url;
                                        }}
                                        onDetectReferenceObjects={async (imageUrl) => {
                                            const result = await api.detectReferenceObjects(imageUrl);
                                            return result?.objects || [];
                                        }}
                                    />
                                );
                            }
                            if (node.kind === 'edit-image') {
                                const en = node as CanvasEditImageNode;
                                let summary: string | null = null;
                                if (en.tool === 'expand' && en.expandInputs) {
                                    summary = formatExpandSummary(en.expandInputs);
                                } else if (en.tool === 'retouch' && en.retouchInputs) {
                                    summary = formatRetouchSummary(en.retouchInputs);
                                } else if (en.tool === 'modify' && en.modifyInputs) {
                                    const p = (en.modifyInputs.prompt || '').trim();
                                    summary = p ? `"${p.slice(0, 60)}${p.length > 60 ? '…' : ''}"` : null;
                                } else if (en.tool === 'design') {
                                    summary = en.designInputs?.renderedUrl
                                        ? `${en.designInputs.width || 0}×${en.designInputs.height || 0}`
                                        : null;
                                } else if (en.tool === 'graphics' && en.graphicsInputs) {
                                    const tc = en.graphicsInputs.textAnnotations.filter((a) => a.text.trim()).length;
                                    const ac = en.graphicsInputs.arrows.length;
                                    summary = tc + ac > 0 ? `${tc} text · ${ac} arrow` : null;
                                }
                                const activeRecipe = recipeBySlug.get(
                                    en.tool === 'expand' ? 'expand-image'
                                    : en.tool === 'retouch' ? 'retouch-image'
                                    : en.tool === 'modify' ? 'modify-image'
                                    : en.tool === 'graphics' ? 'graphics-image'
                                    : en.tool === 'upscale' ? 'upscale-image'
                                    : en.tool === 'design' ? 'design-render'
                                    : 'expand-image',
                                );
                                const editSourceGen = nodes.find((n) => n.id === en.sourceNodeId) as CanvasGenerationNode | undefined;
                                const editSourceUrl = editSourceGen?.asset?.url || editSourceGen?.asset?.thumbnailUrl;
                                return (
                                    <EditImageNode
                                        key={en.id}
                                        nodeId={en.id}
                                        x={en.x}
                                        y={en.y}
                                        zoom={zoom}
                                        selected={selectedNodeIds.has(en.id)}
                                        dimmed={dimmed}
                                        collapsed={en.collapsed}
                                        onToggleCollapse={() => setNodes((prev) => prev.map((n) => (
                                            n.id === en.id && n.kind === 'edit-image'
                                                ? { ...n, collapsed: !(n as CanvasEditImageNode).collapsed }
                                                : n
                                        )))}
                                        tool={en.tool}
                                        sourceImageUrl={en.tool === 'design' && en.designInputs?.renderedUrl ? en.designInputs.renderedUrl : editSourceUrl}
                                        settingsSummary={summary}
                                        generating={en.generating}
                                        creditCost={activeRecipe?.creditCost ?? 10}
                                        creditBalance={balance}
                                        retouchRegions={en.retouchInputs?.regions?.map((r) => ({ id: r.id, prompt: r.prompt || '' }))}
                                        upscaleScale={en.upscaleScale || 2}
                                        onUpscaleScaleChange={(scale) => {
                                            setNodes((prev) => prev.map((n) =>
                                                n.id === en.id && n.kind === 'edit-image'
                                                    ? { ...n, tool: 'upscale', upscaleScale: scale }
                                                    : n,
                                            ));
                                        }}
                                        modifyPrompt={en.modifyInputs?.prompt || ''}
                                        modifyReferenceUrl={en.modifyInputs?.referenceUrl}
                                        onModifyChange={(patch) => {
                                            setNodes((prev) => prev.map((n) => {
                                                if (n.id !== en.id || n.kind !== 'edit-image') return n;
                                                const prevModify = (n as CanvasEditImageNode).modifyInputs || { prompt: '' };
                                                return {
                                                    ...n,
                                                    tool: 'modify',
                                                    modifyInputs: {
                                                        prompt: patch.prompt !== undefined ? patch.prompt : prevModify.prompt,
                                                        referenceUrl: 'referenceUrl' in patch ? patch.referenceUrl : prevModify.referenceUrl,
                                                    },
                                                };
                                            }));
                                        }}
                                        onUploadReference={async (file) => {
                                            const result = await api.uploadStyleRef(file);
                                            return result.url;
                                        }}
                                        onToolChange={(nextTool) => {
                                            pushHistory();
                                            setNodes((prev) => prev.map((n) =>
                                                n.id === en.id && n.kind === 'edit-image'
                                                    ? { ...n, tool: nextTool }
                                                    : n,
                                            ));
                                        }}
                                        onOpenSettings={() => setEditSettingsNodeId(en.id)}
                                        onOpenDesign={() => setDesignEditNodeId(en.id)}
                                        onGenerate={() => handleEditImageGenerate(en.id)}
                                        onDrag={(dx, dy) => moveNode(en.id, dx, dy)}
                                        onClick={(mods) => handleNodeClick(en.id, mods)}
                                        onTargetInChat={() => targetInChat({
                                            nodeId: en.id,
                                            kind: 'edit',
                                            label: en.tool || t('CONTENT_STUDIO.PLANNER.TARGET_EDIT', 'Edit'),
                                        })}
                                        onRemove={() => removeNode(en.id)}
                                        onDuplicate={() => handleDuplicateEditImage(en.id)}
                                    />
                                );
                            }
                            if (node.kind === 'generation') {
                                const gn = node as CanvasGenerationNode;
                                return (
                                    <GenerationNode
                                        key={gn.id}
                                        nodeId={gn.id}
                                        x={gn.x}
                                        y={gn.y}
                                        asset={gn.asset}
                                        approved={gn.approved}
                                        selected={selectedNodeIds.has(gn.id)}
                                        zoom={zoom}
                                        dimmed={dimmed}
                                        onDrag={(dx, dy) => moveNode(gn.id, dx, dy)}
                                        onClick={(mods) => handleNodeClick(gn.id, mods)}
                                        onImageClick={() => setLightboxAsset(gn.asset)}
                                        onApprove={() => {
                                            pushHistory();
                                            setNodes((prev) =>
                                                prev.map((n) =>
                                                    n.id === gn.id && n.kind === 'generation'
                                                        ? { ...n, approved: true }
                                                        : n,
                                                ),
                                            );
                                        }}
                                        onReject={() => {
                                            // Tombstone first so the upcoming
                                            // pushHistory inside removeNode sees
                                            // the asset as reachable (it's still
                                            // in current nodes), deferring the
                                            // api.deleteAsset until the snapshot
                                            // falls off the history stack.
                                            tombstonesRef.current.add(gn.asset.uuid);
                                            removeNode(gn.id);
                                        }}
                                        onMakeVariation={() => handleMakeVariation(gn.asset)}
                                        onMakeVideo={() => handleMakeVideo(gn.asset)}
                                        onPlaceProduct={hasFurniture ? () => handlePlaceProduct(gn.asset) : undefined}
                                        onEditImage={(tool) => handleSpawnEditImage(gn.asset, tool)}
                                        onDesign={() => handleSpawnDesign(gn.asset)}
                                        onAddToTimeline={() => videoEdit.addToTimeline(gn)}
                                        onDownload={() => {
                                            const url = gn.asset.url;
                                            if (!url) return;
                                            const ext = gn.asset.kind === 'video' ? 'mp4' : 'png';
                                            const slug = gn.asset.recipeSlug || 'content-studio';
                                            downloadAsset(url, `${slug}-${gn.asset.uuid.slice(0, 8)}.${ext}`);
                                        }}
                                        onSchedulePost={gn.asset.kind !== 'video' && gn.asset.status === 'ready'
                                            ? () => navigate(`/marketing/social-scheduler/new?asset=${encodeURIComponent(gn.asset.uuid)}&project=${encodeURIComponent(projectUuid || '')}`)
                                            : undefined}
                                        // An imported image was not made by a recipe — nothing to re-run.
                                        onRerun={gn.asset.recipeSlug === 'import' ? undefined : () => handleRerun(gn.asset)}
                                        onTargetInChat={() => targetInChat({
                                            nodeId: gn.id,
                                            kind: 'generation',
                                            label: t('CONTENT_STUDIO.PLANNER.TARGET_IMAGE', 'Image'),
                                            thumbnailUrl: gn.asset?.thumbnailUrl || gn.asset?.url,
                                        })}
                                        onDelete={() => {
                                            tombstonesRef.current.add(gn.asset.uuid);
                                            removeNode(gn.id);
                                        }}
                                        onNoteChange={async (note) => {
                                            // Persist note on the asset, then
                                            // merge the response back into the
                                            // node so the indicator and draft
                                            // reflect the saved value.
                                            const updated = await api.updateAssetNote(gn.asset.uuid, note);
                                            setNodes((prev) =>
                                                prev.map((n) =>
                                                    n.id === gn.id && n.kind === 'generation'
                                                        ? { ...n, asset: { ...n.asset, note: updated?.note } }
                                                        : n,
                                                ),
                                            );
                                        }}
                                    />
                                );
                            }
                            if (node.kind === 'video-edit') {
                                const vn = node as CanvasVideoEditNode;
                                return (
                                    <VideoEditNode
                                        key={vn.id}
                                        nodeId={vn.id}
                                        x={vn.x}
                                        y={vn.y}
                                        zoom={zoom}
                                        selected={selectedNodeIds.has(vn.id)}
                                        dimmed={dimmed}
                                        composition={vn.composition}
                                        renderedUrl={vn.renderedUrl}
                                        renderedThumbnailUrl={vn.renderedThumbnailUrl}
                                        exportState={videoEdit.exportStates[vn.id]}
                                        onOpenEditor={() => videoEdit.openEditor(vn.id)}
                                        onExport={() => { void videoEdit.exportNode(vn.id).catch(() => undefined); }}
                                        onCancelExport={() => videoEdit.cancelExport(vn.id)}
                                        onDrag={(dx, dy) => moveNode(vn.id, dx, dy)}
                                        onClick={(mods) => handleNodeClick(vn.id, mods)}
                                        onRemove={() => removeNode(vn.id)}
                                        onDownload={() => { if (vn.renderedUrl) downloadAsset(vn.renderedUrl, `video-${vn.id.slice(-8)}.mp4`); }}
                                        story={vn.story ? {
                                            progress: videoStory.progressFor(vn)!,
                                            title: vn.story.storyboard?.title,
                                            error: vn.story.error,
                                            onAssembleNow: () => { void videoStory.assemble(vn.id); },
                                        } : null}
                                    />
                                );
                            }
                            if (node.kind === 'design') {
                                const dn = node as CanvasDesignNode;
                                // Resolve every source asset (primary +
                                // extras) into thumbnail URLs so the
                                // DesignNode can render its mosaic on
                                // multi-source designs. URLs come from
                                // the live generation nodes — re-derived
                                // each render so a deleted source falls
                                // out automatically.
                                const sourceThumbnailUrls: string[] = [];
                                const primarySrc = nodes.find(
                                    (p) => p.id === dn.sourceNodeId && p.kind === 'generation',
                                ) as CanvasGenerationNode | undefined;
                                if (primarySrc?.asset?.url) sourceThumbnailUrls.push(primarySrc.asset.url);
                                for (const extraId of dn.additionalSourceNodeIds || []) {
                                    const ex = nodes.find(
                                        (p) => p.id === extraId && p.kind === 'generation',
                                    ) as CanvasGenerationNode | undefined;
                                    if (ex?.asset?.url) sourceThumbnailUrls.push(ex.asset.url);
                                }
                                return (
                                    <DesignNode
                                        key={dn.id}
                                        nodeId={dn.id}
                                        x={dn.x}
                                        y={dn.y}
                                        zoom={zoom}
                                        selected={selectedNodeIds.has(dn.id)}
                                        dimmed={dimmed}
                                        renderedUrl={dn.renderedUrl}
                                        saving={dn.saving}
                                        hasContent={!!dn.fabricJson}
                                        sourceThumbnailUrls={sourceThumbnailUrls}
                                        onOpenEditor={() => setDesignEditorNodeId(dn.id)}
                                        onDrag={(dx, dy) => moveNode(dn.id, dx, dy)}
                                        onClick={(mods) => handleNodeClick(dn.id, mods)}
                                        onRemove={() => removeNode(dn.id)}
                                        onDownload={() => {
                                            if (!dn.renderedUrl) return;
                                            downloadAsset(dn.renderedUrl, `design-${dn.id.slice(-8)}.png`);
                                        }}
                                    />
                                );
                            }
                            if (node.kind === 'wall-picker') {
                                const wp = node as CanvasWallPickerNode;
                                const wpPayload = wp.asset.wallPicker;
                                // Compute the credit cost a Continue press
                                // will reserve. Walks Recipe → Product to
                                // get the recipe definition + product
                                // visualizationType, then runs the same
                                // pricing formula the backend uses.
                                let continueCreditCost: number | undefined;
                                const upstreamRecipeNode = nodes.find(
                                    (n) => n.id === wp.recipeNodeId && n.kind === 'recipe',
                                ) as CanvasRecipeNode | undefined;
                                if (upstreamRecipeNode) {
                                    const recipeDef = recipes.find((r) => r.slug === upstreamRecipeNode.recipeSlug);
                                    const upstreamProductNode = nodes.find(
                                        (n) => n.id === upstreamRecipeNode.productNodeId && n.kind === 'product',
                                    ) as CanvasProductNode | undefined;
                                    if (recipeDef) {
                                        continueCreditCost = resolveCreditCost(recipeDef, {
                                            inputs: { ...upstreamRecipeNode.inputs, phase: 'render' },
                                            visualizationType: upstreamProductNode?.products?.[0]?.visualizationType,
                                        });
                                    }
                                }
                                return (
                                    <WallPickerNode
                                        key={wp.id}
                                        nodeId={wp.id}
                                        x={wp.x}
                                        y={wp.y}
                                        zoom={zoom}
                                        selected={selectedNodeIds.has(wp.id)}
                                        dimmed={dimmed}
                                        baseImageUrl={wp.asset.url}
                                        walls={wpPayload?.walls || []}
                                        imageWidth={wpPayload?.imageWidth || 0}
                                        imageHeight={wpPayload?.imageHeight || 0}
                                        selectedWallIds={wp.selectedWallIds}
                                        pending={wp.asset.status === 'pending' || !wpPayload}
                                        generating={wp.generating}
                                        maskUrl={wpPayload?.maskUrl}
                                        continueCreditCost={continueCreditCost}
                                        onToggleWall={(wallId) => handleToggleWall(wp.id, wallId)}
                                        onContinue={() => handleWallPickerContinue(wp.id)}
                                        onDrag={(dx, dy) => moveNode(wp.id, dx, dy)}
                                        onClick={(mods) => handleNodeClick(wp.id, mods)}
                                        onRemove={() => removeNode(wp.id)}
                                    />
                                );
                            }
                            return null;
                        })}
                    </AnimatePresence>

                    {/* Multi-select checkbox overlays — rendered inside
                        canvas-coord space so they pan / zoom with the
                        nodes they sit on. Only visible in select mode
                        and only over ready image generation nodes
                        (videos + pending + failed are dimmed). Click
                        toggles the asset's membership in the running
                        selection; entire node body is also a click
                        target so users don't have to hit the 24 px
                        checkbox precisely. */}
                    {selectMode && (() => {
                        // Selection order matters for the video flow —
                        // first checked = start frame, last = end frame.
                        // Build a UUID → 1-based position map up front so
                        // the overlays can render their badge.
                        const orderMap = new Map<string, number>();
                        Array.from(selectedAssetUuids).forEach((u, i) => orderMap.set(u, i + 1));
                        return nodes
                            .filter((n) =>
                                n.kind === 'generation'
                                && (n as CanvasGenerationNode).asset.kind !== 'video'
                                && (n as CanvasGenerationNode).asset.status === 'ready',
                            )
                            .map((n) => {
                                const gn = n as CanvasGenerationNode;
                                const position = orderMap.get(gn.asset.uuid);
                                const checked = position !== undefined;
                                const measuredH = nodeHeights.get(gn.id);
                                const overlayH = measuredH
                                    ? measuredH
                                    : ((gn.approved === false || !gn.approved) ? 260 : 220);
                                return (
                                    <div
                                        key={`sel-${gn.id}`}
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            // Picking an image for the agent is a
                                            // single choice, not a multi-select —
                                            // one click answers and closes.
                                            if (pickForAgentRef.current) {
                                                const resolve = pickForAgentRef.current;
                                                pickForAgentRef.current = null;
                                                exitSelectMode();
                                                resolve(gn.id);
                                                return;
                                            }
                                            toggleAssetSelection(gn.asset.uuid);
                                        }}
                                        className={cn(
                                            'absolute z-30 cursor-pointer transition-[outline] duration-150 ease-out',
                                            checked && 'outline-3 -outline-offset-3 outline-foreground',
                                        )}
                                        style={{
                                            left: gn.x,
                                            top: gn.y,
                                            width: GENERATION_NODE_WIDTH,
                                            height: overlayH,
                                        }}
                                    >
                                        <div
                                            className={cn(
                                                'absolute start-2.5 top-2.5 flex size-6.5 items-center justify-center border-2 text-xs font-medium tabular-nums transition-colors',
                                                checked
                                                    ? 'border-foreground bg-foreground text-background'
                                                    : 'border-input bg-background/95',
                                            )}
                                        >
                                            {/* When fewer than 3 are
                                                selected, render the
                                                position number (1 = start,
                                                2 = end) so the keyframe
                                                role is unambiguous before
                                                the user fires Create
                                                video. With 3+ selected
                                                we fall back to a generic
                                                checkmark since Create
                                                video is gated off above
                                                that count. */}
                                            {checked && (selectedAssetUuids.size <= 2 ? (
                                                <span>{position}</span>
                                            ) : (
                                                <Check className="size-4" strokeWidth={3} />
                                            ))}
                                        </div>
                                    </div>
                                );
                            });
                    })()
                    }

                    {/* Recipe selector (RPG card picker — positioned in canvas space) */}
                    <RecipeSelector
                        open={!!recipeSelector}
                        recipes={recipes}
                        anchorX={recipeSelector?.anchorX ?? 0}
                        anchorY={recipeSelector?.anchorY ?? 0}
                        productVisualizationType={
                            (() => {
                                if (!recipeSelector) return undefined;
                                const pn = nodes.find(
                                    (n) => n.id === recipeSelector.productNodeId && n.kind === 'product',
                                ) as CanvasProductNode | undefined;
                                return pn?.products?.[0]?.visualizationType;
                            })()
                        }
                        onSelect={handleSelectRecipe}
                        onClose={() => setRecipeSelector(null)}
                    />
                </CsCanvas>

                {/* Left vertical toolbar */}
                {/* Refresh + Undo/Redo buttons — top right inside canvas.
                    Undo/Redo sit before Refresh so the natural left-to-
                    right reading order matches the action order
                    (rewind → rewind forward → refresh). */}
                {/* Project pill — back, name, and the project switcher. The one
                    piece of chrome that says where you are now that the page
                    chrome is gone. */}
                <div className="absolute start-3 top-3 z-20 flex items-center overflow-hidden rounded-lg border bg-background">
                    <Tooltip>
                        <TooltipTrigger asChild>
                            <button
                                type="button"
                                // A Studio production goes back to its board.
                                onClick={() => navigate((project as any)?.board?.version ? `/marketing/content-studio/${projectUuid}` : '/marketing/content-studio')}
                                className="flex w-9.5 self-stretch items-center justify-center border-e text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
                            >
                                <ArrowLeft className="size-4 rtl:rotate-180" />
                            </button>
                        </TooltipTrigger>
                        <TooltipContent>{(project as any)?.board?.version ? t('STUDIO.BACK_TO_BOARD', 'Back to the board') : t('CONTENT_STUDIO.BACK_TO_PROJECTS', 'Back to projects')}</TooltipContent>
                    </Tooltip>
                    <button
                        type="button"
                        onClick={() => setBrowseOpen(true)}
                        className="flex max-w-65 items-center gap-1.5 px-3.5 py-2 text-sm font-medium text-foreground transition-colors hover:bg-muted"
                    >
                        <span className="min-w-0 truncate">{project.name}</span>
                        <ChevronDown className="size-3 shrink-0 opacity-60" />
                    </button>
                </div>

                {/* Slides left when the agent panel opens so credits and
                    refresh stay visible beside it. */}
                <div
                    className="absolute top-3 z-20 flex items-center gap-2 transition-[inset-inline-end] duration-200"
                    style={{ insetInlineEnd: canvasChromeRight }}
                >
                    <CreditBadge balance={balance} compact />
                    <CanvasToolButton
                        label={t('CONTENT_STUDIO.TOOL_SELECT', 'Select images')}
                        active={selectMode}
                        onClick={() => {
                            if (selectMode) exitSelectMode();
                            else setSelectMode(true);
                        }}
                    >
                        <SquareCheckBig />
                    </CanvasToolButton>
                    <CanvasToolButton
                        label={t('CONTENT_STUDIO.TOOL_UNDO', 'Undo')}
                        onClick={undo}
                        disabled={!canUndo}
                    >
                        <Undo2 />
                    </CanvasToolButton>
                    <CanvasToolButton
                        label={t('CONTENT_STUDIO.TOOL_REDO', 'Redo')}
                        onClick={redo}
                        disabled={!canRedo}
                    >
                        <Redo2 />
                    </CanvasToolButton>
                    <AnimatePresence>
                        {refreshDone && (
                            <motion.div
                                initial={{ opacity: 0, x: 8 }}
                                animate={{ opacity: 1, x: 0 }}
                                exit={{ opacity: 0, x: 8 }}
                                transition={{ duration: 0.2 }}
                            >
                                <span className="whitespace-nowrap rounded-lg border border-success/30 bg-success/10 px-3 py-1 text-xs font-medium text-success">
                                    {t('CONTENT_STUDIO.REFRESHED', 'Canvas refreshed')}
                                </span>
                            </motion.div>
                        )}
                    </AnimatePresence>
                    <CanvasToolButton
                        label={t('CONTENT_STUDIO.REFRESH', 'Refresh canvas')}
                        disabled={refreshing}
                        onClick={() => {
                            if (!projectUuid || refreshing) return;
                            setRefreshing(true);
                            setRefreshDone(false);
                            (async () => {
                                try {
                                    const [p, a] = await Promise.all([
                                        api.getProject(projectUuid),
                                        api.listAssets({ projectUuid, limit: 200 }),
                                    ]);
                                    setProject(p);
                                    const assetMap = new Map(
                                        (a.items || []).map((asset: CsAsset) => [asset.uuid, asset]),
                                    );
                                    if (p.canvasGraph && p.canvasGraph.length > 0) {
                                        const restored = (p.canvasGraph as any[]).map((node: any) => {
                                            if (node.kind === 'generation') {
                                                const fresh = assetMap.get(node.asset?.uuid);
                                                return fresh ? { ...node, asset: fresh } : node;
                                            }
                                            return node;
                                        });
                                        setNodes(restored);
                                    }
                                    loadBalance();
                                    setRefreshDone(true);
                                    setTimeout(() => setRefreshDone(false), 2500);
                                } catch { /* ignore */ }
                                setTimeout(() => setRefreshing(false), 2000);
                            })();
                        }}
                    >
                        <RefreshCw className={cn(refreshing && 'animate-spin')} />
                    </CanvasToolButton>
                </div>

                <AddImageDialog
                    open={addImageOpen}
                    onOpenChange={(o) => { setAddImageOpen(o); if (!o) addImageAtRef.current = null; }}
                    onImport={handleImportImage}
                />
                <CanvasLeftBar
                    onAddProducts={() => setPickerOpen(true)}
                    onAddImage={() => setAddImageOpen(true)}
                    onOpenVideoEditor={() => openVideoEditorFromSelection()}
                    onOpenVideoStory={() => videoStory.openDialog(selectedProductUuids())}
                    onOpenBrandKit={() => navigate('/marketing/content-studio/brand-kit')}
                />

                {/* Studio Agent — always mounted so the conversation and any
                    in-flight run survive collapsing; `open` only switches
                    between the drawer and the bottom composer. */}
                {projectUuid && (
                    <PlannerPanel
                        projectUuid={projectUuid}
                        recipes={recipes}
                        canvasNodes={nodes}
                        loadProducts={loadPlannerProducts}
                        existingNodes={existingRecipeNodes}
                        generations={agentGenerations}
                        edits={agentEdits}
                        mentionableProducts={mentionableProducts}
                        onApply={handlePlanApply}
                        onPickGeneration={pickGenerationForAgent}
                        handoff={agentHandoff}
                        onHandoffConsumed={() => setAgentHandoff(null)}
                        onRetryGeneration={(generationNodeId) => {
                            const node = nodesRef.current.find(
                                (n) => n.id === generationNodeId && n.kind === 'generation',
                            ) as CanvasGenerationNode | undefined;
                            // Same path as the node's own Re-run button, so a
                            // retry from the chat and one from the canvas cannot
                            // behave differently.
                            if (node?.asset) void handleRerun(node.asset);
                        }}
                        onOpenGeneration={(generationNodeId) => {
                            const node = nodesRef.current.find(
                                (n) => n.id === generationNodeId && n.kind === 'generation',
                            ) as CanvasGenerationNode | undefined;
                            if (node?.asset) setLightboxAsset(node.asset);
                        }}
                        creditBalance={balance}
                        open={agentPanelOpen}
                        onOpenChange={setAgentPanelOpen}
                        onWidthChange={setAgentPanelWidth}
                        collapsedHidden={selectMode}
                    />
                )}

                {/* Select-mode floating action bar — bottom-center, pane-
                    anchored so it stays fixed while the canvas pans /
                    zooms underneath. Count pill on the left reassures
                    vendors how many images are feeding the action; the
                    Create buttons are always visible but disabled with
                    zero selection so the available options read before
                    the user commits. Slides up on mount via framer. */}
                <AnimatePresence>
                    {selectMode && (
                        // Outer div handles the static positioning +
                        // horizontal centering. Inner motion.div owns
                        // the slide-up animation. They MUST be split:
                        // framer-motion writes to `transform` for its
                        // y / opacity tweens, which would clobber the
                        // translateX(-50%) we'd otherwise need on the
                        // animated element to keep it centered.
                        <div className="absolute bottom-6 start-1/2 z-30 -translate-x-1/2 rtl:translate-x-1/2">
                            <motion.div
                                initial={{ opacity: 0, y: 12 }}
                                animate={{ opacity: 1, y: 0 }}
                                exit={{ opacity: 0, y: 12 }}
                                transition={{ duration: 0.18, ease: 'easeOut' }}
                            >
                                {/* Backdrop blur lets the bar sit over the canvas
                                    without fully obscuring the nodes beneath it. */}
                                <div className="flex items-center gap-2 rounded-lg border bg-background/95 py-2 pe-2.5 ps-3.5 text-foreground backdrop-blur-sm">
                                    <div className="min-w-30 border-e pe-2 text-xs font-medium tabular-nums text-muted-foreground">
                                        {selectedAssetUuids.size === 0
                                            ? t('CONTENT_STUDIO.SELECT_PROMPT', 'Select images…')
                                            : t(
                                                'CONTENT_STUDIO.SELECT_COUNT',
                                                '{{count}} selected',
                                                { count: selectedAssetUuids.size },
                                            )}
                                    </div>
                                    <Button
                                        variant="outline"
                                        onClick={handleCreateImageFromSelection}
                                        disabled={selectedAssetUuids.size === 0}
                                    >
                                        <ImageIcon />
                                        {t('CONTENT_STUDIO.SELECT_CREATE_IMAGE', 'Create image')}
                                    </Button>
                                    {selectedAssetUuids.size === 2 && (
                                        <Button
                                            variant="outline"
                                            onClick={swapSelectionOrder}
                                            title={t('CONTENT_STUDIO.SELECT_SWAP_ORDER_HINT', 'Swap which image is the start frame and which is the end frame')}
                                        >
                                            <ArrowUpDown />
                                            {t('CONTENT_STUDIO.SELECT_SWAP_ORDER', 'Swap')}
                                        </Button>
                                    )}
                                    <Button
                                        variant="outline"
                                        onClick={handleCreateVideoFromSelection}
                                        disabled={
                                            selectedAssetUuids.size === 0
                                            || selectedAssetUuids.size > 2
                                        }
                                        title={
                                            selectedAssetUuids.size > 2
                                                ? t(
                                                    'CONTENT_STUDIO.SELECT_CREATE_VIDEO_LIMIT',
                                                    'Video supports up to 2 keyframes — keep only the start and end image selected.',
                                                )
                                                : selectedAssetUuids.size === 2
                                                ? t(
                                                    'CONTENT_STUDIO.SELECT_CREATE_VIDEO_HINT',
                                                    'First image is the start frame, second is the end frame. Camera move animates the transition.',
                                                )
                                                : ''
                                        }
                                    >
                                        <Play />
                                        {t('CONTENT_STUDIO.SELECT_CREATE_VIDEO', 'Create video')}
                                    </Button>
                                    <Button
                                        variant="outline"
                                        onClick={handleEditInTimelineFromSelection}
                                        disabled={selectedAssetUuids.size === 0}
                                    >
                                        <Clapperboard />
                                        {t('VIDEO_EDITOR.EDIT_IN_TIMELINE')}
                                    </Button>
                                    <Button
                                        variant="ghost"
                                        size="icon"
                                        onClick={exitSelectMode}
                                        aria-label={t('CONTENT_STUDIO.CANCEL', 'Cancel')}
                                        title={t('CONTENT_STUDIO.CANCEL', 'Cancel')}
                                        className="text-muted-foreground"
                                    >
                                        <X />
                                    </Button>
                                </div>
                            </motion.div>
                        </div>
                    )}
                </AnimatePresence>

                <CanvasContextMenu
                    position={contextMenu}
                    onClose={() => setContextMenu(null)}
                    items={[
                        {
                            // No explicit size: the menu sizes its icons itself,
                            // which is what the hand-rolled SVGs used to rely on.
                            icon: <CirclePlus strokeWidth={1.5} />,
                            label: t('CONTENT_STUDIO.ADD_PRODUCTS', 'Add products'),
                            onClick: () => setPickerOpen(true),
                        },
                        {
                            icon: <ImagePlus strokeWidth={1.5} />,
                            label: t('CONTENT_STUDIO.ADD_IMAGE.TITLE', 'Add an image'),
                            // Lands where the vendor right-clicked, not mid-view.
                            onClick: () => {
                                const rect = canvasElRef.current?.getBoundingClientRect();
                                if (contextMenu && rect) {
                                    addImageAtRef.current = {
                                        x: (contextMenu.x - rect.left - panX) / zoom,
                                        y: (contextMenu.y - rect.top - panY) / zoom,
                                    };
                                }
                                setAddImageOpen(true);
                            },
                        },
                        {
                            icon: <Film strokeWidth={1.5} />,
                            label: selectedProductUuids().length
                                ? t('VIDEO_EDITOR.STORY.MENU_FROM_PRODUCTS', { count: selectedProductUuids().length })
                                : t('VIDEO_EDITOR.STORY.MENU'),
                            onClick: () => videoStory.openDialog(selectedProductUuids()),
                        },
                        {
                            icon: <Clapperboard strokeWidth={1.5} />,
                            label: selectedGenerationNodes().length
                                ? t('VIDEO_EDITOR.EDIT_SELECTED_IN_TIMELINE', { count: selectedGenerationNodes().length })
                                : t('VIDEO_EDITOR.MENU_NEW_TIMELINE'),
                            onClick: () => {
                                const rect = canvasElRef.current?.getBoundingClientRect();
                                openVideoEditorFromSelection(contextMenu && rect
                                    ? { x: (contextMenu.x - rect.left - panX) / zoom, y: (contextMenu.y - rect.top - panY) / zoom }
                                    : undefined);
                            },
                        },
                        {
                            icon: <Book strokeWidth={1.5} />,
                            label: t('CONTENT_STUDIO.TOOL_BRAND_KIT', 'Brand kit'),
                            onClick: () => navigate('/marketing/content-studio/brand-kit'),
                        },
                        {
                            icon: <Maximize2 strokeWidth={1.5} />,
                            label: t('CONTENT_STUDIO.RECENTER', 'Recenter canvas'),
                            onClick: () => {
                                const el = canvasElRef.current;
                                if (!el) return;
                                const { width, height } = el.getBoundingClientRect();
                                handleRecenter(width, height);
                            },
                            dividerBefore: true,
                        },
                    ]}
                />

                {/* Product picker panel (slides in from left) */}
                <ProductPickerPanel
                    open={pickerOpen}
                    replacing={!!replacingProductNodeId}
                    onClose={() => { setPickerOpen(false); setReplacingProductNodeId(null); setAppendingProductNodeId(null); }}
                    onDrop={(products) => {
                        if (appendingProductNodeId) {
                            handleAppendProducts(appendingProductNodeId, products);
                        } else if (replacingProductNodeId) {
                            handleReplaceProduct(replacingProductNodeId, products);
                        } else {
                            handleProductsDrop(products);
                        }
                    }}
                    onDragDropSingle={(product, screenX, screenY) => {
                        const rect = canvasElRef.current?.getBoundingClientRect();
                        if (!rect) return;
                        // Only accept drops inside the canvas area
                        if (screenX < rect.left || screenX > rect.right ||
                            screenY < rect.top || screenY > rect.bottom) return;
                        const canvasX = (screenX - rect.left - panX) / zoom;
                        const canvasY = (screenY - rect.top - panY) / zoom;
                        const id = `prod-${uuidv4().slice(0, 8)}`;
                        pushHistory();
                        setNodes((prev) => [...prev, {
                            kind: 'product' as const,
                            id,
                            x: canvasX,
                            y: canvasY,
                            products: [product],
                        }]);
                    }}
                />
            </div>

            {/* Video timeline editor (opened from a video-edit node) */}
            {videoEdit.modal}

            {/* Video Story: brief → storyboard → shots → clips → timeline */}
            {videoStory.dialog}

            {/* Lightbox */}
            <GenerationLightbox
                asset={lightboxAsset}
                open={!!lightboxAsset}
                onClose={() => setLightboxAsset(null)}
            />

            {/* Edit Image settings modal — opens from the Settings button on
              * an edit-image node. Saves pixel deltas back onto the node; the
              * node's own Generate button fires the recipe. */}
            {(() => {
                const editingNode = editSettingsNodeId
                    ? (nodes.find((n) => n.id === editSettingsNodeId) as CanvasEditImageNode | undefined)
                    : undefined;
                const sourceGen = editingNode
                    ? (nodes.find((n) => n.id === editingNode.sourceNodeId) as CanvasGenerationNode | undefined)
                    : undefined;
                const editingAsset = sourceGen?.asset || null;
                return (
                    <ImageEditorModal
                        open={!!editingNode}
                        asset={editingAsset}
                        initialTool={
                            editingNode?.tool === 'retouch' ? 'retouch'
                            : editingNode?.tool === 'modify' ? 'modify'
                            : editingNode?.tool === 'graphics' ? 'graphics'
                            : 'expand'
                        }
                        initialExpand={editingNode?.expandInputs}
                        initialRetouch={editingNode?.retouchInputs}
                        initialModify={editingNode?.modifyInputs}
                        initialGraphics={editingNode?.graphicsInputs}
                        onClose={() => setEditSettingsNodeId(null)}
                        expandCreditCost={
                            recipes.find((r) => r.slug === 'expand-image')?.creditCost ?? 10
                        }
                        retouchCreditCost={
                            recipes.find((r) => r.slug === 'retouch-image')?.creditCost ?? 12
                        }
                        modifyCreditCost={
                            recipes.find((r) => r.slug === 'modify-image')?.creditCost ?? 10
                        }
                        graphicsCreditCost={
                            recipes.find((r) => r.slug === 'graphics-image')?.creditCost ?? 20
                        }
                        onSaveExpand={(params) => {
                            if (editingNode) handleSaveExpandSettings(editingNode.id, params);
                        }}
                        onSaveRetouch={(params) => {
                            if (editingNode) handleSaveRetouchSettings(editingNode.id, params);
                        }}
                        onSaveModify={(params) => {
                            if (editingNode) handleSaveModifySettings(editingNode.id, params);
                        }}
                        onSaveGraphics={(params) => {
                            if (editingNode) handleSaveGraphicsSettings(editingNode.id, params);
                        }}
                        onUploadReferenceImage={async (file) => {
                            const result = await api.uploadStyleRef(file);
                            return result.url;
                        }}
                    />
                );
            })()}

            {/* Design editor (Fabric.js-based Canva-style layered editor) —
              * opens from a Design node's "Open editor" button. The
              * source asset is the parent generation the design sits on
              * top of. On Save, the rasterized PNG is uploaded and the
              * resulting URL + fabric JSON are stored on the design
              * node (see handleSaveDesign). */}
            {(() => {
                const designNode = designEditorNodeId
                    ? (nodes.find((n) => n.id === designEditorNodeId) as CanvasDesignNode | undefined)
                    : undefined;
                const sourceGen = designNode
                    ? (nodes.find((n) => n.id === designNode.sourceNodeId) as CanvasGenerationNode | undefined)
                    : undefined;
                const isMultiSource = !!designNode?.additionalSourceNodeIds?.length;
                // Multi-source designs (Create-image from N selected
                // images) treat ALL sources as image layers so the
                // Layouts section can grid them together — otherwise
                // the primary would land as the canvas background and
                // the count visible to the layouts engine would be
                // (N - 1) instead of N. Single-source designs keep the
                // existing "primary as background" behavior so vendors
                // can compose text / shapes on top of one image.
                const backgroundUrl = isMultiSource ? null : (sourceGen?.asset?.url || null);
                // Initial layers are honoured only on first open
                // (before any save). Once fabricJson exists, the saved
                // scene is authoritative and loadFromJSON wins.
                let initialImageLayerUrls: string[] | undefined;
                if (designNode && !designNode.fabricJson) {
                    if (isMultiSource) {
                        // Primary first so its stacking order is the
                        // bottom layer / cell 0 in the layout — matches
                        // the order the user selected images on canvas.
                        const urls: string[] = [];
                        if (sourceGen?.asset?.url) urls.push(sourceGen.asset.url);
                        for (const extraId of designNode.additionalSourceNodeIds || []) {
                            const ex = nodes.find(
                                (p) => p.id === extraId && p.kind === 'generation',
                            ) as CanvasGenerationNode | undefined;
                            if (ex?.asset?.url) urls.push(ex.asset.url);
                        }
                        initialImageLayerUrls = urls;
                    } else {
                        initialImageLayerUrls = (designNode as any).initialImageLayerUrls as string[] | undefined;
                    }
                }
                return (
                    <DesignEditorModal
                        open={!!designNode}
                        backgroundUrl={backgroundUrl}
                        initialFabricJson={designNode?.fabricJson}
                        initialImageLayerUrls={initialImageLayerUrls}
                        brandKit={{
                            logoUrl: (project as any)?.brandKit?.logoUrl,
                            colors: (project as any)?.brandKit?.colors,
                        }}
                        onClose={() => setDesignEditorNodeId(null)}
                        onSave={(result) => {
                            if (designEditorNodeId) handleSaveDesign(designEditorNodeId, result);
                        }}
                    />
                );
            })()}

            {/* ── Design editor bound to an EDIT-IMAGE node (Design tool) ── */}
            {(() => {
                if (!designEditNodeId) return null;
                const editNode = nodes.find((n) => n.id === designEditNodeId) as CanvasEditImageNode | undefined;
                if (!editNode) return null;
                // The dev hook (__csOpenDesign) may name a generation directly: design on it.
                const srcGen = ((editNode as any).kind === 'generation'
                    ? editNode
                    : nodes.find((n) => n.id === editNode.sourceNodeId)) as CanvasGenerationNode | undefined;
                const bg = srcGen?.asset?.url || srcGen?.asset?.thumbnailUrl || null;
                return (
                    <DesignEditorModal
                        open
                        backgroundUrl={bg}
                        initialFabricJson={editNode.designInputs?.fabricJson}
                        brandKit={{
                            logoUrl: (project as any)?.brandKit?.logoUrl,
                            colors: (project as any)?.brandKit?.colors,
                        }}
                        onClose={() => setDesignEditNodeId(null)}
                        onSave={(result) => {
                            // Save (upload happens in the background) and close.
                            handleSaveEditDesign(designEditNodeId, result);
                            setDesignEditNodeId(null);
                        }}
                    />
                );
            })()}

            {/* ── Browse projects bottom sheet ──────────────────── */}
            <AnimatePresence>
                {browseOpen && (
                    <>
                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        transition={{ duration: 0.15 }}
                        onClick={closeBrowse}
                        className="absolute inset-0 z-50 bg-foreground/5"
                    />
                    <motion.div
                        initial={{ y: '100%' }}
                        animate={{ y: 0 }}
                        exit={{ y: '100%' }}
                        transition={{
                            // Spring in for a welcoming slide-up, quick
                            // tween out so the dismiss feels snappy.
                            y: browseOpen
                                ? { type: 'spring', stiffness: 350, damping: 36 }
                                : { duration: 0.18, ease: 'easeIn' },
                        }}
                        // Promoted to its own compositor layer so the transform
                        // animates on the GPU, independent of the (now-empty on
                        // close) card SVGs.
                        className="absolute inset-x-6 bottom-0 z-51 will-change-transform"
                    >
                        <div className="max-h-[40vh] border-t bg-background p-5 shadow-[0_-8px_32px_rgba(0,0,0,0.1)]">
                            {/* Header */}
                            <div className="mb-4 flex items-center justify-between">
                                <p className="font-heading text-base font-medium tracking-tight text-foreground">
                                    {t('CONTENT_STUDIO.BROWSE_PROJECTS', 'Browse projects')}
                                </p>
                                <Button
                                    variant="ghost"
                                    size="icon-sm"
                                    onClick={closeBrowse}
                                    aria-label={t('CONTENT_STUDIO.CANCEL', 'Cancel')}
                                    className="text-muted-foreground"
                                >
                                    <X />
                                </Button>
                            </div>

                            {/* Horizontal project cards */}
                            <div className="flex gap-3 overflow-x-auto pb-2">
                                {siblingProjects.map((p) => {
                                    const isActive = p.uuid === projectUuid;
                                    return (
                                        <div
                                            key={p.uuid}
                                            onClick={() => {
                                                if (!isActive) {
                                                    setBrowseOpen(false);
                                                    navigate(`/marketing/content-studio/${p.uuid}`);
                                                }
                                            }}
                                            className={cn(
                                                'w-45 shrink-0 overflow-hidden rounded-lg border bg-background transition-colors',
                                                isActive
                                                    ? 'cursor-default border-foreground'
                                                    : 'cursor-pointer hover:border-ring',
                                            )}
                                        >
                                            {/* Mini preview */}
                                            <div className="relative h-20 overflow-hidden border-b bg-muted bg-[radial-gradient(circle,var(--border)_0.5px,transparent_0.5px)] bg-[length:6px_6px]">
                                                {browseReady && p.canvasGraph && p.canvasGraph.length > 0 && (
                                                    <CanvasMiniMap nodes={p.canvasGraph} />
                                                )}
                                                {isActive && (
                                                    <span className="absolute start-1.5 top-1.5 z-1 bg-foreground px-1.5 py-px text-xs font-medium text-background">
                                                        {t('CONTENT_STUDIO.CURRENT_PROJECT', 'Current')}
                                                    </span>
                                                )}
                                            </div>
                                            <div className="p-2.5">
                                                <p className="truncate text-sm font-medium text-foreground">
                                                    {p.name}
                                                </p>
                                                <p className="mt-0.5 text-xs text-muted-foreground">
                                                    {p.canvasGraph?.length || 0} {t('CONTENT_STUDIO.NODES_COUNT', 'nodes')}
                                                </p>
                                            </div>
                                        </div>
                                    );
                                })}

                                {/* + New project card */}
                                <div
                                    onClick={() => {
                                        setBrowseOpen(false);
                                        setNewProjectOpen(true);
                                    }}
                                    className="flex w-45 shrink-0 cursor-pointer flex-col items-center justify-center gap-2 rounded-xl border border-dashed py-8 text-muted-foreground transition-colors hover:border-ring hover:bg-muted hover:text-foreground"
                                >
                                    <Plus className="size-6" />
                                    <span className="text-sm font-medium">
                                        {t('CONTENT_STUDIO.NEW_PROJECT', 'New project')}
                                    </span>
                                </div>
                            </div>
                        </div>
                    </motion.div>
                    </>
                )}
            </AnimatePresence>

            <NewProjectDialog
                open={newProjectOpen}
                onClose={() => setNewProjectOpen(false)}
                onCreate={async (name, description) => {
                    const created = await api.createProject({ name, description });
                    navigate(`/marketing/content-studio/${created.uuid}/canvas`);
                }}
            />
        </div>
    );
};

export default ContentStudioWorkspacePage;
